/**
 * InspectorPanel — bottom-of-shell editor for the currently-selected
 * layout item. Replaces the per-item popover (`EditPopover`).
 *
 * Visibility is driven by `inspectorItem()` - the selected item ONLY when
 * exactly one is highlighted AND the selection was made by a gesture that
 * means "edit this". Clicking an item in edit mode selects it and the panel
 * slides up; clicking another swaps the content. Clicking empty canvas, Esc,
 * or Done deselects.
 *
 * Two things deliberately do NOT open it (2026-08-09): a right-click, which
 * highlights quietly so its menu can act without summoning the app's heaviest
 * surface, and a multi-selection, whose every menu action hits all the
 * highlighted items while these fields edit exactly one. The menu's Inspect
 * row is what re-opens it, by collapsing the selection back to one.
 *
 * Layout is a single horizontal row, scrolls horizontally if the kind's
 * fields overflow on narrow screens. The matrix picker and confirm
 * dialog still portal to body — they're large/blocking by design.
 *
 * Content surface lifted from the old EditPopover, restructured into
 * left-to-right zones:
 *   identity (kind icon + Name + Size) | kind-specific (CC/Ch/Val/etc.) |
 *   face (Show name / Show info) | color | delete + done
 */

import { followSongTempos, tempoText } from '../timeline/tempoLabel.ts';
import { createSignal, createEffect, createMemo, onCleanup, Show, For, Index, Switch, Match, type JSX } from 'solid-js';
import { installInspectorSteppers } from './inspectorSteppers.ts';
import { obsState, obsBridgeSummary, autoPickObsBridge } from '../obs/scenes.ts';
import { refreshObsScenes, discoverObsBridges } from '../obs/scenePoll.ts';
import { InfoHint } from './InfoHint.tsx';
import { MotionCaptureFields, MotionLandFields } from './MotionPresetFields.tsx';
import {
  programState, programBridgeSummary, autoPickProgramBridge, programDisplayName,
} from '../program/programs.ts';
import { refreshPrograms, discoverProgramBridges } from '../program/programPoll.ts';
import {
  presetState, presetById, presetIsPhotosensitive, autoPickPresetBridge, presetBridgeSummary,
} from '../lighting/presets.ts';
import { HueSceneEditor } from './HueScenePicker.tsx';
import { hueState } from '../lighting/hueScenes.ts';
import { refreshPresets } from '../lighting/presetRead.ts';
import { abletonDeviceStateFor } from '../ableton/deviceState.ts';
import { refreshDeviceStatus } from '../ableton/devicePoll.ts';
import { abletonDeviceFault, abletonDeviceFaultLabel, deviceKey } from '../ableton/deviceControl.ts';
import { describeTarget, resolveTarget } from '../lighting/targets.ts';
import { LightTargetPicker } from './LightTargetPicker.tsx';
import { openLightsManager } from './LightsManager.tsx';
import { STAGE_COLORS, rgbToHex } from '../lighting/palette.ts';
import { isMusicalEffect, effectTakesColor, titleCaseSlug } from '../lighting/effects.ts';
import { softwareEffectsFree, softwareEffectsMusical } from '../fixtures/profileSync.ts';
import { fixtureMode, fixtures } from '../fixtures/store.ts';
import { Portal } from 'solid-js/web';
import type { LayoutItem, LayoutColor, SizePreset, ButtonShape, ButtonItem, SnapshotTarget, ItemPatch, OpVisualTriggerItem, OpVisualGridItem, AudioPadsItem } from '../layout/store.ts';
import { OpVisualTriggerFields, OpVisualGridFields, OpVisualSendsVia } from './OpVisualInspector.tsx';
import { AudioPadsFields } from './AudioPadsInspector.tsx';
import { actionFace } from '../opvisual/wire.ts';
import {
  clearLightLevelField,
  editItem, removeItem, resizeButtonGrid, setGridLook, resizeItem, setItemSize, setItemName,
  setFaceFlag, selectItem, setButtonShape, setTransposeRange, setCapoRange, setRecQuantLabels, setRecQuantQuantizer, setSceneCopyRange,
  setSceneCopyDeleteAll,
  setWavesAutotuneRange, setWavesAutotuneHome, setWavesAutotunePlacement,
  isRatioLocked, toggleRatioLock, isEditMode, setMixerMaster, setMixerReturns, setMixerReturnCount,
  layoutItems, pickButtonForDisplayId, startPickButtonForDisplay, cancelPickButton,
  setRigStateButtonBank, setRigStateButtonLook, itemResizeClamps,
  setAbletonRowCells, abletonButtonToRow, abletonRowToButton,
} from '../layout/store.ts';
import type { AbletonRowItem } from '../layout/store.ts';
import { activeProject, motionPresetById } from '../layout/projectStore.ts';
import {
  ABLETON_CONTROLS, ROW_STEP_TYPE, COMMON_TIME_SIGS, TRANSPORT_ACTIONS, PRACTICE_TRANSPORT_ACTIONS,
  TEMPO_MIN, TEMPO_MAX, controlForStep, cellKey, roundBpm, rowClock, freshAbletonStep, buttonFace,
  type AbletonControlChoice,
} from '../ableton/rows.ts';
import { stepLit } from '../ableton/stepState.ts';
import { midiAllocFailure } from '../layout/midiAlloc.ts';
import { bridges, bridgeConnectedReactive, anyConnectedReactive } from '../opbridge/store.ts';
import { openNavigateAt } from '../navigate/store.ts';
import { machineNameFromInstanceName, type LightingPresetKnob } from '../opbridge/httpClient.ts';
import { dropTuning, setDropTuning } from '../transpose/session.ts';
import { NOTE_NAMES } from '../autotune/session.ts';
import { requestWavesChannel } from '../autotune/conflict.ts';
import type { WavesAutotuneScalePlacement, MixerSkin, PaletteHue, DisplaySourceType, ButtonActionType, LightPresetConfig } from '../layout/projectStore.ts';
import type { AbletonStep, AbletonClockTarget, DmxTarget, MotionShape } from '@opbuttons/schema';
import { barsWords, CROSSFADE_ACTIONS, CROSSFADE_BARS, CROSSFADE_DEFAULT_BARS, CURVE_CHOICES, crossfadeName, crossfadeWhere, type CrossfadeAction } from '../timeline/opMotion.ts';
import {
  REC_QUANT_LABELS, REC_QUANT_DISPLAY,
  LAUNCH_QUANT_LABELS, LAUNCH_QUANT_DISPLAY,
} from '@opbuttons/schema';
import { ulid } from '../utils/ulid.ts';
import { GridPreview, ItemPreview } from './InspectorPreview.tsx';
import { DisplayStyleRow, DisplayTextRows, DisplayShowRows } from './DisplayLookRows.tsx';
import { GridStartsRow, GridFinishRow, GridTitleIconRows, GridTextureRows, GridFlashRow, TrayTitleRow, PadIconRows } from './GridLookRows.tsx';
/** Single pads that share the `.midi-button` face and so the grid's look (DEC-321, components/PadDecor.tsx). */
const PAD_KINDS: ReadonlySet<LayoutItem['kind']> = new Set<LayoutItem['kind']>(['button', 'presetChange', 'snapshotChange', 'rigStateButton', 'lightColor', 'lightFx', 'blackout', 'opvisualTrigger']);
import { TRAY_KINDS } from './TrayTitle.tsx';
import type { GridTitleAt } from '../layout/buttonLook.ts';
import { hueOf, HUE_VALUES, HUE_ORDER, activePedals, pedalModel } from '../layout/projectStore.ts';
import { kindLabel } from '../layout/kindLabels.ts';
import { openItemMenu } from '../layout/itemMenu.ts';
import { openConnectedDevices } from '../settings/store.ts';
import { macros, openMacrosEditor } from '../macros/store.ts';
import { MidiTriggerLearn } from './MidiTriggerLearn.tsx';
import { AssignControllerField } from './AssignController.tsx';
import {
  streamDeckActions, streamDeckStatus, streamDeckError,
  ensureStreamDeckActions, refreshStreamDeckActions,
  findStreamDeckAction, streamDeckActionLabel, streamDeckActionSubtitle,
} from '../opbridge/streamDeckActions.ts';
import { timelines, timelinesInActiveProject } from '../timeline/store.ts';
import { launcherTargets } from '../timeline/bridgeSync.ts';
import { rigStateBanks, openRigStateManager } from '../rigState/store.ts';
import { timelineLoops } from '@opbuttons/schema';
import type { LfoWaveform } from '@opbuttons/schema';
import { MatrixPicker } from './MatrixPicker.tsx';
import { claimSquareForItem } from '../matrix/store.ts';
import { RotationDial } from './RotationDial.tsx';
import { inspectorDocked } from '../layout/inspectorDock.ts';
import { NumberField } from './NumberField.tsx';
import { guardEnabled } from '../performance/guard.ts';
import { isEditingCellsFor, toggleEditingCellsFor, GridCellRows } from './ButtonGrid.tsx';
import { helixSlotLabel } from './PresetChange.tsx';
import { snapshotLabel } from './SnapshotChange.tsx';
import { openHelixPicker } from './HelixPresetPicker.tsx';
import { SendBrowser } from './SendBrowser.tsx';
import { helixPresetName, helixSetlistName } from '../helix/library.ts';
import { SHAPE_LIBRARY, applyAspectRule } from '../layout/shapes.ts';
import { resolveBridge } from '../opbridge/store.ts';
import { shareStatus, unshareableReason, retryShare } from '../modShare/sync.ts';
import type { Need } from '../modShare/ops.ts';
import { Tip, TipRow } from './Tip.tsx';

/** Kinds that only show things. They get no "Share with mods" switch at
 *  all - a disabled one would be a question with no answer. */
const SENDS_NOTHING = new Set<LayoutItem['kind']>(['statusLed', 'label', 'display', 'image']);

/* Friendly, title-cased panel headings — the title is the first thing the
   eye lands on, so "Waves Autotune" reads better than "WAVESAUTOTUNE". The
   map moved to `layout/kindLabels.ts` (2026-08-09) so the item context menu
   heads its rows with the same names this panel titles itself with. */

/**
 * Read-only device summary + a shortcut to Connected Devices, shared by
 * every item kind that binds a `GuitarPedal` (presetChange, snapshotChange,
 * a Guitar FX Preset Button). 2026-08-04 operator ask: "keep the option
 * non-changeable in the item inspector" — channel (and port) are authored
 * ONLY in Connected Devices from here on, never per-item, so there is
 * nothing to edit here, only to see and jump to.
 */
function PedalField(props: { pedalId: string | undefined }) {
  const pedal = () => {
    const id = props.pedalId;
    return id ? activePedals().find((pd) => pd.id === id) : undefined;
  };
  const summary = () => {
    const pd = pedal();
    if (pd) return `${pd.name} (${pedalModel(pd.modelId)?.model ?? pd.type}) · ch ${pd.channel + 1}${pd.port ? ` · ${pd.port}` : ' · no port'}`;
    if (props.pedalId) return 'Device removed. Pick another below.';
    return 'No device assigned';
  };
  return (
    <>
      <span class="inspector-hint">{summary()}</span>
      <button
        type="button"
        class="inspector-pill"
        onClick={() => openConnectedDevices()}
        title="Channel and port are set in Pedals and devices, not here"
      >
        Manage in Pedals and devices…
      </button>
    </>
  );
}

/** Kinds whose commands actually dispatch to a bridge (per-item multi-select
 *  bridge affinity, 2026-08-13 — supersedes DEC-063's primary-only
 *  dispatch). Excludes: pedal-bound kinds (`presetChange`/`snapshotChange`
 *  inherit affinity from their pedal in Connected Devices, never their own
 *  field, per operator confirmation); inbound-only kinds (`statusLed`/
 *  `display` never send, so a "where does this send" picker would be
 *  meaningless — `statusLed` gets its own inbound-filtering picker
 *  instead); and `label`/`image` (no dispatch of any kind).
 *  `timelineLauncher` joined 2026-09-28: it was excluded while it fired to
 *  nothing, but since the OPBridge timeline engine shipped (2026-08-16) its
 *  push, fire and stop all fan out to `bridgeIds` (TimelineLauncher.tsx), so
 *  hiding the picker left a live control with no way to see or change where
 *  it sends. */
const FIRES_TO_BRIDGES = new Set<LayoutItem['kind']>([
  'button', 'lfo', 'fader', 'buttongrid', 'sequencer', 'macro', 'streamdeck',
  'xypad', 'piano', 'transpose', 'capo', 'recQuant', 'abletonRow', 'sceneCopy', 'wavesAutotune', 'mixer', 'radial',
  'opvisualTrigger', 'opvisualGrid', 'timelineLauncher',
]);

/**
 * Multi-select "which mesh bridge(s) does this item's command fire to"
 * picker (2026-08-13). Rendered whenever at least one bridge is paired
 * (widened from `> 1` on 2026-08-30, operator ask — see the Show below).
 *
 * Absent `bridgeIds` (the common case) tracks the default bridge
 * DYNAMICALLY — shown as the "Default" pill selected, not yet an explicit
 * choice. Tapping any pill converts to an explicit array from then on:
 * toggling the effective set in/out, same interaction as a checkbox row.
 * Unchecking everything is a real, legitimate "sends nowhere" state, not
 * an error — the item components' own dead-state text says so. "Default"
 * resets back to dynamic tracking (distinct from explicitly re-selecting
 * whichever bridge is currently default, which would pin to that bridge's
 * literal id and stop following future default changes).
 */
function BridgeAffinityField(props: { item: LayoutItem & { bridgeIds?: string[] } }) {
  const defaultId = () => bridges().find((b) => b.is_primary)?.id;
  /** A Timeline Launcher's Default is NOT the primary: it plays where the timeline's devices and lights
   *  are (DEC-224, `launcherTargets`), so that is the machine Default lights here. Showing the primary
   *  put "Mac" on a lighting launcher that plays on the PC (2026-10-09). */
  const launcherHome = (): string | null => {
    if (props.item.kind !== 'timelineLauncher') return null;
    const t = timelines().find((x) => x.id === (props.item as Extract<LayoutItem, { kind: 'timelineLauncher' }>).timelineId);
    return t ? (launcherTargets(t, undefined)[0] ?? null) : null;
  };
  const defaultTargets = (): string[] => {
    const home = launcherHome();
    if (home !== null) return home ? [home] : [];
    return defaultId() ? [defaultId()!] : [];
  };
  const effective = (): string[] => props.item.bridgeIds ?? defaultTargets();

  /** Is THIS row one of the item's targets? Compared by RESOLUTION, not by
   *  string equality, because a stored value may be either a legacy local
   *  row id or a stable node id and both must light the same pill. */
  function selected(rowId: string): boolean {
    return effective().some((t) => resolveBridge(t)?.id === rowId);
  }

  /** What to STORE for a row: its node id when it has one, else its local
   *  row id. An item rides the synced project document, so a row id is
   *  meaningless on every other machine - see `resolveBridge`. A row with no
   *  node yet (pre-`/health` backfill) keeps the legacy behaviour rather than
   *  being given an invented identity. */
  function storedIdFor(rowId: string): string {
    return bridges().find((b) => b.id === rowId)?.nodeId ?? rowId;
  }

  function toggle(rowId: string): void {
    // Rebuild from the RESOLVED set so a toggle also migrates whatever else
    // is in the list - the operator touching this control is the cheapest
    // possible moment to upgrade the other entries, and leaving them mixed
    // would mean the same item resolves differently on two machines.
    const keep = effective().filter((t) => resolveBridge(t)?.id !== rowId);
    const next = keep.map((t) => {
      const row = resolveBridge(t);
      return row ? storedIdFor(row.id) : t;   // unresolvable: leave it alone
    });
    if (!selected(rowId)) next.push(storedIdFor(rowId));
    editItem(props.item.id, { bridgeIds: next } as ItemPatch);
  }
  return (
    // 2026-08-30 (operator): show this on EVERY firing item, even with a
    // single bridge - "even if there's only 1 opbridge, it should still show
    // it in the inspector". Previously gated on `> 1` as "a picker with one
    // option is noise"; in practice the opposite bit harder - with the
    // picker hidden there was no way to see WHERE an item fires, and no
    // discoverable route to the "sends nowhere" state (DEC-138) on a
    // single-bridge rig, which is most rigs.
    <Show when={bridges().length > 0}>
      <div class="inspector-zone" data-sends-to="multi">
        <div class="inspector-pills" data-stack="true">
          <span class="inspector-pills-label">Sends to</span>
          <button
            type="button"
            class="inspector-pill"
            data-selected={props.item.bridgeIds === undefined ? 'true' : 'false'}
            onClick={() => editItem(props.item.id, { bridgeIds: undefined } as ItemPatch)}
            title={props.item.kind === 'timelineLauncher' ? 'Plays where the devices and lights of its timeline are' : 'Follow whichever bridge is set as default'}
          >
            Default
          </button>
          <For each={bridges()}>
            {(b) => (
              <button
                type="button"
                class="inspector-pill"
                data-selected={selected(b.id) ? 'true' : 'false'}
                onClick={() => toggle(b.id)}
                title={b.bridge_instance_name || b.name}
              >
                {/* Machine name, same as SingleMachineField, so one computer
                    reads the same in every "Sends to" (2026-09-23). */}
                {machineNameFromInstanceName(b.bridge_instance_name) || b.bridge_instance_name || b.name}
              </button>
            )}
          </For>
        </div>
      </div>
    </Show>
  );
}

/**
 * The local row id of the ONE machine a single-machine control is aimed at,
 * or '' when it names none.
 *
 * Not `resolveBridge(bridgeIds[0])` alone: that falls back to the PRIMARY
 * bridge for an empty value, so an unaimed OBS or Ableton Device button
 * lit the primary as chosen while its own fault model said "pick a
 * computer" and dispatch refused it - and every hint gated on "no machine
 * picked" could never show. Caught 2026-09-23 by
 * `inspector_sends_to_is_last_in_what_it_does`, red before this fix.
 */
function aimedRowId(bridgeIds: string[] | undefined): string {
  const first = (bridgeIds ?? [])[0];
  if (!first) return '';
  return resolveBridge(first)?.id ?? first;
}

/**
 * Single-machine "Sends to" for the controls that can only ever mean ONE
 * computer: an OBS Scene button (one program scene per machine) and an
 * Ableton Device button (the address is loopback, so two machines would be
 * two different Live sets). Same place, label and vertical stack as
 * {@link BridgeAffinityField} (operator, 2026-09-23: every "sends to" lives at
 * the bottom of What it does, and a horizontal row does not work on iPad),
 * but single-select and with NO "Default" row: both controls are refused
 * until a machine is named, because a wrong machine fails silently.
 *
 * `annotate` adds the per-machine fact that helps the choice (OBS: scene
 * count or "no OBS here"; Ableton Device: offline). Stores the node id, not
 * the row id - the item rides the synced project document.
 *
 * A **None** row sits under the machines (operator, 2026-09-29: "it should be
 * none is option too" - once one machine was lit there was no way back). It
 * stores `bridgeIds: []`, DEC-138's explicit "sends nowhere", which every
 * user of this field already refuses to send; it is NOT the default bridge.
 * `[]` is also what stops the auto-aims from re-choosing a machine: they
 * fire only while `bridgeIds` has never been set (`undefined`).
 *
 * `after` renders INSIDE the same zone, below the machines: a kind whose
 * destination has a second part (OPVisual's "Sends via", DEC-215) keeps it in
 * Sends to, so Sends to stays the last section of What it does (DEC-213) by
 * construction rather than by a second zone having to remember its place.
 */
function SingleMachineField(props: {
  item: LayoutItem & { bridgeIds?: string[] | undefined };
  annotate: (rowId: string, connected: boolean) => string;
  after?: JSX.Element;
}) {
  const selectedRowId = () => aimedRowId(props.item.bridgeIds);
  function pick(rowId: string): void {
    const stored = bridges().find((b) => b.id === rowId)?.nodeId ?? rowId;
    editItem(props.item.id, { bridgeIds: [stored] });
  }
  return (
    <div class="inspector-zone" data-sends-to="single">
      {/* Nothing paired: no row at all (the notice under the head says so
          once, with a way to fix it, 2026-10-15); `after` still shows. */}
      <Show when={bridges().length > 0}>
      <div class="inspector-pills" data-stack="true">
        <span class="inspector-pills-label">Sends to</span>
        <Show when={bridges().length > 0}>
          <For each={bridges()}>
            {(b) => {
              const label = () => machineNameFromInstanceName(b.bridge_instance_name)
                || b.bridge_instance_name || b.name || b.id;
              const note = () => props.annotate(b.id, bridgeConnectedReactive(b.id));
              return (
                <button
                  type="button"
                  class="inspector-pill"
                  data-selected={selectedRowId() === b.id ? 'true' : 'false'}
                  onClick={() => pick(b.id)}
                  title={b.bridge_instance_name || b.name}
                >
                  {label()}{note() ? ` - ${note()}` : ''}
                </button>
              );
            }}
          </For>
          <button
            type="button"
            class="inspector-pill"
            data-sends-to-none="true"
            data-selected={selectedRowId() === '' ? 'true' : 'false'}
            onClick={() => editItem(props.item.id, { bridgeIds: [] })}
            title="Send to no computer - the control does nothing until one is chosen"
          >
            None
          </button>
        </Show>
      </div>
      </Show>
      {props.after}
    </div>
  );
}

/** The item's color name (cyan/magenta/amber/green/…) drives the panel's
 *  accent hue via `data-accent`. Falls back to cyan for colorless kinds. */
function itemAccent(item: LayoutItem): string {
  return (item as { color?: string }).color ?? 'cyan';
}

const SIZES: SizePreset[] = ['S', 'M', 'L'];

/* Which "How it looks" controls a kind's face actually reads (operator,
   2026-10-15: "hide them" - a control that changes nothing is not offered).
   Read from the renderers (hideName / hideLabel / nameSize / --hue) and
   measured by `_probe_inspector_dead_controls`, which flags any row shown
   here on a kind whose pixels it does not move. A new kind starts in none
   of these: add it once its face reads the flag. */
const READS_NAME: ReadonlySet<LayoutItem['kind']> = new Set<LayoutItem['kind']>([
  'button', 'fader', 'radial', 'xypad', 'lfo', 'macro', 'streamdeck', 'statusLed', 'presetChange',
  'snapshotChange', 'rigStateButton', 'lightColor', 'lightLevel', 'lightFx', 'blackout',
  'opvisualTrigger', 'opvisualGrid',
]);
const READS_INFO: ReadonlySet<LayoutItem['kind']> = new Set<LayoutItem['kind']>([
  'button', 'fader', 'radial', 'xypad', 'lfo', 'macro', 'streamdeck', 'presetChange',
  'snapshotChange', 'rigStateButton', 'lightColor', 'lightLevel', 'lightFx', 'blackout',
  'opvisualTrigger', 'opvisualGrid', 'buttongrid', 'audioPads',
]);
const READS_FONT: ReadonlySet<LayoutItem['kind']> = new Set<LayoutItem['kind']>(['button', 'buttongrid', 'display']);
/** Kinds that draw no colour of their own (the Display has its own styles,
 *  the Timeline Launcher and OPVisual Grid paint their content, Audio Pads
 *  colour each pad from its own Pad colour). */
const IGNORES_COLOUR: ReadonlySet<LayoutItem['kind']> = new Set<LayoutItem['kind']>([
  'timelineLauncher', 'opvisualGrid', 'audioPads',
]);
const BUTTON_ACTION_TYPES: { id: 'cc' | 'noteon' | 'pc'; label: string; title: string }[] = [
  { id: 'cc', label: 'CC', title: 'Send a Control Change' },
  { id: 'noteon', label: 'Note', title: 'Send a Note On / Note Off pair' },
  { id: 'pc', label: 'PC', title: 'Send a Program Change - one-shot, no OFF, so the button always behaves as a trigger' },
];
const BUTTON_MODES: { id: 'toggle' | 'momentary' | 'trigger'; label: string; title: string }[] = [
  { id: 'toggle',    label: 'Toggle',    title: 'Each tap flips state - sends ON then OFF on alternating taps' },
  { id: 'momentary', label: 'Momentary', title: 'Press = ON, release = OFF (releasing anywhere ends the press)' },
  { id: 'trigger',   label: 'Trigger',   title: 'Each tap fires ON then OFF in the same tick - one-shot' },
];
const NAME_SIZE_PRESETS: { id: 'auto' | 'S' | 'M' | 'L' | 'XL'; label: string; title: string }[] = [
  { id: 'auto', label: 'Auto', title: 'Per-line auto-shrink: each line shrinks independently to fit the face width' },
  { id: 'S',    label: 'S',    title: 'Small - locked font size, no auto-shrink' },
  { id: 'M',    label: 'M',    title: 'Medium - locked font size, no auto-shrink' },
  { id: 'L',    label: 'L',    title: 'Large - locked font size, no auto-shrink' },
  { id: 'XL',   label: 'XL',   title: 'Extra large - locked font size, no auto-shrink' },
];
/* Said as what happens (2026-10-15): "One / One (req) / Many / Up to N" did
   not say it, and four pills did not fit the iPad's row. A dropdown, like
   Shape. */
const BUTTONGRID_MODES: { id: 'radio' | 'radio-required' | 'multi' | 'up-to-n'; label: string; title: string }[] = [
  { id: 'radio',          label: 'One at a time',  title: 'One cell on at a time, or none. Tapping the lit cell turns it off.' },
  { id: 'radio-required', label: 'Always one on',  title: 'Always exactly one cell on. Tapping the lit cell keeps it on.' },
  { id: 'multi',          label: 'Any number',     title: 'Every cell turns on and off on its own. Any combination can be on.' },
  { id: 'up-to-n',        label: 'Up to a limit',  title: 'At most the number below on at once. A new tap turns off the oldest.' },
];

/* Grouped 2026-08-27 when the Ableton Control sources took the list from 8
   to 13. The Clock / Ableton session split is not cosmetic tidying: the
   Clock three are properties BOTH clocks have, so they follow the active
   clock and stay meaningful with no laptop, while the Ableton session three
   have no local equivalent and honestly read `--` when Ableton is absent.
   That is a real behavioural difference and the grouping is what tells the
   operator about it without a sentence of explanation.

   ⚠️ Metronome belongs under Ableton session, NOT Clock, however much it
   sounds like a clock thing. It is Live's AUDIBLE click (`live_set.metronome`,
   confirmed by OPBridge reading the device bytes) - not our visual beat
   dots, which share the word in conversation only. Filing it next to the
   practice-clock controls would quietly imply it toggles those. */
type DisplaySourceGroup = 'Clock' | 'Ableton session' | 'Bind to an item' | 'Session state';

const DISPLAY_SOURCES: { id: DisplaySourceType; label: string; title: string; group: DisplaySourceGroup }[] = [
  { id: 'bpm',          label: 'BPM',           group: 'Clock', title: 'Current tempo - the live one when Ableton owns the clock, the practice one otherwise' },
  { id: 'timesig',      label: 'Time sig',      group: 'Clock', title: 'Time signature of whichever clock is active' },
  { id: 'transport',    label: 'Transport',     group: 'Clock', title: 'PLAY / STOP of whichever clock is active' },

  { id: 'recquant',     label: 'Rec quant',     group: 'Ableton session', title: "Live's record quantization, as the device reports it. Shows -- when Ableton is absent" },
  { id: 'launchquant',  label: 'Launch quant',  group: 'Ableton session', title: "Live's global launch quantization. A different vocabulary from rec quant" },
  { id: 'metronome',    label: 'Metronome',     group: 'Ableton session', title: "Live's audible metronome click - not the beat dots" },

  { id: 'lfo',       label: 'LFO',       group: 'Bind to an item', title: 'Bind to an LFO item on this page' },
  { id: 'timeline',  label: 'Timeline',  group: 'Bind to an item', title: 'Bind to a Timeline Launcher item on this page' },
  { id: 'button',    label: 'Button',    group: 'Bind to an item', title: 'Bind to a Button item on this page - shows ON/OFF' },

  { id: 'nowplaying', label: 'Now playing', group: 'Session state', title: 'The song timeline playing on any computer: its sections, where you are in them, and a countdown before each change' },
  { id: 'helix',     label: 'Helix',     group: 'Session state', title: 'Last preset/snapshot OPButtons sent to a registered pedal' },
  { id: 'transpose', label: 'Transpose', group: 'Session state', title: 'The session-wide concert transpose - no binding needed' },
  { id: 'capo',      label: 'Capo',      group: 'Session state', title: 'The concert transpose, viewed as a capo fret - no binding needed' },
  { id: 'key',       label: 'Key',       group: 'Session state', title: 'The session-wide Waves Autotune key + scale - no binding needed' },
];

/** Source list in display order, grouped. Derived from DISPLAY_SOURCES
 *  rather than hand-maintained beside it, so adding a source cannot leave
 *  it rendered in no group (or in two). */
const DISPLAY_SOURCE_GROUPS: { group: DisplaySourceGroup; items: typeof DISPLAY_SOURCES }[] =
  (['Clock', 'Ableton session', 'Bind to an item', 'Session state'] as const)
    .map((group) => ({ group, items: DISPLAY_SOURCES.filter((s) => s.group === group) }));

/* Ableton Control step kinds, in the two groups the operator thinks in.
   Rendered as native <optgroup>s: the grouping IS the explanation, and a
   native select gets iPad's own picker for free rather than a hand-rolled
   list that has to be re-solved for touch.

   The Clock three are properties BOTH clocks have, so they carry a target
   toggle. The Session three exist only in Live and carry none - and the
   TYPES enforce that, so a Session step cannot be aimed at the practice
   clock even if this list were wrong. */
const ABLETON_STEP_KINDS: {
  group: 'Clock' | 'Ableton session' | 'Slave Ableton';
  id: AbletonStep['type'];
  label: string;
}[] = [
  { group: 'Clock', id: 'ableton.tempo', label: 'Tempo (BPM)' },
  { group: 'Clock', id: 'ableton.timesig', label: 'Time signature' },
  // Practice transport sits in Clock because the practice clock IS a clock,
  // and it is a SEPARATE entry from Ableton Transport below rather than a
  // target toggle on it - the verbs are inverted between the two, so one
  // control carrying both would change meaning under the operator. See
  // PracticeTransportStep's comment and OPBridge's "COLLISION" entry.
  { group: 'Clock', id: 'practice.transport', label: 'Practice transport' },
  { group: 'Ableton session', id: 'ableton.transport', label: 'Transport' },
  { group: 'Ableton session', id: 'ableton.recquant', label: 'Record quantize' },
  { group: 'Ableton session', id: 'ableton.launchquant', label: 'Launch quantize' },
  { group: 'Ableton session', id: 'ableton.metronome', label: 'Metronome' },
  { group: 'Ableton session', id: 'ableton.endsessionrecord', label: 'End Session Record' },
  { group: 'Ableton session', id: 'opmotion.crossfade', label: 'Crossfader (OP-Motion)' },
  { group: 'Ableton session', id: 'opmotion.capture', label: 'Capture (OP-Motion)' },
  { group: 'Ableton session', id: 'opmotion.land', label: 'Landing preset (OP-Motion)' },
  { group: 'Ableton session', id: 'ableton.sustainkill', label: 'Sustain kill (release pedal)' },
  // Its own group because it is the only step here that addresses a
  // DIFFERENT MACHINE. Filing it under "Ableton session" would read as
  // something that happens to the master's set, which is the one thing it
  // never does.
  { group: 'Slave Ableton', id: 'ableton.resync', label: 'Resync (snap to master)' },
];

/* `freshAbletonStep` (a fresh step of the chosen kind, with that kind's own
   default) moved to `ableton/rows.ts` (DEC-291), so the palette's "One
   button" and this picker build the same step. Switching kind still REPLACES
   the step wholesale rather than patching it: the fields do not overlap (a
   tempo has a `bpm`, a quant a `label`), and carrying one into the other
   would leave a step that fails validation at dispatch with no visible
   cause. */

/**
 * Narrow a step to one kind, or `null`.
 *
 * Needed because the editor renders through `<Index>`, where `step` is an
 * ACCESSOR. TypeScript narrows `s.type === 'x' ? s.foo : ...` on a plain
 * value, but cannot carry that narrowing across two separate `step()` CALLS
 * - each call is, as far as the checker knows, free to return something
 * different. This gives one call and a real narrow, instead of sprinkling
 * `as` casts that would silently rot the moment a step's fields change.
 *
 * `<Index>` is not optional here - see the comment at the list itself.
 */
function stepAs<T extends AbletonStep['type']>(
  s: AbletonStep, type: T,
): Extract<AbletonStep, { type: T }> | null {
  return s.type === type ? (s as Extract<AbletonStep, { type: T }>) : null;
}

/** The step's clock target, or `null` for the kinds that have no second
 *  target (both transports, and all three Ableton-session properties). */
function clockOf(s: AbletonStep): 'ableton' | 'practice' | null {
  return 'clock' in s ? s.clock : null;
}

/**
 * OBS Scene editor - the whole "What it does" body for an `actionType: 'obs'`
 * button, replacing the MIDI block the way AbletonStepsEditor does.
 *
 * The picker is a LIVE list read from the bridge this button is aimed at, not
 * a free-text box with a hopeful label. Scene names are the operator's own,
 * they live on another machine, and typing one by hand is a silent 422 the
 * night you find out you typed "Cam 2" and OBS calls it "CAM 2". So the list
 * is the primary path.
 *
 * But it is NOT the only path, and that is deliberate. Layouts get authored
 * on a laptop in a cafe with no bridge in reach, and a picker that offers
 * nothing there would make the control unauthorable exactly when there is
 * time to author it. So a free-text row sits under the list, always, and the
 * current value is shown whether or not the list contains it - with the
 * mismatch called out rather than silently corrected.
 *
 * Every failure to read the list is named, because each has a different
 * owner: `unauthorized` is OPBridge's to fix (the `opbuttons-full` bundle
 * does not grant the OBS scopes - measured, not assumed), `unavailable` is
 * the operator's (start OBS), and the rest is ours.
 */
/**
 * Item ids this panel has already auto-aimed at a machine.
 *
 * Without it the auto-pick effect below re-fires the moment its own write is
 * undone, so Ctrl+Z on an auto-aimed button does nothing visible and undo
 * reads as broken. `editItem` goes through `projectHistory`, so the automatic
 * write IS an undo step whether or not anyone wanted it to be; the cheap
 * honest answer is to aim once and then leave the operator in charge.
 *
 * Module-level and never cleared: "we have already offered this item an
 * opinion" is true for the life of the session, and re-offering it after a
 * deliberate clear is exactly the behaviour being prevented.
 */
const autoAimedObsItems = new Set<string>();

function ObsSceneEditor(props: { item: Extract<LayoutItem, { kind: 'button' }> }) {
  const scene = () => props.item.obsScene ?? '';
  /** The bridge this button fires at. One only - OBS is per-machine. Empty
   *  string when none is chosen, which is a refusal state, not a default. */
  // Resolve on READ: the stored value may be a node id (post-migration) or
  // a legacy local row id, and the <select> is keyed by local row id.
  const bridgeId = () => aimedRowId(props.item.bridgeIds);
  const st = () => obsState(bridgeId());
  const [busy, setBusy] = createSignal(false);

  /** Every paired bridge, as a machine the operator might mean. Labelled by
   *  MACHINE name (`GAMING-PC`), not by bridge id or the row's nickname:
   *  the question this control asks is "which computer is running OBS", and
   *  a bridge id is not an answer to it. Falls back through the row's own
   *  name to the id so an un-named row is still selectable rather than
   *  rendering as a blank option. */
  const machines = () => bridges().map((b) => ({
    id: b.id,
    label: machineNameFromInstanceName(b.bridge_instance_name)
      || b.bridge_instance_name
      || b.name
      || b.id,
    connected: bridgeConnectedReactive(b.id),
  }));

  async function refresh(): Promise<void> {
    if (!bridgeId() || busy()) return;
    setBusy(true);
    try {
      await refreshObsScenes(bridgeId());
    } finally {
      setBusy(false);
    }
  }

  // Read the list as soon as the panel opens on an OBS button, and again if
  // the operator re-aims it at another machine. Without this the picker would
  // sit empty until the slow correction poll came round, which reads as
  // "OBS support is broken" on first contact - the one impression that
  // matters most for a control nobody has used before.
  createEffect(() => {
    const id = bridgeId();
    if (!id) return;
    if (obsState(id).status !== 'unknown') return;
    void refresh();
  });

  /**
   * The only thing about the bridge list this panel actually cares about:
   * WHICH machines exist and which are reachable.
   *
   * A memo, not a raw `bridges()` read, and that is load-bearing. `bridges()`
   * is rewritten by health ticks whenever any row's metadata moves - a cert
   * expiry, a WebTransport hash - and an effect tracking it directly would
   * re-run on each of those. The effect below performs a NETWORK FAN-OUT, so
   * "re-runs occasionally for an unrelated reason" means "dials every machine
   * again for an answer that has not changed". A memo dedupes by value, so
   * the effect wakes only when a machine appears, disappears or changes
   * connection state - which are exactly the three events that can change
   * the answer.
   */
  const machineKey = createMemo(() => bridges()
    .map((b) => `${b.id}:${bridgeConnectedReactive(b.id) ? 1 : 0}`)
    .join(','));

  // Ask every connected bridge whether it has OBS, while this panel is open.
  // That is what lets the Computer dropdown annotate each machine ("4
  // scenes" / "no OBS here") instead of listing bare names the operator has
  // to guess between - and it is what lets the auto-pick below find the one
  // machine that actually has OBS. Bounded to "the panel is open": a bridge
  // with no OBS must not cost a failed dial every 20s forever, which is why
  // this is not in the poll loop.
  createEffect(() => {
    if (!machineKey()) return;
    if (bridges().length < 2) return;
    void discoverObsBridges();
  });

  // Aim an unaimed button at the only machine it could mean.
  //
  // The operator's own framing: there is generally ONE OBS on the LAN, so
  // the common rig should need no machine configuration at all. This runs
  // after discovery, so by the time there are several bridges it is picking
  // the one with a REACHABLE OBS rather than the only row in the list.
  // `autoPickObsBridge` returns null the moment there is a genuine choice -
  // that is what the dropdown is for, and guessing it would be the silent
  // misroute DEC-205 refuses.
  //
  // ONCE per item (see `autoAimedObsItems`): this write is undoable, and an
  // effect that re-applies itself on undo turns Ctrl+Z into a no-op.
  createEffect(() => {
    void machineKey();
    if (autoAimedObsItems.has(props.item.id)) return;
    // Only while never set: `[]` is an explicit None and must stick.
    if (props.item.bridgeIds !== undefined) return;
    const pick = autoPickObsBridge(bridges().map((b) => b.id));
    if (!pick) return;
    autoAimedObsItems.add(props.item.id);
    editItem(props.item.id, { bridgeIds: [pick] });
  });

  const known = () => st().scenes;
  const missing = () => !!scene() && st().status === 'ok' && !known().includes(scene());

  return (
    <div class="inspector-zone">
      {/* The machine is picked under "Sends to" at the bottom of What it
          does (operator, 2026-09-23: every destination lives there), as a
          SINGLE-select `SingleMachineField` - OBS has one program scene per
          machine, so firing one switch at several makes no sense. It used to
          be a "Computer" dropdown here, above the scenes. On a one-OBS LAN,
          the operator's normal case, the auto-pick below fills it in and it
          is never touched. */}
      <label class="inspector-field">
        <span>Scene</span>
        <select
          value={known().includes(scene()) ? scene() : ''}
          disabled={known().length === 0}
          onChange={(e) => editItem(props.item.id, { obsScene: e.currentTarget.value })}
        >
          <option value="">
            {known().length > 0 ? 'pick a scene' : bridgeId() ? 'no scenes read' : (bridges().length === 0 ? 'No computer yet' : 'Pick a computer')}
          </option>
          <For each={known()}>
            {(name) => <option value={name}>{name}</option>}
          </For>
        </select>
      </label>

      {/* Always present, never only-on-failure. See the component header:
          authoring happens away from the rig, and this is the path that
          still works there. */}
      <label class="inspector-field">
        <span>Or type it</span>
        {/* The placeholder is short on purpose: this field is narrow, and a
            longer string truncated mid-word, which reads as a broken control
            rather than as a hint. Seen in the render, not reasoned about. */}
        <input
          type="text"
          value={scene()}
          placeholder="exact name"
          onInput={(e) => editItem(props.item.id, { obsScene: e.currentTarget.value })}
        />
      </label>

      <div class="inspector-pills">
        <button
          type="button"
          class="inspector-pill"
          disabled={!bridgeId() || busy()}
          onClick={() => void refresh()}
          title="Read the scene list from OBS on this bridge"
        >
          {busy() ? 'Reading...' : 'Refresh scenes'}
        </button>
        <Show when={st().status === 'ok' && st().current}>
          <span class="inspector-hint">Live now: {st().current}</span>
        </Show>
      </div>

      <Show when={!bridgeId() && machines().length > 1}>
        <InfoHint title="Which computer runs OBS?" short={'Pick the computer running OBS under "Sends to".'}>
          <p>More than one computer is paired, and more than one could be running OBS.</p>
          <p>This button is refused until you pick one, rather than guessing. A send to the
          wrong computer comes back as "OBS not reachable", which looks like OBS was shut
          rather than like a wrong computer - so a wrong guess would not announce itself.</p>
        </InfoHint>
      </Show>
      <Show when={st().status === 'unauthorized'}>
        <InfoHint title="No OBS access" short="This OPBridge has not given OPButtons OBS access.">
          <p>OPBridge decides what each paired app may do. Its OPButtons permissions
          (the <code>opbuttons-full</code> bundle) need <code>/obs/scenes</code> and
          <code>/obs/scene/**</code>.</p>
          <p>That is fixed on OPBridge's side, and existing pairings pick it up with no
          re-pair. Until then the scene list stays empty and the button cannot switch.</p>
        </InfoHint>
      </Show>
      <Show when={st().status === 'unavailable'}>
        <InfoHint title="OBS is not answering" short="OBS is not answering - start OBS on that computer.">
          <p>OPBridge on that computer could not reach OBS.</p>
          <p>Start OBS, then check <b>Tools &gt; WebSocket Server Settings</b> has the
          server enabled - OPBridge talks to OBS through it.</p>
        </InfoHint>
      </Show>
      <Show when={st().status === 'error' && st().detail}>
        <span class="inspector-hint">{st().detail}</span>
      </Show>
      <Show when={missing()}>
        <InfoHint title="Scene not in OBS" short={<>OBS has no scene called "{scene()}".</>}>
          <p>The scene was renamed or deleted in OBS.</p>
          <p>This button will not switch anything until it names a scene that exists - pick
          one from the list above.</p>
        </InfoHint>
      </Show>
      <Show when={!scene()}>
        <span class="inspector-hint">No scene chosen - this button does nothing yet.</span>
      </Show>
    </div>
  );
}

/** Item ids this panel has already auto-aimed - see `autoAimedObsItems`. */
const autoAimedProgramItems = new Set<string>();

/**
 * Run Program editor - the "What it does" body for an
 * `actionType: 'runProgram'` button.
 *
 * A picker over the target machine's registered programs, and deliberately
 * NOT a path box. OPBridge only runs what the owner of that machine
 * registered there (loopback-only), so the only thing this button can
 * honestly offer is that list. There is a typed-id fallback for the same
 * reason OBS has one: authoring happens away from the rig.
 *
 * The empty list is the first thing a new user meets, so its copy says
 * where programs come from rather than just "none".
 */
function RunProgramEditor(props: { item: Extract<LayoutItem, { kind: 'button' }> }) {
  const programId = () => props.item.runProgramId ?? '';
  const bridgeId = () => aimedRowId(props.item.bridgeIds);
  const st = () => programState(bridgeId());
  const [busy, setBusy] = createSignal(false);

  async function refresh(): Promise<void> {
    if (!bridgeId() || busy()) return;
    setBusy(true);
    try {
      await refreshPrograms(bridgeId());
    } finally {
      setBusy(false);
    }
  }

  // Read the list as soon as the panel opens, and again on re-aim.
  createEffect(() => {
    const id = bridgeId();
    if (!id) return;
    if (programState(id).status !== 'unknown') return;
    void refresh();
  });

  // Same dedupe as the OBS editor: only wake the fan-out when a machine
  // appears, disappears or changes connection state.
  const machineKey = createMemo(() => bridges()
    .map((b) => `${b.id}:${bridgeConnectedReactive(b.id) ? 1 : 0}`)
    .join(','));

  /** Re-run the auto-pick when a discovery read lands, not only when the
   *  bridge list moves - "which machine HAS programs" is only known after. */
  const programStatesKey = createMemo(() => bridges()
    .map((b) => `${b.id}:${programState(b.id).status}:${programState(b.id).programs.length}`)
    .join(','));

  // Ask every connected bridge for its list while the panel is open, so the
  // "Sends to" rows can say "3 programs" / "no programs yet".
  createEffect(() => {
    if (!machineKey()) return;
    if (bridges().length < 2) return;
    void discoverProgramBridges();
  });

  // Aim an unaimed button at the only machine it could mean. Once per item,
  // so Ctrl+Z on the automatic aim is not immediately re-applied.
  createEffect(() => {
    void machineKey();
    void programStatesKey();
    if (autoAimedProgramItems.has(props.item.id)) return;
    // Only while never set: `[]` is an explicit None and must stick.
    if (props.item.bridgeIds !== undefined) return;
    const pick = autoPickProgramBridge(bridges().map((b) => b.id));
    if (!pick) return;
    autoAimedProgramItems.add(props.item.id);
    editItem(props.item.id, { bridgeIds: [pick] });
  });

  const known = () => st().programs;
  const missing = () => !!programId() && st().status === 'ok'
    && !known().some((p) => p.id === programId());
  const chosen = () => known().find((p) => p.id === programId());

  /** Pick a program. An unnamed button takes the program's name, so the
   *  common path (drop, pick) leaves a labelled button on stage rather than
   *  a blank one. A name the operator typed is never overwritten. */
  function choose(id: string): void {
    const entry = known().find((p) => p.id === id);
    const patch: { runProgramId: string; name?: string } = { runProgramId: id };
    if (entry && !(props.item.name ?? '').trim()) patch.name = programDisplayName(entry);
    editItem(props.item.id, patch);
  }

  return (
    <div class="inspector-zone">
      <label class="inspector-field">
        <span>Program</span>
        <select
          value={chosen() ? programId() : ''}
          disabled={known().length === 0}
          onChange={(e) => choose(e.currentTarget.value)}
        >
          <option value="">
            {known().length > 0 ? 'pick a program'
              : !bridgeId() ? (bridges().length === 0 ? 'No computer yet' : 'Pick a computer')
              : st().status === 'ok' ? 'none on that computer yet'
              : 'no list read'}
          </option>
          <For each={known()}>
            {(p) => <option value={p.id}>{programDisplayName(p)}</option>}
          </For>
        </select>
      </label>

      <label class="inspector-field">
        <span>Program id</span>
        <input
          type="text"
          value={programId()}
          placeholder="program id"
          onInput={(e) => editItem(props.item.id, { runProgramId: e.currentTarget.value })}
        />
      </label>

      <div class="inspector-pills">
        <button
          type="button"
          class="inspector-pill"
          disabled={!bridgeId() || busy()}
          onClick={() => void refresh()}
          title="Read the program list from OPBridge on that computer"
        >
          {busy() ? 'Reading...' : 'Refresh programs'}
        </button>
        <Show when={chosen()?.state === 'running'}>
          <span class="inspector-hint">Running now</span>
        </Show>
      </div>

      <Show when={chosen()?.mode === 'approve'}>
        <span class="inspector-hint">Asks first - you OK it on that computer.</span>
      </Show>
      <Show when={st().status === 'ok' && known().length === 0}>
        <InfoHint title="Adding a program" label="How?" short="No programs on that computer yet - add them in OPBridge.">
          <p>On the computer that should run it, open OPBridge's dashboard and go to
          <b> Programs on this computer</b>.</p>
          <p>Add program, pick it from the programs running there (or type its
          location), give it a name, choose <b>Start it straight away</b> or
          <b> Ask me first</b>, and Save.</p>
          <p>Only that computer can add its own programs - so nothing on the network can
          make a computer run something its owner did not put on its list. Once it is
          there, this button can start it from anywhere in your rig.</p>
        </InfoHint>
      </Show>
      <Show when={!bridgeId() && bridges().length > 1}>
        <InfoHint title="Which computer runs it?" short={'Pick the computer that runs it under "Sends to".'}>
          <p>More than one computer is paired, and each has its own list of programs.</p>
          <p>This button is refused until you pick one, rather than guessing - the wrong
          computer just answers "no such program", which looks like the program was
          removed.</p>
        </InfoHint>
      </Show>
      <Show when={st().status === 'unauthorized'}>
        <InfoHint title="No program access" short="This OPBridge has not given OPButtons program access.">
          <p>OPBridge decides what each paired app may do. Its OPButtons permissions
          (the <code>opbuttons-full</code> bundle) need <code>/programs</code> and
          <code>/program/launch/*</code> - OPBridge v1.15.159 and later have them.</p>
          <p>Update OPBridge on that computer. Existing pairings pick it up with no
          re-pair. Until then the list stays empty and the button cannot start anything.</p>
        </InfoHint>
      </Show>
      <Show when={st().status === 'error' && st().detail}>
        <span class="inspector-hint">{st().detail}</span>
      </Show>
      <Show when={missing()}>
        <InfoHint title="Program not on that computer" short={<>No program "{programId()}" on that computer.</>}>
          <p>It was removed from that computer's list in OPBridge, or this button is aimed
          at a different computer from the one it was set up on.</p>
          <p>Pick a program from the list above, or change "Sends to".</p>
        </InfoHint>
      </Show>
      <Show when={!programId()}>
        <span class="inspector-hint">No program chosen - this button does nothing yet.</span>
      </Show>
    </div>
  );
}

/** Item ids this panel has already auto-aimed - see `autoAimedObsItems`. */
const autoAimedPresetItems = new Set<string>();

/** "Stop after" choices, in beats. `undefined` = runs until stopped. */
const PRESET_LENGTHS: Array<{ beats: number | undefined; label: string }> = [
  { beats: undefined, label: 'Until stopped' },
  { beats: 4, label: '4 beats' },
  { beats: 8, label: '8 beats' },
  { beats: 16, label: '16 beats' },
  { beats: 32, label: '32 beats' },
];

/** A choice knob stays a row of pills only while it fits one: at most three
 *  options, none longer than seven characters. */
function knobFitsPills(k: LightingPresetKnob): boolean {
  return k.options.length <= 3 && k.options.every((o) => o.length <= 7);
}

/**
 * Light Preset editor - the "What it does" body for an
 * `actionType: 'lightPreset'` button.
 *
 * A picker over the target machine's preset catalogue (OPBridge's built-in
 * list, DEC-601), then one control per knob drawn from the catalogue's own
 * description of it - swatches for a colour, pills for a choice, a slider
 * for a level - so a preset OPBridge adds tomorrow gets its controls with no
 * change here. Only knobs the operator actually changes are stored; the rest
 * follow the preset's defaults.
 *
 * Colours are the stage palette (DEC-058), not a wheel, for the reason that
 * palette records: lighting is thought about in named colours, and on an
 * iPad a swatch is a tap target where a wheel is a fiddle. The first swatch
 * is the preset's OWN default colour, so "leave it as designed" is one tap.
 */
function LightPresetEditor(props: { item: Extract<LayoutItem, { kind: 'button' }> }) {
  const cfg = (): LightPresetConfig => props.item.lightPreset ?? { presetId: '' };
  const bridgeId = () => aimedRowId(props.item.bridgeIds);
  const st = () => presetState(bridgeId());
  const [busy, setBusy] = createSignal(false);

  async function refresh(): Promise<void> {
    if (!bridgeId() || busy()) return;
    setBusy(true);
    try {
      await refreshPresets(bridgeId());
    } finally {
      setBusy(false);
    }
  }

  // Read the list as soon as the panel opens, and again on re-aim.
  createEffect(() => {
    const id = bridgeId();
    if (!id) return;
    if (presetState(id).status !== 'unknown') return;
    void refresh();
  });

  // Aim an unaimed button at the machine that outputs the rig's lights.
  // Once per item, so Ctrl+Z on the automatic aim is not re-applied.
  const machineKey = createMemo(() => `${bridges().map((b) => `${b.id}:${b.nodeId ?? ''}`).join(',')}|${fixtures().map((f) => f.outputNodeId ?? '').join(',')}`);
  createEffect(() => {
    void machineKey();
    if (autoAimedPresetItems.has(props.item.id)) return;
    // Only while never set: `[]` is an explicit None and must stick.
    if (props.item.bridgeIds !== undefined) return;
    const pick = autoPickPresetBridge(bridges());
    if (!pick) return;
    autoAimedPresetItems.add(props.item.id);
    editItem(props.item.id, { bridgeIds: [pick] });
  });

  const presets = () => st().presets;
  const chosen = () => presetById(cfg().presetId, st());
  const missing = () => !!cfg().presetId && st().status === 'ok' && !chosen();

  function save(next: LightPresetConfig): void {
    editItem(props.item.id, { lightPreset: next });
  }
  /** The config without one optional field. */
  function without(key: 'knobs' | 'target' | 'lengthBeats'): LightPresetConfig {
    const next = { ...cfg() };
    delete next[key];
    return next;
  }

  /** Pick a preset. Knob overrides belong to the preset they were set on
   *  (keys differ between presets), so they are dropped; lights and length
   *  are about the button, not the preset, and stay. An unnamed button
   *  takes the preset's name. */
  function choose(id: string): void {
    const p = presets().find((x) => x.id === id);
    const patch: { lightPreset: LightPresetConfig; name?: string } = {
      lightPreset: { ...without('knobs'), presetId: id },
    };
    if (p && !(props.item.name ?? '').trim()) patch.name = p.name;
    editItem(props.item.id, patch);
  }

  const knobValue = (k: LightingPresetKnob): string | number => cfg().knobs?.[k.key] ?? k.default;
  const overridden = (k: LightingPresetKnob): boolean => cfg().knobs?.[k.key] !== undefined;

  /** Set a knob. Setting it back to the preset's default REMOVES the
   *  override, so only real choices are stored. */
  function setKnob(k: LightingPresetKnob, value: string | number): void {
    const knobs = { ...(cfg().knobs ?? {}) };
    const isDefault = typeof value === 'string' && typeof k.default === 'string'
      ? value.toLowerCase() === k.default.toLowerCase()
      : value === k.default;
    if (isDefault) delete knobs[k.key]; else knobs[k.key] = value;
    save(Object.keys(knobs).length > 0 ? { ...without('knobs'), knobs } : without('knobs'));
  }

  function setTarget(target: DmxTarget): void {
    save(target.kind === 'all' ? without('target') : { ...without('target'), target });
  }
  function setLength(beats: number | undefined): void {
    save(beats === undefined ? without('lengthBeats') : { ...without('lengthBeats'), lengthBeats: beats });
  }

  const mode = () => props.item.mode ?? 'toggle';
  const swatches = STAGE_COLORS.filter((c) => c.label !== 'Off');

  return (
    <div class="inspector-zone" data-light-preset-editor="true">
      <label class="inspector-field">
        <span>Preset</span>
        <select
          value={chosen() ? cfg().presetId : ''}
          disabled={presets().length === 0}
          onChange={(e) => choose(e.currentTarget.value)}
        >
          <option value="">
            {presets().length > 0 ? 'pick a preset'
              : !bridgeId() ? (bridges().length === 0 ? 'No computer yet' : 'Pick a computer')
              : st().status === 'unknown' ? 'reading the list...'
              : 'no list read'}
          </option>
          <optgroup label="Across all the lights">
            <For each={presets().filter((p) => p.scope === 'global')}>
              {(p) => <option value={p.id}>{p.name}</option>}
            </For>
          </optgroup>
          <optgroup label="For one light">
            <For each={presets().filter((p) => p.scope === 'single')}>
              {(p) => <option value={p.id}>{p.name}</option>}
            </For>
          </optgroup>
        </select>
      </label>

      <Show when={chosen()}>
        {(p) => (
          <>
            <span class="inspector-hint">{p().description}</span>
            <Show when={presetIsPhotosensitive(p())}>
              <p class="sd-picker-note" data-tone="warn">
                Flashes fast - it can affect people with photosensitive epilepsy.
              </p>
            </Show>
            <For each={p().knobs}>
              {(k) => (
                <Switch>
                  <Match when={k.kind === 'color'}>
                    {/* Two rows, deliberately: any `.inspector-pills` row
                        holding a pill becomes a no-wrap segmented control
                        (app.css `:has(.inspector-pill)`), so a pill among the
                        swatches stopped them wrapping and pushed the whole
                        panel sideways (seen 2026-09-29). */}
                    <div data-knob={k.key}>
                    <div class="inspector-pills">
                      <span class="inspector-pills-label">{k.label}</span>
                      {/* WORDED, not a bare swatch: the preset's own colour
                          is usually a red or a white that also sits in the
                          palette, and on an iPad there is no hover title to
                          tell "as designed" from "I picked red". The chip
                          shows which colour that is. */}
                      <button
                        type="button"
                        class="inspector-pill"
                        data-knob-default="true"
                        data-selected={overridden(k) ? 'false' : 'true'}
                        title="The colour this preset was designed with"
                        onClick={() => setKnob(k, k.default)}
                      >
                        <span
                          aria-hidden="true"
                          style={{
                            display: 'inline-block', width: '0.8em', height: '0.8em', 'border-radius': '50%',
                            background: String(k.default), 'margin-right': '0.4em', 'vertical-align': '-0.1em',
                          }}
                        />
                        Its own
                      </button>
                    </div>
                    <div class="inspector-pills">
                      <span class="inspector-pills-label" />
                      <For each={swatches}>
                        {(c) => {
                          const hex = rgbToHex(c.rgb);
                          return (
                            <button
                              type="button"
                              class="inspector-swatch sk-swatch"
                              style={{ '--swatch': hex }}
                              data-selected={overridden(k) && String(knobValue(k)).toLowerCase() === hex ? 'true' : 'false'}
                              title={c.label}
                              aria-label={c.label}
                              onClick={() => setKnob(k, hex)}
                            />
                          );
                        }}
                      </For>
                    </div>
                    </div>
                  </Match>
                  {/* Pills only while they fit a segmented row: at most three
                      short options. Measured 2026-09-29: Flow's four laps
                      and four directions ran their labels into each other.
                      Longer lists are a select, which on the iPad opens the
                      system picker - one tap, every option readable. */}
                  <Match when={k.kind === 'choice' && !knobFitsPills(k)}>
                    <label class="inspector-field" data-knob={k.key}>
                      <span>{k.label}</span>
                      <select value={String(knobValue(k))} onChange={(e) => setKnob(k, e.currentTarget.value)}>
                        <For each={k.options}>{(opt) => <option value={opt}>{opt}</option>}</For>
                      </select>
                    </label>
                  </Match>
                  <Match when={k.kind === 'choice'}>
                    <div class="inspector-pills" data-knob={k.key}>
                      <span class="inspector-pills-label">{k.label}</span>
                      <For each={k.options}>
                        {(opt) => (
                          <button
                            type="button"
                            class="inspector-pill"
                            data-selected={knobValue(k) === opt ? 'true' : 'false'}
                            onClick={() => setKnob(k, opt)}
                          >
                            {opt}
                          </button>
                        )}
                      </For>
                    </div>
                  </Match>
                  <Match when={k.kind === 'level'}>
                    <label class="inspector-field" data-knob={k.key}>
                      <span>{k.label}</span>
                      <input
                        type="range" min="0" max="1" step="0.05"
                        value={Number(knobValue(k))}
                        onChange={(e) => setKnob(k, Math.round(Number(e.currentTarget.value) * 100) / 100)}
                      />
                    </label>
                  </Match>
                </Switch>
              )}
            </For>
          </>
        )}
      </Show>

      <LightTargetPicker
        target={cfg().target ?? { kind: 'all' }}
        onChange={setTarget}
        onManageGroups={openLightsManager}
        wholeLightsOnly
      />
      <Show when={chosen()?.scope === 'single' && (!cfg().target || cfg().target!.kind === 'all')}>
        <span class="inspector-hint">Made for one light - on All lights, every light plays it.</span>
      </Show>

      <label class="inspector-field" data-preset-length="true">
        <span>Stop after</span>
        <select
          value={cfg().lengthBeats === undefined ? '' : String(cfg().lengthBeats)}
          onChange={(e) => setLength(e.currentTarget.value === '' ? undefined : Number(e.currentTarget.value))}
        >
          <For each={PRESET_LENGTHS}>
            {(l) => <option value={l.beats === undefined ? '' : String(l.beats)}>{l.label}</option>}
          </For>
        </select>
      </label>
      <Show when={mode() === 'trigger' && cfg().lengthBeats === undefined}>
        <span class="inspector-hint">Trigger never stops it: pick a Stop after, or Toggle.</span>
      </Show>

      <div class="inspector-pills">
        <button
          type="button"
          class="inspector-pill"
          disabled={!bridgeId() || busy()}
          onClick={() => void refresh()}
          title="Read the preset list from OPBridge on that computer"
        >
          {busy() ? 'Reading...' : 'Refresh presets'}
        </button>
      </div>

      <Show when={!bridgeId() && bridges().length > 1}>
        <InfoHint title="Which computer are the lights on?" short={'Pick the computer your lights are wired to under "Sends to".'}>
          <p>More than one computer is paired. A preset plays on the lights wired to the
          computer it is sent to, and is not passed on to the others.</p>
          <p>This button is refused until you pick one, rather than guessing - the wrong
          computer would play it on its own lights, or on none, and still say it worked.</p>
        </InfoHint>
      </Show>
      <Show when={st().status === 'unauthorized' || st().status === 'unsupported' || (st().status === 'error' && st().detail)}>
        <span class="inspector-hint">{st().detail}</span>
      </Show>
      <Show when={missing()}>
        <span class="inspector-hint">No preset "{cfg().presetId}" on that computer - pick one above.</span>
      </Show>
      <Show when={!cfg().presetId}>
        <span class="inspector-hint">No preset chosen - this button does nothing yet.</span>
      </Show>
    </div>
  );
}

/**
 * Ableton Device editor - the "What it does" body for an
 * `actionType: 'abletonDevice'` button, replacing the MIDI block the way
 * ObsSceneEditor does.
 *
 * # Why this is free text where OBS got a picker
 *
 * `OP-ButtonsDevice` has a `list` verb, so a picker is buildable - but
 * OPBridge does not ingest its three `list_*` replies yet (measured
 * 2026-09-20: unknown address tails are logged and dropped), so there is
 * nothing to populate one from today. Typed names are therefore the only
 * path, which raises the stakes on the hints below: a name that matches
 * nothing is the exact failure this feature was built to make VISIBLE, and
 * the Inspector is where it gets explained at length.
 *
 * # What the operator can type, which is more forgiving than it looks
 *
 * The device matches on the device's NAME **or its CLASS**, case-insensitively,
 * **exact first and substring only if nothing matched exactly**. So `reverb`
 * finds Live's stock Reverb, and `valhalla` finds `ValhallaRoom`. Spaces are
 * fine - the device converts them to `_` on both sides before comparing.
 */
function AbletonDeviceEditor(props: { item: Extract<LayoutItem, { kind: 'button' }> }) {
  const target = () => props.item.abletonDevice;
  const trackName = () => target()?.trackName ?? '';
  const deviceName = () => target()?.deviceName ?? '';
  const nth = () => target()?.nth ?? 1;
  /** One bridge only. The address is loopback, so "several machines" would
   *  mean asking several different Live sets and lighting from whichever
   *  replied last. */
  // Resolve on READ: the stored value may be a node id (post-migration) or
  // a legacy local row id, and the <select> is keyed by local row id.
  const bridgeId = () => aimedRowId(props.item.bridgeIds);
  const st = () => abletonDeviceStateFor(bridgeId());
  const [busy, setBusy] = createSignal(false);

  const patch = (p: { trackName?: string; deviceName?: string; nth?: number }) =>
    editItem(props.item.id, {
      abletonDevice: {
        trackName: trackName(),
        deviceName: deviceName(),
        ...(target()?.nth !== undefined ? { nth: target()!.nth } : {}),
        ...p,
      },
    });

  const machines = () => bridges().map((b) => ({
    id: b.id,
    label: machineNameFromInstanceName(b.bridge_instance_name)
      || b.bridge_instance_name
      || b.name
      || b.id,
    connected: bridgeConnectedReactive(b.id),
  }));

  async function refresh(): Promise<void> {
    if (!bridgeId() || busy()) return;
    setBusy(true);
    try {
      await refreshDeviceStatus(bridgeId());
    } finally {
      setBusy(false);
    }
  }

  // Read as soon as the panel opens, and again on re-aim - same reasoning as
  // the OBS editor: an empty panel on first contact reads as "this feature
  // is broken" rather than "nothing has been asked yet".
  createEffect(() => {
    const id = bridgeId();
    if (!id) return;
    if (abletonDeviceStateFor(id).status !== 'unknown') return;
    void refresh();
  });

  /** The live fault, so the panel explains exactly what the face is showing
   *  rather than offering a second opinion about it. */
  const fault = () => abletonDeviceFault(target(), props.item.bridgeIds, st());

  /** Have we actually heard about THIS name? Drives the "asked, nothing came
   *  back" hint, which is the honest reading of an absent entry - see
   *  `deviceControl.ts::OPBRIDGE_NO_MATCH_ARITY_FIXED_IN`. */
  const heardOfIt = () => {
    if (!trackName() || !deviceName()) return false;
    const wantTrack = deviceKey(trackName());
    const wantName = deviceKey(deviceName());
    return st().readings.some(
      (r) => deviceKey(r.trackName) === wantTrack
        && deviceKey(r.deviceName).includes(wantName),
    );
  };

  return (
    <div class="inspector-zone">
      {/* The computer (whose Live set) is picked under "Sends to" at the
          bottom of What it does, single-select (`SingleMachineField`) -
          moved there 2026-09-23 with every other destination. Unlike OBS it
          is never auto-picked: see `addAbletonDevice` on why a wrong guess
          here cannot announce itself. */}
      <label class="inspector-field">
        <span>Track</span>
        <input
          type="text"
          value={trackName()}
          placeholder="e.g. Vocals"
          onInput={(e) => patch({ trackName: e.currentTarget.value })}
        />
      </label>

      <label class="inspector-field">
        <span>Device</span>
        <input
          type="text"
          value={deviceName()}
          placeholder="e.g. Reverb"
          onInput={(e) => patch({ deviceName: e.currentTarget.value })}
        />
      </label>

      {/* `nth` only earns its slot once the name is genuinely repeated, and
          the device REFUSES a multiple match at nth 1 rather than picking -
          so this row is the operator's way of saying "yes, I mean the second
          one". Left at 1 it stays out of the way. */}
      <label class="inspector-field">
        <span>Which one</span>
        <input
          type="number"
          min="1"
          value={nth()}
          onInput={(e) => {
            const n = Math.round(Number(e.currentTarget.value));
            patch({ nth: Number.isFinite(n) && n >= 1 ? n : 1 });
          }}
        />
      </label>

      <div class="inspector-pills">
        <button
          type="button"
          class="inspector-pill"
          disabled={!bridgeId() || busy()}
          onClick={() => void refresh()}
          title="Re-read device state from Ableton on this bridge"
        >
          {busy() ? 'Reading...' : 'Refresh state'}
        </button>
        <Show when={st().deviceVersion}>
          <span class="inspector-hint">OP-ButtonsDevice {st().deviceVersion}</span>
        </Show>
      </div>

      <Show when={!bridgeId() && machines().length > 0}>
        <InfoHint title="Which computer runs Ableton?" short={'Pick the computer running Ableton under "Sends to".'}>
          <p>This button is refused until you pick one, rather than guessing.</p>
          <p>The device is reached on that computer's own loopback address, so a send to
          the wrong computer is accepted and silently dropped with no error anywhere - a
          wrong guess would never announce itself.</p>
        </InfoHint>
      </Show>
      <Show when={st().status === 'unauthorized'}>
        <InfoHint title="Re-pair needed" short="Re-pair this computer in Settings.">
          <p>This pairing is older than the permission that lets OPButtons read device
          state back from Ableton.</p>
          <p>Re-pairing the computer in Settings gives it a fresh token. Nothing in Ableton
          needs changing.</p>
        </InfoHint>
      </Show>
      <Show when={st().status === 'unavailable'}>
        <InfoHint title="OP-ButtonsDevice is not reporting" short="Load OP-ButtonsDevice in Live on that computer.">
          <p>{st().detail ?? 'OP-ButtonsDevice is not reporting on that machine.'}</p>
          <p>Load OP-ButtonsDevice on any track in the set. If it is already there, check
          its panel says <b>BIND OK</b> - a second copy in the same set binds nothing and
          stays silently deaf.</p>
        </InfoHint>
      </Show>
      <Show when={st().status === 'error' && st().detail}>
        <span class="inspector-hint">{st().detail}</span>
      </Show>

      <Show when={fault() !== null && st().status === 'ok'}>
        <span class="inspector-hint">
          Face reads "{abletonDeviceFaultLabel(fault()!)}".
        </span>
      </Show>

      {/* The honest reading of "we asked and heard nothing". It is NOT "the
          device is missing": the poll ASKS and the reply arrives later, so an
          in-flight read and a genuine miss look identical from here. Saying
          "not in Live" would be a confident wrong answer.
          A real `no_match` now DOES arrive (OPBridge fixed the scope-atom
          arity in v1.15.131, verified on the running daemon) and renders the
          `device-missing` fault properly - this hint is the other case. */}
      <Show when={st().status === 'ok' && trackName() !== '' && deviceName() !== '' && !heardOfIt()}>
        <InfoHint title="Nothing heard yet" short="Nothing heard from this device yet - check the names.">
          <p>Ableton has not reported anything for "{deviceName()}" on "{trackName()}" yet.</p>
          <p>Either the read is still on its way, or nothing on that track matches that
          name - check the spelling against Live. The name matches the device's title OR
          its type, and part of the name is enough.</p>
        </InfoHint>
      </Show>
      <Show when={!trackName() || !deviceName()}>
        <span class="inspector-hint">
          Name a track and a device - this button does nothing yet.
        </span>
      </Show>
    </div>
  );
}


function AbletonStepsEditor(props: { item: Extract<LayoutItem, { kind: 'button' }> }) {
  const steps = (): AbletonStep[] => props.item.abletonSteps ?? [];
  const write = (next: AbletonStep[]) => editItem(props.item.id, { abletonSteps: next });
  const patch = (i: number, p: Partial<AbletonStep>) =>
    write(steps().map((s, n) => (n === i ? ({ ...s, ...p } as AbletonStep) : s)));

  return (
    <div class="ableton-steps">
      {/* ⚠️ <Index>, NEVER <For>. `patch()` below spreads a fresh object
          (`{ ...s, ...p }`) on every keystroke, and <For> diffs by
          REFERENTIAL identity - so a new object per character means the row
          unmounts and remounts, and any focused <input> inside it loses
          focus. The operator experiences it as "one letter at a time".

          <Index> renders one row per POSITION and pushes value changes in
          through an Accessor, so the row stays mounted and only the reactive
          bindings inside it update.

          This is a documented recurring bug in this codebase, not a
          hypothetical: CP-042 hit it on the fixture-lane rename, the
          sections list had the same shape, and `page_manager_rename_
          survives_typing.ts` exists as a regression smoke for the same
          class. It cost an operator report here too. If you add a row with
          an input to any list, this is the first thing to check. */}
      <Index each={steps()}>
        {(step, i) => (
          <div class="ableton-step">
            <div class="ableton-step-head">
              <select
                class="ableton-step-kind"
                value={step().type}
                onChange={(e) => {
                  const next = e.currentTarget.value as AbletonStep['type'];
                  if (next === step().type) return;
                  write(steps().map((s, n) => (n === i ? freshAbletonStep(next, s.target) : s)));
                }}
              >
                <optgroup label="Clock">
                  <For each={ABLETON_STEP_KINDS.filter((k) => k.group === 'Clock')}>
                    {(k) => <option value={k.id}>{k.label}</option>}
                  </For>
                </optgroup>
                <optgroup label="Ableton session">
                  <For each={ABLETON_STEP_KINDS.filter((k) => k.group === 'Ableton session')}>
                    {(k) => <option value={k.id}>{k.label}</option>}
                  </For>
                </optgroup>
                {/* The optgroups are enumerated here rather than derived from
                    ABLETON_STEP_KINDS, so a kind in a group with no optgroup
                    renders NOWHERE and the step is unreachable - which is
                    exactly what happened when 'Slave Ableton' was added and
                    only ableton_resync_sends_osc caught it. Adding a group to
                    that list means adding a block here too. */}
                <optgroup label="Slave Ableton (streampc)">
                  <For each={ABLETON_STEP_KINDS.filter((k) => k.group === 'Slave Ableton')}>
                    {(k) => <option value={k.id}>{k.label}</option>}
                  </For>
                </optgroup>
              </select>
              <button
                type="button"
                class="ableton-step-remove"
                title="Remove this step"
                onClick={() => write(steps().filter((_, n) => n !== i))}
              >
                ×
              </button>
            </div>

            {/* Target toggle — only the steps that HAVE a `clock` field, i.e.
                tempo and time signature. Absent rather than disabled
                everywhere else: those steps have exactly one possible
                target, so a greyed toggle would pose a question with no
                answer. Note the two transports deliberately have no `clock`
                field at all - they are separate step kinds, because the
                verbs are inverted between the clocks. */}
            <Show when={clockOf(step()) !== null}>
              <div class="inspector-pills ableton-step-target">
                <span class="inspector-pills-label">Clock</span>
                <button type="button" class="inspector-pill"
                  data-selected={clockOf(step()) === 'ableton' ? 'true' : 'false'}
                  onClick={() => patch(i, { clock: 'ableton' } as Partial<AbletonStep>)}
                  title="Write the real Ableton session through OP-BridgeSync"
                >Ableton</button>
                <button type="button" class="inspector-pill"
                  data-selected={clockOf(step()) === 'practice' ? 'true' : 'false'}
                  onClick={() => patch(i, { clock: 'practice' } as Partial<AbletonStep>)}
                  title="Drive the local practice clock only - display, no output, works with no laptop"
                >Practice</button>
              </div>
            </Show>

            <Show when={step().type === 'ableton.tempo'}>
              <label class="inspector-field">
                <span>BPM</span>
                <input type="number" min="20" max="999" step="0.01"
                  value={stepAs(step(), 'ableton.tempo')?.bpm ?? 120}
                  onInput={(e) => patch(i, { bpm: Number(e.currentTarget.value) } as Partial<AbletonStep>)}
                />
              </label>
            </Show>

            <Show when={step().type === 'ableton.timesig'}>
              <label class="inspector-field">
                <span>Time signature</span>
                <span class="ableton-sig">
                  <input type="number" min="1" max="99"
                    value={stepAs(step(), 'ableton.timesig')?.numerator ?? 4}
                    onInput={(e) => patch(i, { numerator: Number(e.currentTarget.value) } as Partial<AbletonStep>)}
                  />
                  <span class="ableton-sig-slash">/</span>
                  <input type="number" min="1" max="32"
                    value={stepAs(step(), 'ableton.timesig')?.denominator ?? 4}
                    onInput={(e) => patch(i, { denominator: Number(e.currentTarget.value) } as Partial<AbletonStep>)}
                  />
                </span>
              </label>
            </Show>

            {/* ⚠️ The three verbs mean DIFFERENT things on the two clocks, so
                the labels are target-aware rather than a bare Play/Stop/
                Continue. Measured by OPBridge on the live rig 2026-08-27
                (`OPApps/opbridge-opbuttons.md`, "COLLISION" + "CORRECTION"):

                  Ableton   play = RESET to the top and roll
                            stop = PAUSE IN PLACE (does not rewind)
                            continue = resume from the paused position

                  Practice  stop REWINDS to the downbeat (DEC-091)

                So `stop` on Ableton does what `playing:false` does on the
                practice clock, and `play` on Ableton does what `stop` does
                on the practice clock - the words are inverted between them.
                Labelling both "Stop" and leaving it there is how an operator
                learns the difference on stage. OPBridge's own guidance was
                explicitly "do not merge them into one control"; we keep one
                step type but never one set of words. */}
            <Show when={step().type === 'ableton.transport'}>
              <label class="inspector-field">
                <span>Action</span>
                <select
                  value={stepAs(step(), 'ableton.transport')?.action ?? 'play'}
                  onChange={(e) => patch(i, { action: e.currentTarget.value } as Partial<AbletonStep>)}
                >
                  <option value="play">Play - restart from the top</option>
                  <option value="stop">Stop - pause in place</option>
                  <option value="continue">Continue - resume from pause</option>
                </select>
              </label>
            </Show>

            {/* Practice transport - its own step, its own words. `stop`
                REWINDS here and PAUSES on Ableton, which is exactly why
                these are two entries rather than one with a toggle. */}
            <Show when={step().type === 'practice.transport'}>
              <label class="inspector-field">
                <span>Action</span>
                <select
                  value={stepAs(step(), 'practice.transport')?.action ?? 'play'}
                  onChange={(e) => patch(i, { action: e.currentTarget.value } as Partial<AbletonStep>)}
                >
                  <option value="play">Play</option>
                  <option value="stop">Stop - rewinds to the downbeat</option>
                </select>
              </label>
            </Show>

            {/* Two quant pickers, two vocabularies, never one shared list.
                Index 8 is 1/32 in the record enum and 1/4T in the launch
                one, so a merged picker would silently produce the wrong
                quantization. See abletonRecQuant.ts. */}
            <Show when={step().type === 'ableton.recquant'}>
              <label class="inspector-field">
                <span>Record quantize</span>
                <select
                  value={stepAs(step(), 'ableton.recquant')?.label ?? '16'}
                  onChange={(e) => patch(i, { label: e.currentTarget.value } as Partial<AbletonStep>)}
                >
                  <For each={REC_QUANT_LABELS}>
                    {(l) => <option value={l}>{REC_QUANT_DISPLAY[l]}</option>}
                  </For>
                </select>
              </label>
              {/* The same two quantizers as the Rec Quantize item (DEC-273):
                  OP-Quantize sends the grid to the device and sets Live's own
                  to None (`recQuantSends`). Absent = Live. */}
              <label class="inspector-field">
                <span>Quantizer</span>
                <select
                  value={stepAs(step(), 'ableton.recquant')?.via === 'op-quantize' ? 'op-quantize' : 'live'}
                  onChange={(e) => patch(i, { via: e.currentTarget.value === 'op-quantize' ? 'op-quantize' : undefined } as Partial<AbletonStep>)}
                  title="Live: Live's own record quantize. OP-Quantize: the OP-Quantize device on each track, with Live's own set to None."
                >
                  <option value="live">Live</option>
                  <option value="op-quantize">OP-Quantize</option>
                </select>
              </label>
            </Show>

            <Show when={step().type === 'ableton.launchquant'}>
              <label class="inspector-field">
                <span>Launch quantize</span>
                <select
                  value={stepAs(step(), 'ableton.launchquant')?.label ?? '1bar'}
                  onChange={(e) => patch(i, { label: e.currentTarget.value } as Partial<AbletonStep>)}
                >
                  <For each={LAUNCH_QUANT_LABELS}>
                    {(l) => <option value={l}>{LAUNCH_QUANT_DISPLAY[l]}</option>}
                  </For>
                </select>
              </label>
            </Show>

            <Show when={step().type === 'ableton.metronome'}>
              <label class="inspector-field">
                <span>Metronome</span>
                <select
                  value={stepAs(step(), 'ableton.metronome')?.mode ?? 'toggle'}
                  onChange={(e) => patch(i, { mode: e.currentTarget.value } as Partial<AbletonStep>)}
                >
                  <option value="toggle">Toggle</option>
                  <option value="on">On</option>
                  <option value="off">Off</option>
                </select>
              </label>
            </Show>

            {/* No fields - the device takes no arguments. An empty row would
                read as broken, so say what the step does and, crucially,
                WHICH MACHINE it hits: every other step on this button goes
                to the master, and this one does not. */}
            <Show when={step().type === 'ableton.resync'}>
              <InfoHint title="Resync slave" label="What this does" short={'Set "Sends to" to the SLAVE computer.'}>
                <p>Snaps the slave Ableton's playhead to the master's position, now.</p>
                <p>Seeks only - it can never start or stop either transport, so it is safe
                to hit mid-song.</p>
                <p>It fires on whichever computer "Sends to" names, and on the master there
                is nothing listening - so aim it at the slave.</p>
              </InfoHint>
            </Show>

            <Show when={step().type === 'ableton.endsessionrecord'}>
              <InfoHint title="End Session Record" label="What this does" short="Turns Live's Session Record off.">
                <p>Turns Live's Session Record button off, which ends the take. It only ever turns it
                off, so a double tap or a retried send can never start a recording.</p>
                <p>It needs OP-BridgeSync v3.26-rc4 or later in your set; with an older one the tap
                reaches nothing.</p>
              </InfoHint>
            </Show>

            <Show when={step().type === 'opmotion.crossfade'}>
              {(() => {
                const xf = () => stepAs(step(), 'opmotion.crossfade');
                return (
                  <>
                    <label class="inspector-field">
                      <span>Crossfader</span>
                      <select
                        data-crossfade-action
                        value={xf()?.action ?? 'flick'}
                        onChange={(e) => {
                          const action = e.currentTarget.value as CrossfadeAction;
                          patch(i, (action === 'flick'
                            ? { action, bars: undefined, shape: undefined }
                            : { action, bars: xf()?.bars ?? CROSSFADE_DEFAULT_BARS, shape: xf()?.shape ?? 's' }) as Partial<AbletonStep>);
                        }}
                      >
                        <option value="fadeA">Fade to A</option>
                        <option value="fadeC">Fade to the centre (C)</option>
                        <option value="fadeB">Fade to B</option>
                        <option value="flick">Flick to the other side</option>
                      </select>
                    </label>
                    <Show when={xf() && xf()!.action !== 'flick'}>
                      <CrossfadeFadeFields
                        bars={xf()?.bars ?? CROSSFADE_DEFAULT_BARS}
                        shape={xf()?.shape ?? 's'}
                        onBars={(bars) => patch(i, { bars } as Partial<AbletonStep>)}
                        onShape={(shape) => patch(i, { shape } as Partial<AbletonStep>)}
                      />
                    </Show>
                    <InfoHint title="Crossfader" label="What this does" short="Needs OP-Motion on Main.">
                      <p>Moves Live's crossfader through OP-Motion on your Main track. A fade glides to A, the
                      centre (C, both sides in) or B over the bars you set, from wherever it is; a flick goes
                      to the other side of wherever it is, at once.</p>
                      <p>OP-Motion reports nothing back yet, so this button never lights.</p>
                    </InfoHint>
                  </>
                );
              })()}
            </Show>

            <Show when={stepAs(step(), 'opmotion.capture')}>
              {(c) => <MotionCaptureFields step={c()} onPatch={(p) => patch(i, p as Partial<AbletonStep>)} />}
            </Show>

            <Show when={stepAs(step(), 'opmotion.land')}>
              {(l) => <MotionLandFields step={l()} target={l().target} onPatch={(p) => patch(i, p as Partial<AbletonStep>)} />}
            </Show>

            <Show when={step().type === 'ableton.sustainkill'}>
              <InfoHint title="Sustain kill" label="What this does" short={'Set "Sends to" to the computer running Ableton.'}>
                <p>Lets go of the sustain pedal on every track running OP-SustainStop, so any
                note the pedal is holding stops.</p>
                <p>Without a computer under "Sends to" the button refuses to send rather than
                guess.</p>
              </InfoHint>
            </Show>
          </div>
        )}
      </Index>

      <button
        type="button"
        class="ableton-step-add"
        title="Fire more than one thing from this button - 140 BPM and 6/8 and rec quant off is one tap"
        onClick={() => write([...steps(), freshAbletonStep('ableton.tempo', steps()[0]?.target ?? '')])}
      >
        + Add step
      </button>
      <Show when={steps().length === 0}>
        <span class="inspector-hint">No steps - this button does nothing until you add one.</span>
      </Show>
    </div>
  );
}

/**
 * Which Ableton Control an item is, and whether it is the row form (DEC-291).
 * Null for anything that is not an Ableton Control with a single job: a
 * chain has no row form, and the rest are not Ableton Controls.
 */
function abletonFormOf(item: LayoutItem): { choice: AbletonControlChoice; isRow: boolean } | null {
  if (item.kind === 'recQuant') return { choice: ABLETON_CONTROLS.find((c) => c.id === 'recQuant')!, isRow: true };
  if (item.kind === 'abletonRow') return { choice: ABLETON_CONTROLS.find((c) => c.row === item.control)!, isRow: true };
  if (item.kind === 'button' && item.actionType === 'ableton' && (item.abletonSteps ?? []).length === 1) {
    return { choice: controlForStep(item.abletonSteps![0]!), isRow: false };
  }
  return null;
}

/** What points at this item and would stop working once it is the other
 *  form, in words: a row cannot be a timeline step, a MIDI trigger's target
 *  or a Display's button, and a controller bound in OPBridge fires the old
 *  named action. Said BEFORE the switch, never discovered after. */
function abletonSwitchLinks(item: LayoutItem): string[] {
  const out: string[] = [];
  if (item.kind === 'button') {
    for (const t of timelines()) {
      if (t.lanes.some((l) => l.steps.some((s) => s.kind === 'buttonRef' && s.buttonId === item.id))) out.push(`the timeline "${t.name}"`);
    }
    if (item.midiTrigger) out.push('its MIDI trigger');
    const displays = (activeProject()?.pages ?? []).flatMap((p) => p.items)
      .filter((i) => i.kind === 'display' && i.buttonId === item.id).length;
    if (displays > 0) out.push(displays === 1 ? 'a Display showing it' : `${displays} Displays showing it`);
  }
  if (item.controllerBound) out.push('a controller assigned to it');
  return out;
}

/** The step a row becomes as one button: the value Live is on, when the row
 *  has it lit, else the row's first value. */
function rowPick(item: LayoutItem): AbletonStep | null {
  if (item.kind === 'abletonRow') return item.cells.find((c) => stepLit(c) === true) ?? item.cells[0] ?? null;
  if (item.kind === 'recQuant') {
    const via = item.quantizer === 'op-quantize' ? { via: 'op-quantize' as const } : {};
    const steps: AbletonStep[] = item.labels.map((label) => ({ id: ulid(), target: '', type: 'ableton.recquant', label, ...via }));
    return steps.find((s) => stepLit(s) === true) ?? steps[0] ?? null;
  }
  return null;
}

/**
 * "One button | Full row" (DEC-291, operator 2026-10-18: "each ableton
 * control should have the option to have its own full button grid"). The
 * item switches in place: same id, same spot, same colour, computer, guard
 * and share setting, one undo away.
 */
function AbletonLayoutSwitch(props: { item: LayoutItem }) {
  const form = () => abletonFormOf(props.item);
  const [confirm, setConfirm] = createSignal<string[] | null>(null);
  function go(toRow: boolean): void {
    const f = form();
    if (!f || f.isRow === toRow) return;
    const links = abletonSwitchLinks(props.item);
    if (links.length > 0 && confirm() === null) { setConfirm(links); return; }
    setConfirm(null);
    if (toRow) abletonButtonToRow(props.item.id);
    else {
      const pick = rowPick(props.item);
      if (pick) abletonRowToButton(props.item.id, pick);
    }
  }
  // Leaving the item (or it changing under us) drops a pending confirm.
  createEffect(() => { void props.item.id; setConfirm(null); });
  return (
    <Show when={form()}>
      {(f) => (
        <div class="inspector-zone" data-ableton-layout>
          <div class="inspector-pills">
            <span class="inspector-pills-label">{f().choice.label}</span>
            <button type="button" class="inspector-pill" data-layout="button"
              data-selected={f().isRow ? 'false' : 'true'}
              onClick={() => go(false)}
              title={f().isRow ? 'Make this one button carrying the value Live is on (else the first)' : 'One button, one value'}
            >One button</button>
            <Show when={f().choice.row !== null}>
              <button type="button" class="inspector-pill" data-layout="row"
                data-selected={f().isRow ? 'true' : 'false'}
                onClick={() => go(true)}
                title="Every value as its own cell, lit from Live"
              >Full row</button>
            </Show>
          </div>
          <Show when={confirm()}>
            {(links) => (
              <div class="inspector-notice" data-tone="warn" role="status" data-ableton-switch-confirm>
                <span class="inspector-notice-head">Switching to {f().isRow ? 'one button' : 'a full row'} unlinks {links().join(', ')}</span>
                <span class="inspector-notice-body">
                  {f().isRow ? 'The controller fires what the row was.' : 'A row cannot be fired from those.'} Undo brings it back.
                </span>
                <button type="button" class="inspector-notice-btn" onClick={() => go(!f().isRow)}>Switch anyway</button>
                <button type="button" class="inspector-notice-btn" onClick={() => setConfirm(null)}>Keep the button</button>
              </div>
            )}
          </Show>
        </div>
      )}
    </Show>
  );
}

/** A BPM field that commits on Enter or leaving the box, never per
 *  keystroke: the row re-sorts on commit, and a per-keystroke commit would
 *  move the value out from under the box being typed in. */
function TempoField(props: { value: number; onCommit: (bpm: number) => void; title: string }) {
  return (
    <input
      type="number" class="ableton-row-bpm" min={TEMPO_MIN} max={TEMPO_MAX} step="0.01"
      value={props.value}
      title={props.title}
      onChange={(e) => {
        const n = Number(e.currentTarget.value);
        if (!Number.isFinite(n) || n < TEMPO_MIN || n > TEMPO_MAX) { e.currentTarget.value = String(props.value); return; }
        props.onCommit(roundBpm(n));
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
}

/** A crossfade fade's length and shape (2026-10-23): bars as one-tap picks, the five OP-Motion shapes. */
function CrossfadeFadeFields(props: { bars: number; shape: MotionShape; onBars: (bars: number) => void; onShape: (shape: MotionShape) => void }) {
  return (
    <>
      <div class="inspector-pills" data-crossfade-bars>
        <span class="inspector-pills-label">Over</span>
        <For each={CROSSFADE_BARS}>
          {(b) => (
            <button type="button" class="inspector-pill" data-selected={props.bars === b ? 'true' : 'false'}
              onClick={() => props.onBars(b)} title={`Fade over ${barsWords(b)}`}>{b === 1 ? '1 bar' : `${b} bars`}</button>
          )}
        </For>
      </div>
      <label class="inspector-field">
        <span>Shape</span>
        <select data-crossfade-shape value={props.shape} onChange={(e) => props.onShape(e.currentTarget.value as MotionShape)}>
          <For each={CURVE_CHOICES.filter((c) => c.id !== 'hold')}>
            {(c) => <option value={c.id}>{c.label}: {c.hint}</option>}
          </For>
        </select>
      </label>
    </>
  );
}

/**
 * What a full row offers (DEC-291). Values are picked, never typed in tap
 * order: the row always draws in canonical order (`normaliseRowCells`), so
 * the canvas never reshuffles. The last value cannot be removed - delete the
 * item to get rid of it.
 */
function AbletonRowEditor(props: { item: AbletonRowItem }) {
  const cells = (): AbletonStep[] => props.item.cells;
  const write = (next: AbletonStep[]) => setAbletonRowCells(props.item.id, next);
  const has = (key: string) => cells().some((c) => cellKey(c) === key);
  /** Add a value, or take it away (never the last one). */
  const toggle = (s: AbletonStep) => {
    const k = cellKey(s);
    if (has(k)) { if (cells().length > 1) write(cells().filter((c) => cellKey(c) !== k)); }
    else write([...cells(), s]);
  };
  const clock = () => rowClock(cells());
  const setClock = (c: AbletonClockTarget) => write(cells().map((s) => ('clock' in s ? { ...s, clock: c } : s) as AbletonStep));
  const mk = (fields: Record<string, unknown>): AbletonStep =>
    ({ id: ulid(), target: '', type: ROW_STEP_TYPE[props.item.control], ...fields }) as AbletonStep;

  // Time signatures: the common eight as one-tap picks, plus any other the
  // operator typed, all in the row's own order.
  const sigs = (): Array<[number, number]> => {
    const all = new Map<string, [number, number]>();
    for (const [n, d] of COMMON_TIME_SIGS) all.set(`${n}/${d}`, [n, d]);
    for (const c of cells()) if (c.type === 'ableton.timesig') all.set(`${c.numerator}/${c.denominator}`, [c.numerator, c.denominator]);
    return [...all.values()].sort((a, b) => (a[1] - b[1]) || (a[0] - b[0]));
  };
  const [otherN, setOtherN] = createSignal('');
  const [otherD, setOtherD] = createSignal('');
  const otherValid = () => {
    const n = Number(otherN()); const d = Number(otherD());
    return Number.isInteger(n) && n >= 1 && n <= 99 && Number.isInteger(d) && d >= 1 && d <= 32;
  };

  const clockPills = () => (
    <div class="inspector-pills">
      <span class="inspector-pills-label">Clock</span>
      <button type="button" class="inspector-pill" data-selected={clock() === 'ableton' ? 'true' : 'false'}
        onClick={() => setClock('ableton')} title="Set Live's own value through OP-AbletonSync">Ableton</button>
      <button type="button" class="inspector-pill" data-selected={clock() === 'practice' ? 'true' : 'false'}
        onClick={() => setClock('practice')} title="The local practice clock only - no output, works with no laptop">Practice</button>
    </div>
  );

  return (
    <div class="inspector-zone" data-ableton-row-editor={props.item.control}>
      <Switch>
        <Match when={props.item.control === 'tempo'}>
          {clockPills()}
          <div class="inspector-field ableton-row-tempos">
            <span>Presets</span>
            <div class="ableton-row-tempo-list">
              <Index each={cells()}>
                {(c, i) => (
                  <span class="ableton-row-tempo">
                    <TempoField
                      value={c().type === 'ableton.tempo' ? (c() as Extract<AbletonStep, { type: 'ableton.tempo' }>).bpm : 120}
                      title="A tempo preset, 20-999 BPM, up to 2 decimals"
                      onCommit={(bpm) => write(cells().map((s, n) => (n === i ? ({ ...s, bpm } as AbletonStep) : s)))}
                    />
                    <button type="button" class="ableton-step-remove" title="Remove this preset"
                      disabled={cells().length <= 1}
                      onClick={() => write(cells().filter((_, n) => n !== i))}>×</button>
                  </span>
                )}
              </Index>
            </div>
          </div>
          <button type="button" class="ableton-step-add" data-ableton-row-add
            onClick={() => {
              const top = Math.max(...cells().map((c) => (c.type === 'ableton.tempo' ? c.bpm : 0)));
              write([...cells(), mk({ bpm: roundBpm(Math.min(TEMPO_MAX, (Number.isFinite(top) ? top : 120) + 10)), clock: clock() })]);
            }}
          >+ Add a preset</button>
        </Match>

        <Match when={props.item.control === 'timeSig'}>
          {clockPills()}
          <div class="inspector-pills" data-wrap="true">
            <span class="inspector-pills-label">Values</span>
            <For each={sigs()}>
              {([n, d]) => (
                <button type="button" class="inspector-pill"
                  data-selected={has(`sig-${n}-${d}`) ? 'true' : 'false'}
                  onClick={() => toggle(mk({ numerator: n, denominator: d, clock: clock() }))}
                  title={`Time signature ${n}/${d}`}
                >{n}/{d}</button>
              )}
            </For>
          </div>
          <div class="inspector-field">
            <span>Other</span>
            <span class="ableton-sig ableton-row-other">
              <input type="number" min="1" max="99" placeholder="5" value={otherN()} onInput={(e) => setOtherN(e.currentTarget.value)} />
              <span class="ableton-sig-slash">/</span>
              <input type="number" min="1" max="32" placeholder="4" value={otherD()} onInput={(e) => setOtherD(e.currentTarget.value)} />
              <button type="button" class="inspector-pill" disabled={!otherValid()}
                onClick={() => {
                  write([...cells(), mk({ numerator: Number(otherN()), denominator: Number(otherD()), clock: clock() })]);
                  setOtherN(''); setOtherD('');
                }}>Add</button>
            </span>
          </div>
        </Match>

        <Match when={props.item.control === 'transport'}>
          {/* Worded for what each verb DOES on Live, because the words are
              inverted against the practice clock (DEC-182). */}
          <div class="inspector-pills">
            <span class="inspector-pills-label">Values</span>
            <For each={TRANSPORT_ACTIONS}>
              {(action) => (
                <button type="button" class="inspector-pill" data-selected={has(action) ? 'true' : 'false'}
                  onClick={() => toggle(mk({ action }))}
                  title={action === 'play' ? 'Play - restart from the top' : action === 'stop' ? 'Stop - pause in place' : 'Continue - resume from the pause'}
                >{action === 'play' ? 'Play' : action === 'stop' ? 'Stop' : 'Continue'}</button>
              )}
            </For>
          </div>
        </Match>

        {/* OP-Motion's crossfader (2026-10-23): which cells, and the fades' shared length and shape. */}
        <Match when={props.item.control === 'crossfader'}>
          {(() => {
            const fades = () => cells().filter((c): c is Extract<AbletonStep, { type: 'opmotion.crossfade' }> => c.type === 'opmotion.crossfade' && c.action !== 'flick');
            const bars = () => fades()[0]?.bars ?? CROSSFADE_DEFAULT_BARS;
            const shape = () => fades()[0]?.shape ?? 's';
            const setFades = (p: { bars?: number; shape?: MotionShape }) =>
              write(cells().map((c) => (c.type === 'opmotion.crossfade' && c.action !== 'flick' ? ({ ...c, ...p } as AbletonStep) : c)));
            return (
              <>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Values</span>
                  <For each={CROSSFADE_ACTIONS}>
                    {(action) => (
                      <button type="button" class="inspector-pill" data-selected={has(`xf-${action}`) ? 'true' : 'false'}
                        onClick={() => toggle(mk(action === 'flick' ? { action } : { action, bars: bars(), shape: shape() }))}
                        title={action === 'flick' ? 'Flick: to the other side, at once' : `Fade to ${crossfadeWhere(action)} over ${barsWords(bars())}`}
                      >{crossfadeName(action)}</button>
                    )}
                  </For>
                </div>
                <Show when={fades().length > 0}>
                  <CrossfadeFadeFields bars={bars()} shape={shape()} onBars={(b) => setFades({ bars: b })} onShape={(s) => setFades({ shape: s })} />
                </Show>
              </>
            );
          })()}
        </Match>

        <Match when={props.item.control === 'practiceTransport'}>
          <div class="inspector-pills">
            <span class="inspector-pills-label">Values</span>
            <For each={PRACTICE_TRANSPORT_ACTIONS}>
              {(action) => (
                <button type="button" class="inspector-pill" data-selected={has(action) ? 'true' : 'false'}
                  onClick={() => toggle(mk({ action }))}
                  title={action === 'play' ? 'Play the practice clock' : 'Stop - rewinds to the downbeat'}
                >{action === 'play' ? 'Play' : 'Stop'}</button>
              )}
            </For>
          </div>
        </Match>

        {/* ⚠️ LAUNCH quantize: its own vocabulary, never the record one
            (DEC-181) - index 8 is 1/4T here and 1/32 there. */}
        <Match when={props.item.control === 'launchQuant'}>
          <div class="inspector-pills" data-wrap="true">
            <span class="inspector-pills-label">Values</span>
            <For each={LAUNCH_QUANT_LABELS}>
              {(label) => (
                <button type="button" class="inspector-pill" data-selected={has(`lq-${label}`) ? 'true' : 'false'}
                  onClick={() => toggle(mk({ label }))}
                  title={label === '0' ? "None - clips launch the instant they are pressed. A real value, not 'unset'." : `Launch quantize ${LAUNCH_QUANT_DISPLAY[label]}`}
                >{LAUNCH_QUANT_DISPLAY[label]}</button>
              )}
            </For>
          </div>
        </Match>
      </Switch>
      <TipRow label="When it lights" text={props.item.control === 'crossfader'
        ? 'Needs OP-Motion on your Main track. It reports nothing back yet, so these cells never light.'
        : props.item.control === 'practiceTransport' || clock() === 'practice'
        ? 'A cell lights while the practice clock is on its value.'
        : 'A cell lights while Live is on its value; a tap that has not landed shows amber.'} />
    </div>
  );
}

const MIXER_SKINS: { id: MixerSkin; label: string; title: string }[] = [
  { id: 'neon',    label: 'Neon',    title: 'Flat neon glow - the app-native look' },
  { id: 'console', label: 'Console', title: 'Modern dark studio hardware - molded caps, milled channel' },
  { id: 'neve',    label: 'Neve',    title: 'Warm vintage Neve desk - brushed metal, cream labels, red switches' },
  { id: 'ableton', label: 'Ableton', title: 'Ableton Live session-mixer look - flat dark grey, thin fader + dB scale, blue solo, red arm' },
];

/* The four-value COLORS list was retired 2026-07-29 (DEC-127 landing). It
   named STATE tokens, so under the skin system `cyan` and `green` both
   resolved to --sk-line-lit and `magenta` and `amber` both to
   --sk-armed-ink - four options, two outcomes. Replaced by HUE_ORDER, which
   is orthogonal to state. Saved projects are migrated on read by hueOf(). */

const WAVEFORMS: LfoWaveform[] = ['sine', 'saw'];

const WAVES_SCALE_PLACEMENTS: { id: WavesAutotuneScalePlacement; label: string; title: string }[] = [
  { id: 'bottom', label: 'Bottom', title: 'Scale row below the key rows (full width)' },
  { id: 'top',    label: 'Top',    title: 'Scale row above the key rows (full width)' },
  { id: 'left',   label: 'Left',   title: 'Scale column left of the key rows (compact - 3 buttons in 2 rows)' },
  { id: 'right',  label: 'Right',  title: 'Scale column right of the key rows (compact - 3 buttons in 2 rows)' },
];

interface InspectorPanelProps {
  item: LayoutItem;
}

function clampNum(raw: string, max: number): number {
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(max, n));
}

export function InspectorPanel(props: InspectorPanelProps) {
  const [matrixPickerOpen, setMatrixPickerOpen] = createSignal(false);

  type PendingPick = { fromCh: number; fromCc: number; toCh: number; toCc: number };
  const [pendingPick, setPendingPick] = createSignal<PendingPick | null>(null);

  /** Kinds that bind a single (channel, ccNumber) pair on the ITEM, so one
   *  picker result maps straight onto them. ButtonGrid is absent on purpose
   *  - its CC lives per CELL, so its picker is wired in `ButtonGrid.tsx`'s
   *  cell row instead. XY pad is absent because it binds TWO CCs (x and y)
   *  and the picker returns one. The `Matrix…` buttons live inside each
   *  kind's own <Show> block, so nothing here needs to re-check the kind. */
  type MatrixBindableKind = 'button' | 'lfo' | 'fader' | 'radial';

  /** Label shown at the top of the picker so it names what it is about. */
  const matrixPickLabel = () =>
    (props.item as { name?: string }).name?.trim() || props.item.kind.toUpperCase();

  /**
   * Which namespace the picker should browse, and which field a pick writes.
   * Only a Button can be anything other than CC; every other bindable kind
   * (lfo / fader / radial) is CC by construction.
   *
   * This exists because the Send browser's "Open Matrix" was opening a CC
   * grid whatever the message type was, so picking from it on a Note button
   * would have written a ccNumber and left the note untouched - a pick that
   * appears to work and changes nothing.
   */
  const pickSpace = (): 'cc' | 'note' | 'pc' => {
    if (props.item.kind !== 'button') return 'cc';
    const at = (props.item as ButtonItem).actionType ?? 'cc';
    return at === 'noteon' ? 'note' : at === 'pc' ? 'pc' : 'cc';
  };
  /** The item's current value in whichever namespace is being browsed. */
  const pickCurrentValue = (): number => {
    const it = props.item as ButtonItem;
    if (pickSpace() === 'note') return it.note ?? 60;
    if (pickSpace() === 'pc') return it.program ?? 0;
    return (props.item as Extract<LayoutItem, { kind: MatrixBindableKind }>).ccNumber;
  };

  function handleMatrixPick(toCh: number, toCc: number): void {
    // Note / PC pick straight through: the overwrite confirm below is written
    // against a CC binding (its copy names CC numbers), and a fresh note or
    // program has no "looks unbound" tell the way CC 0 / ch 0 does.
    const sp = pickSpace();
    if (sp !== 'cc') {
      editItem(props.item.id, sp === 'note'
        ? { channel: toCh, note: toCc }
        : { channel: toCh, program: toCc });
      claimPickedSquare();
      return;
    }
    const it = () => props.item as Extract<LayoutItem, { kind: MatrixBindableKind }>;
    const fromCh = it().channel;
    const fromCc = it().ccNumber;
    // Re-picking the square it already has still claims it: that is how a
    // control bound before claims existed gets its name into the matrix.
    if (fromCh === toCh && fromCc === toCc) { claimPickedSquare(); return; }
    const looksUnbound = fromCc === 0 && fromCh === 0;
    if (looksUnbound) {
      editItem(props.item.id, { channel: toCh, ccNumber: toCc });
      claimPickedSquare();
      return;
    }
    setPendingPick({ fromCh, fromCc, toCh, toCc });
  }
  function confirmPick(): void {
    const p = pendingPick();
    if (!p) return;
    editItem(props.item.id, { channel: p.toCh, ccNumber: p.toCc });
    claimPickedSquare();
    setPendingPick(null);
  }
  /** A square picked from the matrix is claimed under this control's name
   *  (OPBridge S317): read the item AFTER the edit, from the store, so the
   *  claim lands on the binding that was just written. */
  function claimPickedSquare(): void {
    const now = layoutItems().find((x) => x.id === props.item.id);
    if (now) claimSquareForItem(now);
  }
  function cancelPick(): void { setPendingPick(null); }

  function onClose(): void { selectItem(null); }

  /**
   * THE BACKDROP MUST NOT EAT A CLICK ON ANOTHER ITEM (2026-08-09).
   *
   * The backdrop is `position: fixed; inset: 0` and only hidden above 1400px
   * of viewport, so on the primary device (iPad, 1180 landscape) it covers
   * the entire canvas while the inspector is open. Every press meant for an
   * item landed on it and deselected instead - which is why *"left or right
   * click another item"* did nothing there, while the same gesture on a wide
   * desktop window (no backdrop) swapped the panel exactly as documented.
   * A behaviour that depends on the browser window's width is a bug, not a
   * breakpoint.
   *
   * `elementsFromPoint` (plural) rather than temporarily disabling pointer
   * events on the backdrop: it reads the whole stack at once, so nothing has
   * to be mutated and restored inside an event handler.
   */
  function itemIdUnder(x: number, y: number): string | null {
    if (typeof document === 'undefined' || !document.elementsFromPoint) return null;
    for (const el of document.elementsFromPoint(x, y)) {
      const item = (el as HTMLElement).closest?.('.editable-item') as HTMLElement | null;
      if (item?.dataset.itemId) return item.dataset.itemId;
    }
    return null;
  }
  /**
   * EVERY button, deliberately - including the right one. A right-click over
   * empty canvas has to tear this backdrop down HERE, on pointerdown, because
   * that is what leaves the canvas exposed by the time the browser dispatches
   * the `contextmenu` event a moment later; the canvas's own handler is the
   * only thing that opens the page menu, and it can only fire if the event
   * targets the canvas. Returning early for non-primary buttons instead looks
   * harmless and silently kills the page right-click menu whenever the
   * inspector is open - caught by radial_and_label_render, which right-clicks
   * the canvas to leave edit mode with a Label still selected.
   */
  function onBackdropPointerDown(ev: PointerEvent): void {
    const id = itemIdUnder(ev.clientX, ev.clientY);
    // Pressing the item the panel is ALREADY on still closes, exactly as
    // before. Only a press on a DIFFERENT item changes meaning. Keeping the
    // old behaviour here is not just conservatism: on the primary device that
    // press is how you get the backdrop out of the way in order to drag the
    // item you are editing, and swallowing it would have quietly removed the
    // only route to that.
    if (id && id !== props.item.id) selectItem(id);
    else onClose();
  }
  /** Only reached when the press landed on an ITEM (the empty-canvas case
   *  unmounted this backdrop on pointerdown above). Re-points the inspector
   *  and opens that item's menu, matching what EditableItem does when no
   *  backdrop is in the way. */
  function onBackdropContextMenu(ev: MouseEvent): void {
    const id = itemIdUnder(ev.clientX, ev.clientY);
    if (!id) return;
    ev.preventDefault();
    ev.stopPropagation();
    selectItem(id);
    openItemMenu(id, ev.clientX, ev.clientY);
  }

  return (
    <>
      {/* Backdrop — visible only in the bottom-sheet (narrow viewport)
          variant. CSS media query hides it on wide viewports. Tap to
          dismiss.
          Suppressed in three cases:
            - ButtonGrid edit-cells mode: cell taps need to reach the
              cells to focus their Inspector row.
            - Test mode: user got here via right-click escape hatch;
              any left-pointerdown on the selected item should reach
              the wrapper's arbiter so drag works. The dim also
              conflicts with the "you're still in test mode" vibe.
              Tap-outside-to-deselect goes away in test mode; use Esc
              or click empty canvas.
            (History: the Helix preset picker was a case here - mounted by
              this panel as a SIBLING of the backdrop, so the backdrop ate
              every click in it, caught by `helix_preset_picker_by_name`
              with "<div class=inspector-backdrop> intercepts pointer
              events". Since 2026-09-29 App mounts it and hides this whole
              panel while it is up; see HelixPresetPicker.tsx.)
            - A "click on canvas" button pick is in flight (DisplayItem
              source=button, 2026-08-12): the entire point of pick mode
              is for a canvas tap to reach the Button item underneath,
              and this backdrop would swallow it exactly like the Helix
              picker case above. Caught the identical way - a smoke
              failed with the identical "<div class=inspector-backdrop>
              intercepts pointer events" message.
       */}
      {/* Docked beside the canvas (layout/inspectorDock.ts) the canvas is not
          under the panel, so there is nothing to dim and a click on it has to
          reach the items: picking another one retargets the panel. */}
      <Show when={!(props.item.kind === 'buttongrid' && isEditingCellsFor(props.item.id)) && isEditMode() && !pickButtonForDisplayId() && !inspectorDocked()}>
        <div
          class="inspector-backdrop"
          onPointerDown={onBackdropPointerDown}
          onContextMenu={onBackdropContextMenu}
          aria-hidden="true"
        />
      </Show>
    {/* `data-item-id`: the panel is a non-keyed <Show> child, so the SAME
        DOM node is reused when the selection moves to another item - which
        makes "did the inspector follow the click?" invisible from outside
        without this. Same seam `.editable-item` already carries. */}
    <div
      class="inspector-panel"
      role="dialog"
      aria-label="Edit item"
      data-item-id={props.item.id}
      data-accent={itemAccent(props.item)}
      data-dock={inspectorDocked() ? 'true' : undefined}
      ref={(el) => onCleanup(installInspectorSteppers(el))}
    >
      <div class="inspector-scroll">
        {/* MIDI-ALLOCATION NOTICE (2026-08-09). Operator: *"if a new midi note
            or cc cannot be assigned for whatever reason, most likely no more
            space in channel, prompt user in the inspector and have the item
            pulsate with a red aura so that the user knows to open the
            inspector."* The pulse is the summons; this is the explanation,
            and it is FIRST in the scroll so the answer is on screen the
            moment the panel arrives rather than below a fold.

            It clears itself: any edit to a MIDI field on this item retires
            the flag (`editItem` → `patchTouchesMidi`), so fixing the CC in
            the field a few rows below is what makes the notice and the pulse
            go away. Nothing to dismiss. */}
        <Show when={midiAllocFailure(props.item.id)}>
          {(reason) => (
            <div class="inspector-notice" data-tone="fault" role="status">
              <span class="inspector-notice-head">No free MIDI slot</span>
              <span class="inspector-notice-body">
                {reason()} This copy kept the same number as the item it came
                from, so both fire together. Give it a free number below, or
                move it to another channel.
              </span>
            </div>
          )}
        </Show>

        {/* IDENTITY — kind + Name, promoted above both intent bands
            (2026-08-10). Name is the one field about the ITEM rather than
            about what it does or how it looks, so it belongs to neither band,
            and it is the field an author reaches for first. */}
        {/* Head, two rows (operator, 2026-10-15: "'done' at the top and the
            bottom" and "the name of the item isn't obvious that it's
            editable"): the kind beside a Done, then Name as a real field with
            a pencil. The chip is the item's Colour (it read the retired
            `color` field before, so it never matched the pick), absent when
            there is none. */}
        <div
          class="inspector-identity"
          data-hue={hueOf(props.item as { hue?: PaletteHue; color?: string })}
          style={{ '--insp-chip': HUE_VALUES[hueOf(props.item as { hue?: PaletteHue; color?: string })] }}
        >
          <div class="inspector-identity-top">
          {/* An Ableton Control button IS a button, but reading "BUTTON" on
              a tile whose whole panel is Ableton steps makes the operator
              hunt for the MIDI fields that are deliberately absent. Name it
              after what it does, matching its palette tile. */}
          <span class="inspector-identity-kind">
            {props.item.kind === 'button' && props.item.actionType === 'ableton'
              ? 'Ableton Control'
              : props.item.kind === 'button' && props.item.actionType === 'obs'
                ? 'OBS Scene'
                : props.item.kind === 'button' && props.item.actionType === 'abletonDevice'
                  ? 'Ableton Device'
                  : props.item.kind === 'button' && props.item.actionType === 'runProgram'
                    ? 'Run Program'
                    : props.item.kind === 'button' && props.item.actionType === 'lightPreset'
                      ? 'Light Preset'
                      : props.item.kind === 'button' && props.item.actionType === 'hueScene'
                        ? 'Hue Scene'
                        : kindLabel(props.item.kind)}
          </span>
          <button type="button" class="inspector-done-top" onClick={onClose}>Done</button>
          </div>
          <label class="inspector-name-row">
            <span>Name</span>
            <span class="inspector-name-well" title="Click to rename">
              <NameField item={props.item} />
              <svg class="inspector-name-pencil" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M11.3 2.3a1 1 0 0 1 1.4 0l1 1a1 1 0 0 1 0 1.4L6 12.4 2.8 13.2l.8-3.2z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round" />
              </svg>
            </span>
          </label>
        </div>

        {/* OFFLINE, said once (2026-10-15). Before, an item with no OPBridge
            said so up to three times in three wordings (an OBS Scene: under
            Scene, under Controller and under Sends to), none of them with a
            way to fix it. Only on items that send; the button goes straight
            to the place that fixes it. */}
        <Show when={!SENDS_NOTHING.has(props.item.kind) && !anyConnectedReactive()}>
          <div class="inspector-notice" data-tone="warn" data-offline role="status">
            <span class="inspector-notice-head">
              {bridges().length === 0 ? 'No computer paired yet' : "OPBridge isn't connected"}
            </span>
            <span class="inspector-notice-body">
              {bridges().length === 0
                ? 'Set it up now; it sends once you pair OPBridge.'
                : 'Set it up now; it sends when OPBridge is back.'}
            </span>
            <button type="button" class="inspector-notice-btn" onClick={() => { onClose(); openNavigateAt('connect'); }}>
              {bridges().length === 0 ? 'Pair a computer' : 'Computers'}
            </button>
          </div>
        </Show>

        {/* WHAT IT DOES — the blue band. Behaviour and wiring. */}
        <div class="inspector-band" data-band="function" data-intent="does">
          <span class="inspector-band-label">What it does</span>

        {/* Ableton Control: One button or Full row (DEC-291). First in the
            band because it decides what everything under it means. */}
        <AbletonLayoutSwitch item={props.item} />

        {/* Kind-specific config — Button. */}
        <Show when={props.item.kind === 'button'}>
          {(() => {
            const btn = () => props.item as Extract<LayoutItem, { kind: 'button' }>;
            const actionType = (): ButtonActionType => btn().actionType ?? 'cc';
            const mode = (): 'toggle' | 'momentary' | 'trigger' => btn().mode ?? 'toggle';
            // A Program Change has no OFF, so FireButton collapses pc to
            // trigger regardless of `mode`. Disable the Mode pills rather
            // than letting the author pick a setting that silently does
            // nothing — same honesty rule as the dead-state item copy.
            // Ableton Control is the same shape of thing: setting a tempo has
            // no un-set, so its release is swallowed too.
            const modeDisabled = () => actionType() === 'pc' || actionType() === 'ableton'
              || actionType() === 'obs' || actionType() === 'abletonDevice'
              || actionType() === 'runProgram' || actionType() === 'hueScene';
            // Ableton Control, OBS Scene and Ableton Device all replace the
            // whole MIDI block rather than sitting beside it - none emits
            // MIDI, so a Channel or CC row would be a dead control.
            //
            // ⚠️ Forgetting to add a new non-MIDI action type here renders
            // the entire MIDI block UNDER its bespoke editor. Found by
            // LOOKING at the render on 2026-09-20, not by any assertion:
            // `abletonDevice` shipped its editor with Message / Send /
            // Channel / MIDI Learn still showing beneath it.
            const isBespoke = () => actionType() === 'ableton' || actionType() === 'obs'
              || actionType() === 'abletonDevice' || actionType() === 'runProgram'
              || actionType() === 'lightPreset' || actionType() === 'hueScene';
            // A Light Preset is bespoke (no MIDI block) but, unlike the
            // others, all three modes MEAN something - play/stop, hold to
            // play, one-shot - so it keeps the Mode row, worded for light.
            const isPreset = () => actionType() === 'lightPreset';
            const PRESET_MODE_TITLES: Record<'toggle' | 'momentary' | 'trigger', string> = {
              toggle: 'Tap to play, tap again to stop',
              momentary: 'Plays while you hold it, stops when you let go',
              trigger: 'Each tap plays it from the top; it does not stop it',
            };
            return (
              <div class="inspector-zone">
                {/* Mode is ABSENT for Ableton Control, not disabled.
                    Every Ableton step is a one-shot on the wire - setting a
                    tempo or a quantization has no OFF - so Toggle and
                    Momentary can never apply. A permanently-disabled pill
                    row is a control that poses a question with no answer,
                    the same reason the `pc` value row is absent rather than
                    dimmed.

                    The one Ableton control with a real on/off state is the
                    metronome, and it gets that state from Live's readback
                    rather than from this row - a metronome button lights
                    from `/clock/status`, so it stays honest when the click
                    is changed in Live or from OPBridge's dashboard. Putting
                    a Mode pill here would imply a local latch we
                    deliberately do not use. */}
                <Show when={!isBespoke() || isPreset()}>
                <div class="inspector-pills" data-fit="content">
                  <span class="inspector-pills-label">Mode</span>
                  {BUTTON_MODES.map((m) => (
                    <button
                      type="button"
                      class="inspector-pill"
                      data-selected={(modeDisabled() ? 'trigger' : mode()) === m.id ? 'true' : 'false'}
                      disabled={modeDisabled() && m.id !== 'trigger'}
                      onClick={() => editItem(props.item.id, { mode: m.id })}
                      title={modeDisabled()
                        ? 'A Program Change is a one-shot - PC buttons always trigger'
                        : isPreset() ? PRESET_MODE_TITLES[m.id] : m.title}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                </Show>
                {/* ABLETON CONTROL — replaces the whole MIDI block below,
                    because this button emits no MIDI at all. Showing a
                    Channel or CC field here would be a dead control, the
                    same dishonesty the `pc` value row avoids by being absent
                    rather than dimmed. */}
                <Show when={actionType() === 'ableton'}>
                  <AbletonStepsEditor item={btn()} />
                </Show>
                <Show when={actionType() === 'obs'}>
                  <ObsSceneEditor item={btn()} />
                </Show>
                <Show when={actionType() === 'abletonDevice'}>
                  <AbletonDeviceEditor item={btn()} />
                </Show>
                <Show when={actionType() === 'runProgram'}>
                  <RunProgramEditor item={btn()} />
                </Show>
                <Show when={isPreset()}>
                  <LightPresetEditor item={btn()} />
                </Show>
                <Show when={actionType() === 'hueScene'}>
                  <HueSceneEditor item={btn()} />
                </Show>

                <Show when={!isBespoke()}>
                {/* The MESSAGE hinge, directly under Mode: it decides what
                    Send and the value row below it mean, so it sits above
                    the rows it governs (2026-08-10 redesign). */}
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Message</span>
                  {BUTTON_ACTION_TYPES.map((t) => (
                    <button
                      type="button"
                      class="inspector-pill"
                      data-selected={actionType() === t.id ? 'true' : 'false'}
                      onClick={() => {
                        // Carry the CURRENT number across the switch (cc 64
                        // -> note stays note 64) rather than letting the new
                        // field fall back to its own separate default (note
                        // defaults to 60 when never set) - the number is the
                        // thing the operator is mid-picking, not the message
                        // type, so it shouldn't reset out from under them.
                        const carry = actionType() === 'cc' ? btn().ccNumber
                          : actionType() === 'noteon' ? (btn().note ?? 60)
                          : (btn().program ?? 0);
                        editItem(props.item.id, t.id === 'cc' ? { actionType: 'cc', ccNumber: carry }
                          : t.id === 'noteon' ? { actionType: 'noteon', note: carry }
                          : { actionType: 'pc', program: carry });
                      }}
                      title={t.title}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                {/* ONE Send row for all three message types, replacing three
                    near-identical CC / Note / Prog blocks. The browser beside
                    it is a channel-scoped slice of the Matrix. */}
                <SendBrowser
                  space={actionType() === 'cc' ? 'cc' : actionType() === 'noteon' ? 'note' : 'pc'}
                  channel={btn().channel}
                  selfId={props.item.id}
                  value={actionType() === 'cc'
                    ? btn().ccNumber
                    : actionType() === 'noteon'
                      ? (btn().note ?? 60)
                      : (btn().program ?? 0)}
                  onChange={(n) => editItem(props.item.id,
                    actionType() === 'cc' ? { ccNumber: n }
                      : actionType() === 'noteon' ? { note: n }
                        : { program: n })}
                  onOpenMatrix={() => setMatrixPickerOpen(true)}
                />

                <div class="inspector-rule" />

                {/* Channel — every message type has one, except a pedal-bound
                    Guitar FX Preset button, whose port AND channel resolve
                    live from the pedal (DEC-152, amended 2026-08-04). */}
                <Show
                  when={actionType() === 'pc' && btn().pedalId}
                  fallback={(
                    <label class="inspector-field">
                      <span>Channel</span>
                      <input
                        type="number" min="1" max="16"
                        value={btn().channel + 1}
                        onInput={(e) => editItem(props.item.id, {
                          channel: clampNum(e.currentTarget.value, 16) - 1,
                        })}
                      />
                    </label>
                  )}
                >
                  <PedalField pedalId={btn().pedalId} />
                </Show>
                </Show>

                {/* The value row is CONTEXTUAL, not merely relabelled: a CC
                    carries an on-value, a note carries a velocity, and a
                    Program Change carries neither. The row is absent for pc
                    rather than dimmed - it is inapplicable, not advanced. */}
                <Show when={actionType() === 'cc'}>
                  <label class="inspector-field">
                    <span>On value</span>
                    <input
                      type="number" min="0" max="127"
                      value={btn().onValue}
                      onInput={(e) => editItem(props.item.id, {
                        onValue: clampNum(e.currentTarget.value, 127),
                      })}
                    />
                  </label>
                  {/* Off value: what a release / un-toggle sends. Defaults to
                      0, which is what the hardcoded constant always sent, so
                      existing buttons are unchanged. Only meaningful for cc -
                      a note's off is a real note-off, not an author's choice. */}
                  <label class="inspector-field">
                    <span>Off value</span>
                    <input
                      type="number" min="0" max="127"
                      value={btn().offValue ?? 0}
                      onInput={(e) => editItem(props.item.id, {
                        offValue: clampNum(e.currentTarget.value, 127),
                      })}
                    />
                  </label>
                </Show>
                <Show when={actionType() === 'noteon'}>
                  <label class="inspector-field">
                    <span>Velocity</span>
                    <input
                      type="number" min="1" max="127"
                      value={btn().velocity ?? 127}
                      onInput={(e) => editItem(props.item.id, {
                        velocity: Math.max(1, clampNum(e.currentTarget.value, 127)),
                      })}
                    />
                  </label>
                </Show>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config — LFO. */}
        <Show when={props.item.kind === 'lfo'}>
          <div class="inspector-zone">
            {/* One way to choose a CC on every kind (2026-10-15): the Button's
                browse list, with the Matrix a click inside it; was a CC field and a
                separate full-width "Matrix..." button. */}
            <SendBrowser
              space="cc"
              label="CC"
              channel={(props.item as Extract<LayoutItem, { kind: 'lfo' }>).channel}
              selfId={props.item.id}
              value={(props.item as Extract<LayoutItem, { kind: 'lfo' }>).ccNumber}
              onChange={(n) => editItem(props.item.id, { ccNumber: n })}
              onOpenMatrix={() => setMatrixPickerOpen(true)}
            />
            <label class="inspector-field">
              <span>Channel</span>
              <input
                type="number" min="1" max="16"
                value={(props.item as Extract<LayoutItem, { kind: 'lfo' }>).channel + 1}
                onInput={(e) => editItem(props.item.id, {
                  channel: clampNum(e.currentTarget.value, 16) - 1,
                })}
              />
            </label>
          </div>
        </Show>

        {/* Kind-specific config — Stream Deck. The action list is DELIVERED
            BY OPBRIDGE (`GET /streamdeck/actions` → Elgato MCP Deck), never
            typed: actions are addressed by a UUID per instance, and only the
            keys the operator put in their Stream Deck "MCP Actions" profile
            are reachable. So this is a picker over a fetched catalogue, and
            an unavailable catalogue says WHY rather than rendering an empty
            list that reads as "you have no actions". */}
        <Show when={props.item.kind === 'streamdeck'}>
          {(() => {
            const sd = () => props.item as Extract<LayoutItem, { kind: 'streamdeck' }>;
            // Fetch on first render of the block — i.e. when the operator
            // selects a Stream Deck item, which is exactly when the list is
            // wanted. Not polled: the catalogue only changes when they edit
            // the deck profile.
            ensureStreamDeckActions();
            return (
              <div class="inspector-zone">
                <div class="sd-picker-head">
                  <span class="inspector-pills-label">Action</span>
                  <button
                    type="button"
                    class="inspector-secondary sd-picker-refresh"
                    onClick={() => { void refreshStreamDeckActions(); }}
                    title="Re-read the action list from OPBridge"
                  >
                    {streamDeckStatus() === 'loading' ? 'Loading…' : 'Refresh'}
                  </button>
                </div>

                <Show when={streamDeckStatus() === 'ready' && streamDeckActions().length > 0}>
                  <div class="sd-picker-list">
                    <For each={streamDeckActions()}>
                      {(a) => (
                        <button
                          type="button"
                          class="sd-picker-row"
                          data-selected={sd().actionId === a.id ? 'true' : 'false'}
                          onClick={() => editItem(props.item.id, {
                            actionId: a.id,
                            // Cache the title so the face still reads right
                            // when OPBridge is down and the list can't load.
                            actionTitle: streamDeckActionLabel(a),
                          })}
                          title={a.pluginId ?? a.id}
                        >
                          <span class="sd-picker-row-title">{streamDeckActionLabel(a)}</span>
                          <Show when={streamDeckActionSubtitle(a)}>
                            <span class="sd-picker-row-sub">{streamDeckActionSubtitle(a)}</span>
                          </Show>
                        </button>
                      )}
                    </For>
                  </div>
                </Show>

                <Show when={streamDeckStatus() === 'ready' && streamDeckActions().length === 0}>
                  <p class="sd-picker-note">
                    OPBridge reached Stream Deck, but no actions are exposed. Add the keys you
                    want to reach to the <strong>MCP Actions</strong> profile in Stream Deck.
                  </p>
                </Show>

                {/* Only when ANOTHER OPBridge is up: with none, the notice under
                    the head already says so (2026-10-15). */}
                <Show when={streamDeckStatus() === 'no-bridge' && anyConnectedReactive()}>
                  <p class="sd-picker-note" data-tone="warn">
                    This item's OPBridge isn't connected, so its Stream Deck actions can't load.
                  </p>
                </Show>

                <Show when={streamDeckStatus() === 'unavailable' || streamDeckStatus() === 'error'}>
                  <p class="sd-picker-note" data-tone="warn">{streamDeckError()}</p>
                </Show>

                {/* Assigned-but-missing is a real state: the operator removed
                    the key from the MCP Actions profile after picking it. The
                    button would fail at fire time, so say it at edit time. */}
                <Show when={
                  sd().actionId
                  && streamDeckStatus() === 'ready'
                  && !findStreamDeckAction(sd().actionId)
                }>
                  <p class="sd-picker-note" data-tone="warn">
                    The assigned action ({sd().actionTitle ?? sd().actionId?.slice(0, 8)}) is no
                    longer in the MCP Actions profile. Pick another.
                  </p>
                </Show>

                <label class="inspector-field" title="The text the face shows for the action. Leave it empty to use the action's own name.">
                  <span>Shown as</span>
                  <input
                    type="text"
                    value={sd().label ?? ''}
                    placeholder={sd().actionTitle ?? 'Action name'}
                    onInput={(e) => editItem(props.item.id, { label: e.currentTarget.value })}
                  />
                </label>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config — StatusLed. Read-only indicator: pick
            the source event kind (CC or Note) + its number + channel +
            threshold that lights the lamp. Threshold defaults to 1 so
            any non-zero echo turns the LED on — matches how DAWs
            typically send toggle-CCs. */}
        <Show when={props.item.kind === 'statusLed'}>
          {(() => {
            const led = () => props.item as Extract<LayoutItem, { kind: 'statusLed' }>;
            const src = (): 'midi.cc' | 'midi.note' => led().sourceType ?? 'midi.cc';
            return (
              <div class="inspector-zone">
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Listens on</span>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={src() === 'midi.cc' ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { sourceType: 'midi.cc' })}
                    title="Light up on Control Change events"
                  >
                    CC
                  </button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={src() === 'midi.note' ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { sourceType: 'midi.note' })}
                    title="Light up on Note On events (Note Off / vel=0 turns off)"
                  >
                    Note
                  </button>
                </div>
                <Show when={src() === 'midi.cc'}>
                  <label class="inspector-field">
                    <span>CC</span>
                    <input
                      type="number" min="0" max="127"
                      value={led().ccNumber ?? 0}
                      onInput={(e) => editItem(props.item.id, {
                        ccNumber: clampNum(e.currentTarget.value, 127),
                      })}
                    />
                  </label>
                </Show>
                <Show when={src() === 'midi.note'}>
                  <label class="inspector-field">
                    <span>Note</span>
                    <input
                      type="number" min="0" max="127"
                      value={led().note ?? 60}
                      onInput={(e) => editItem(props.item.id, {
                        note: clampNum(e.currentTarget.value, 127),
                      })}
                    />
                  </label>
                </Show>
                <label class="inspector-field">
                  <span>Channel</span>
                  <input
                    type="number" min="1" max="16"
                    value={led().channel + 1}
                    onInput={(e) => editItem(props.item.id, {
                      channel: clampNum(e.currentTarget.value, 16) - 1,
                    })}
                  />
                </label>
                <label class="inspector-field">
                  <span>Threshold</span>
                  <input
                    type="number" min="1" max="127"
                    value={led().threshold ?? 1}
                    onInput={(e) => editItem(props.item.id, {
                      threshold: Math.max(1, clampNum(e.currentTarget.value, 127)),
                    })}
                    title="Incoming value ≥ threshold turns the LED on"
                  />
                </label>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config - Display. One bound source per item
            (DEC pattern shared with StatusLed/Mixer/WavesAutotune), plus the
            skin-matched/LCD style toggle mirroring Mixer's per-item skin
            override. */}
        <Show when={props.item.kind === 'display'}>
          {(() => {
            const dsp = () => props.item as Extract<LayoutItem, { kind: 'display' }>;
            const [sourceOpen, setSourceOpen] = createSignal(false);
            const currentSource = () => DISPLAY_SOURCES.find((s) => s.id === dsp().source) ?? DISPLAY_SOURCES[0]!;
            return (
              <div class="inspector-zone">
                {/* Animated dropdown, same grid-template-rows 0fr->1fr expand
                    technique as SendBrowser's Matrix browser (the "matrix
                    dropdown"). Not pills: an earlier 8-pill segmented-control
                    row squished each pill's clickable box narrower than its
                    own overflowing text, so a tap near the visible label
                    could land on the WRONG neighbor - a real hit-testing bug,
                    not a test artifact (found via a Playwright coordinate
                    click landing on a different pill than a raw DOM .click()
                    on the same element). A dropdown has no such failure mode
                    at any option count. */}
                <label class="inspector-field source-dropdown-field">
                  <span>Source</span>
                  <div class="source-dropdown">
                    <button
                      type="button"
                      class="source-dropdown-btn"
                      data-open={sourceOpen() ? 'true' : undefined}
                      aria-expanded={sourceOpen() ? 'true' : 'false'}
                      onClick={() => setSourceOpen((v) => !v)}
                      title={currentSource().title}
                    >
                      <span class="source-dropdown-label">{currentSource().label}</span>
                      <span class="source-dropdown-chevron" aria-hidden="true">▾</span>
                    </button>
                    <div class="source-dropdown-wrap" data-open={sourceOpen() ? 'true' : undefined}>
                      <div class="source-dropdown-inner">
                        <div class="source-dropdown-list">
                          <For each={DISPLAY_SOURCE_GROUPS}>
                            {(grp) => (
                              <>
                                <div class="source-dropdown-group">{grp.group}</div>
                                <For each={grp.items}>
                                  {(opt) => (
                                    <button
                                      type="button"
                                      class="source-dropdown-row"
                                      data-cur={dsp().source === opt.id ? 'true' : undefined}
                                      title={opt.title}
                                      onClick={() => {
                                        editItem(dsp().id, { source: opt.id });
                                        setSourceOpen(false);
                                      }}
                                    >
                                      {opt.label}
                                    </button>
                                  )}
                                </For>
                              </>
                            )}
                          </For>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>
                <Show when={dsp().source === 'lfo'}>
                  <label class="inspector-field">
                    <span>LFO</span>
                    <select
                      value={dsp().lfoId ?? ''}
                      onChange={(e) => editItem(dsp().id, { lfoId: e.currentTarget.value })}
                    >
                      <option value="">Pick an LFO</option>
                      <For each={layoutItems().filter((i): i is Extract<LayoutItem, { kind: 'lfo' }> => i.kind === 'lfo')}>
                        {(i) => <option value={i.id}>{i.name || i.waveform}</option>}
                      </For>
                    </select>
                  </label>
                </Show>
                <Show when={dsp().source === 'timeline'}>
                  <label class="inspector-field">
                    <span>Launcher</span>
                    <select
                      value={dsp().timelineLauncherId ?? ''}
                      onChange={(e) => editItem(dsp().id, { timelineLauncherId: e.currentTarget.value })}
                    >
                      <option value="">Pick a Timeline Launcher</option>
                      <For each={layoutItems().filter((i): i is Extract<LayoutItem, { kind: 'timelineLauncher' }> => i.kind === 'timelineLauncher')}>
                        {(i) => {
                          const t = () => timelines().find((x) => x.id === i.timelineId);
                          return <option value={i.id}>{i.name || t()?.name || 'timeline launcher'}</option>;
                        }}
                      </For>
                    </select>
                  </label>
                </Show>
                <Show when={dsp().source === 'nowplaying'}>
                  {/* DEC-315: the countdown before a section changes; 4 beats unless set. */}
                  <div class="inspector-pills" data-testid="display-countdown">
                    <span class="inspector-pills-label">Countdown</span>
                    <For each={[4, 8, 16] as const}>
                      {(n) => (
                        <button type="button" class="inspector-pill"
                          data-selected={(dsp().countdownBeats ?? 4) === n ? 'true' : 'false'}
                          onClick={() => editItem(dsp().id, { countdownBeats: n })}
                        >
                          {n} beats
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
                <Show when={dsp().source === 'helix'}>
                  <PedalField pedalId={dsp().pedalId} />
                  <div class="inspector-pills">
                    <span class="inspector-pills-label">Pedal</span>
                    <For each={activePedals()}>
                      {(pd) => (
                        <button type="button" class="inspector-pill"
                          data-selected={dsp().pedalId === pd.id ? 'true' : 'false'}
                          onClick={() => editItem(dsp().id, { pedalId: pd.id })}
                        >
                          {pd.name}
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
                <Show when={dsp().source === 'button'}>
                  <label class="inspector-field">
                    <span>Button</span>
                    <select
                      value={dsp().buttonId ?? ''}
                      onChange={(e) => editItem(dsp().id, { buttonId: e.currentTarget.value })}
                    >
                      <option value="">Pick a Button</option>
                      <For each={layoutItems().filter((i): i is Extract<LayoutItem, { kind: 'button' }> => i.kind === 'button')}>
                        {(i) => <option value={i.id}>{i.name || `cc ${i.ccNumber}`}</option>}
                      </For>
                    </select>
                  </label>
                  {/* "Click on canvas" (2026-08-12) - alternative to the
                      <select> above for a rig with many similarly-named
                      buttons, where picking by eye on the canvas is faster
                      than reading a dropdown list. Dims every non-Button
                      item + pulses the pickable ones (app.css
                      `[data-pick-mode]`, skin-components.css
                      `[data-pickable]`) until a button is clicked or the
                      pick is cancelled (Esc / click elsewhere / leave edit
                      mode - see layout/store.ts). */}
                  <Show
                    when={pickButtonForDisplayId() === dsp().id}
                    fallback={
                      <button type="button" class="inspector-pill"
                        onClick={() => startPickButtonForDisplay(dsp().id)}
                      >
                        Click on canvas…
                      </button>
                    }
                  >
                    <div class="inspector-pills">
                      <span class="inspector-hint">Click a Button on the canvas - Esc to cancel</span>
                      <button type="button" class="inspector-pill" onClick={() => cancelPickButton()}>
                        Cancel
                      </button>
                    </div>
                  </Show>
                </Show>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config — Fader. CC + channel + orientation +
            centered mode. Value is not exposed in the inspector; drags
            update it via the store. */}
        {/* Radial (DEC-116) — CC + channel + the double-tap reset target.
            No orientation pill: a knob has no long axis, and no bipolar
            "Rest" pill either, since the mixer's pan slider already owns
            bipolar and DEC-116 keeps v1 absolute-only. */}
        {/* Preset change (DEC-148). The Inspector speaks the DEVICE's
            vocabulary — Setlist / Preset / Snapshot — not MIDI's. The author
            should never need to know a Program Change exists; that is the
            whole reason this is its own item kind. Channel and port are
            NOT authored here at all (2026-08-04, DEC-152 amended) — they
            live on the bound `GuitarPedal` in Connected Devices and
            resolve live, so `PedalField` closes the zone with a summary
            + a shortcut instead of editable fields. */}
        <Show when={props.item.kind === 'presetChange'}>
          {(() => {
            const pc = () => props.item as Extract<LayoutItem, { kind: 'presetChange' }>;
            return (
              <div class="inspector-zone">
                <label class="inspector-field">
                  <span>Setlist</span>
                  <input
                    type="number" min="1" max="8"
                    value={pc().setlist + 1}
                    onInput={(e) => editItem(props.item.id, {
                      setlist: clampNum(e.currentTarget.value, 8) - 1,
                    })}
                  />
                </label>
                <label class="inspector-field">
                  <span>Preset</span>
                  <input
                    type="number" min="1" max="128"
                    value={pc().program + 1}
                    onInput={(e) => editItem(props.item.id, {
                      program: clampNum(e.currentTarget.value, 128) - 1,
                    })}
                  />
                </label>
                {/* The unit prints presets as bank+letter (02B), so echo that
                    back — a bare 1-128 index is not what the player reads off
                    the pedal. Once a library has been read, the NAME is what
                    the player actually recognises, so it leads and the slot
                    notation follows it. */}
                <span class="inspector-hint">
                  {(() => {
                    const nm = helixPresetName(pc().setlist, pc().program) ?? pc().presetName;
                    return nm
                      ? `${helixSetlistName(pc().setlist)} · ${nm} (${helixSlotLabel(pc().program)})`
                      : `= ${helixSlotLabel(pc().program)}`;
                  })()}
                </span>
                {/* The novice path: pick "Essex A15" instead of working out
                    that it is setlist 1, preset 6. Deliberately a button and
                    not an auto-opening picker — reading the library costs the
                    pedal (DEC-150), so nothing here fetches without a press. */}
                <button
                  type="button"
                  class="inspector-pill"
                  onClick={() => openHelixPicker(props.item.id, pc().setlist)}
                  title="Choose a preset by name, read from the pedal"
                >
                  Pick by name…
                </button>
                <label class="inspector-field">
                  <span>Snapshot</span>
                  <input
                    type="number" min="0" max="8"
                    value={typeof pc().snapshot === 'number' ? (pc().snapshot as number) + 1 : 0}
                    onInput={(e) => {
                      // 0 means "don't touch the snapshot" — recalling a preset
                      // must not silently move a snapshot set by hand.
                      const n = clampNum(e.currentTarget.value, 8);
                      editItem(props.item.id, { snapshot: n === 0 ? undefined : n - 1 });
                    }}
                  />
                </label>
                <PedalField pedalId={pc().pedalId} />
                <MidiTriggerLearn
                  itemId={props.item.id}
                  binding={pc().midiTrigger}
                  path="a"
                  onSave={(binding) => editItem(props.item.id, { midiTrigger: binding })}
                  onClear={() => editItem(props.item.id, { midiTrigger: undefined })}
                />
              </div>
            );
          })()}
        </Show>

        {/* Snapshot (DEC-151). Pills, not a number field: there are exactly
            ten choices and eight of them are the pedal's own footswitch row,
            so a row of pills IS the pedal — the author picks the switch they
            can see under their foot. Next/Prev sit at the end because they
            are the relative moves, and they are the whole reason this is not
            a 1-8 spinner. Channel and port are not authored here — see
            `PedalField`'s doc comment, same as presetChange. */}
        <Show when={props.item.kind === 'snapshotChange'}>
          {(() => {
            const sn = () => props.item as Extract<LayoutItem, { kind: 'snapshotChange' }>;
            const isSel = (t: SnapshotTarget): boolean => sn().snapshot === t;
            return (
              <div class="inspector-zone">
                {/* A fixed grid like the pedal (2026-10-15): 1-4, 5-8, then Prev
                    and Next together. As wrapped chips "Prev" landed alone on
                    a line of its own; as ten equal segments the words clipped
                    (2026-10-10). */}
                <div class="inspector-pills inspector-pills-grid" data-cols="4">
                  <span class="inspector-pills-label">Snapshot</span>
                  <div class="inspector-pills-cells">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <button
                      type="button"
                      class="inspector-pill"
                      data-selected={isSel(n) ? 'true' : 'false'}
                      onClick={() => editItem(props.item.id, { snapshot: n })}
                      title={`Select snapshot ${n + 1} (CC69 value ${n})`}
                    >
                      {n + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={isSel('prev') ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { snapshot: 'prev' })}
                    title="Step back to the previous snapshot (CC69 value 9)"
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={isSel('next') ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { snapshot: 'next' })}
                    title="Advance to the next snapshot, whichever one is current (CC69 value 8)"
                  >
                    Next
                  </button>
                  </div>
                </div>
                <PedalField pedalId={sn().pedalId} />
                <MidiTriggerLearn
                  itemId={props.item.id}
                  binding={sn().midiTrigger}
                  path="a"
                  onSave={(binding) => editItem(props.item.id, { midiTrigger: binding })}
                  onClear={() => editItem(props.item.id, { midiTrigger: undefined })}
                />
              </div>
            );
          })()}
        </Show>

        {/* Rig State (PLAN.md QUEUED "Rig States"). Bank first, then a
            look FROM that bank - the look select is empty/disabled until
            a bank is picked, same cascading shape as Display's LFO/
            Timeline-Launcher pickers above. Picking a different bank
            clears the (now meaningless) look selection rather than
            leaving a lookId that points at the wrong bank's look array. */}
        <Show when={props.item.kind === 'rigStateButton'}>
          {(() => {
            const rsb = () => props.item as Extract<LayoutItem, { kind: 'rigStateButton' }>;
            const bank = () => rigStateBanks().find((b) => b.id === rsb().bankId);
            return (
              <div class="inspector-zone">
                <label class="inspector-field">
                  <span>Rig State</span>
                  <select
                    value={rsb().bankId ?? ''}
                    onChange={(e) => {
                      const bankId = e.currentTarget.value;
                      if (bankId) setRigStateButtonBank(rsb().id, bankId);
                    }}
                  >
                    <option value="">Pick a Rig State</option>
                    <For each={rigStateBanks()}>
                      {(b) => <option value={b.id}>{b.name}</option>}
                    </For>
                  </select>
                </label>
                <label class="inspector-field">
                  <span>Look</span>
                  <select
                    value={rsb().lookId ?? ''}
                    disabled={!bank()}
                    onChange={(e) => setRigStateButtonLook(rsb().id, e.currentTarget.value || undefined)}
                  >
                    <option value="">Pick a look</option>
                    <For each={bank()?.looks ?? []}>
                      {(l) => <option value={l.id}>{l.name}{l.isHome ? ' (HOME)' : ''}</option>}
                    </For>
                  </select>
                </label>
                <button
                  type="button"
                  class="inspector-pill"
                  onClick={() => openRigStateManager()}
                  title="Create banks and looks, or add targets to one"
                >
                  Manage Rig States…
                </button>
              </div>
            );
          })()}
        </Show>

        {/* ── Lighting (PLAN.md QUEUED "DMX / lighting v1") ───────────────
            Target first in all three, because "which lights" is the question
            the operator is answering when they place the control - the value
            it sends is the easy part. `LightTargetPicker` is shared by all
            three rather than copied, so a change to how targets are chosen
            cannot land on two of them and miss the third. */}
        <Show when={props.item.kind === 'lightColor'}>
          {(() => {
            const lc = () => props.item as Extract<LayoutItem, { kind: 'lightColor' }>;
            return (
              <div class="inspector-zone">
                <LightTargetPicker
                  target={lc().target}
                  onChange={(target) => editItem(lc().id, { target })}
                  onManageGroups={openLightsManager}
                />
                {/* Swatches, not a dropdown of colour NAMES. The control's
                    whole point is that a colour is visible, and a <select>
                    would hide the one attribute worth seeing. */}
                {/* Reuses the item-hue swatch row verbatim (`.inspector-pills`
                    + `.inspector-swatch.sk-swatch` + `data-selected`), so this
                    needs no new CSS and cannot drift from the picker beside
                    it. `data-hue="none"` is the existing struck-through well,
                    which is exactly the right rendering for the Off swatch -
                    pure black would read as a hole. */}
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Colour</span>
                  <For each={STAGE_COLORS}>
                    {(c) => (
                      <button
                        type="button"
                        class="inspector-swatch sk-swatch"
                        data-hue={c.label === 'Off' ? 'none' : undefined}
                        style={{ '--swatch': rgbToHex(c.rgb) }}
                        data-selected={lc().color_label === c.label ? 'true' : 'false'}
                        title={c.label}
                        aria-label={c.label}
                        onClick={() => editItem(lc().id, { color_label: c.label })}
                      />
                    )}
                  </For>
                </div>
                {/* Level is OPT-IN, and the checkbox is the honest shape.
                    OPBridge's /state route leaves an omitted field alone, so
                    "no level" is a real, useful setting - it means this
                    button changes the colour and leaves the operator's
                    brightness exactly where they had it. A plain 0-255 field
                    defaulting to 255 would silently full-brighten a dimmed
                    rig on every colour change. */}
                <label class="inspector-field">
                  <span>Brightness</span>
                  <input
                    type="checkbox"
                    checked={lc().level !== undefined}
                    onChange={(e) => {
                      if (e.currentTarget.checked) editItem(lc().id, { level: 255 });
                      else clearLightLevelField(lc().id);
                    }}
                  />
                </label>
                <Show when={lc().level !== undefined}>
                  <label class="inspector-field">
                    <span>Brightness</span>
                    <input
                      type="number" min="0" max="255"
                      value={lc().level ?? 255}
                      onInput={(e) => editItem(lc().id, { level: clampNum(e.currentTarget.value, 255) })}
                    />
                  </label>
                </Show>
              </div>
            );
          })()}
        </Show>

        <Show when={props.item.kind === 'lightLevel'}>
          {(() => {
            const ll = () => props.item as Extract<LayoutItem, { kind: 'lightLevel' }>;
            return (
              <div class="inspector-zone">
                <LightTargetPicker
                  target={ll().target}
                  onChange={(target) => editItem(ll().id, { target })}
                  onManageGroups={openLightsManager}
                />
                <TipRow label="Brightness only" text="Sends brightness only, so it never changes the colour the lights are on." />
              </div>
            );
          })()}
        </Show>

        <Show when={props.item.kind === 'lightFx'}>
          {(() => {
            const lf = () => props.item as Extract<LayoutItem, { kind: 'lightFx' }>;
            // Built-in macros are per-FIXTURE, so they can only be offered
            // when the target names fixtures that actually declare them. A
            // group spanning a PL60C and a SlimPAR offers only the software
            // effects, because arming "police" on a fixture with no FX mode
            // is a request OPBridge will refuse - DEC-064, never offer what
            // you cannot do.
            const builtIns = () => {
              const targets = resolveTarget(lf().target);
              if (targets.length === 0) return [] as string[];
              const sets = targets.map((f) => new Set(fixtureMode(f)?.effects ?? []));
              const first = sets[0];
              if (!first) return [] as string[];
              return [...first].filter((name) => sets.every((s) => s.has(name))).sort();
            };
            return (
              <div class="inspector-zone">
                <LightTargetPicker
                  target={lf().target}
                  onChange={(target) => editItem(lf().id, { target })}
                  onManageGroups={openLightsManager}
                />
                <label class="inspector-field">
                  <span>Effect</span>
                  <select
                    value={lf().effect}
                    onChange={(e) => editItem(lf().id, { effect: e.currentTarget.value })}
                  >
                    {/* Empty is a real, working choice, not a null option -
                        OPBridge reads it as STOP. Labelled as the action it
                        is rather than as an absence. */}
                    <option value="">Stop effects</option>
                    <Show when={softwareEffectsFree().length > 0}>
                      <optgroup label="Effects">
                        <For each={softwareEffectsFree()}>
                          {(e) => <option value={e.name}>{e.label}</option>}
                        </For>
                      </optgroup>
                    </Show>
                    <Show when={softwareEffectsMusical().length > 0}>
                      <optgroup label="On the beat">
                        <For each={softwareEffectsMusical()}>
                          {(e) => <option value={e.name}>{e.label}</option>}
                        </For>
                      </optgroup>
                    </Show>
                    <Show when={builtIns().length > 0}>
                      <optgroup label="Built into the light">
                        <For each={builtIns()}>
                          {(name) => <option value={name}>{titleCaseSlug(name)}</option>}
                        </For>
                      </optgroup>
                    </Show>
                  </select>
                </label>
                {/* The one thing about this control that will otherwise read
                    as a bug, named at authoring time rather than discovered
                    mid-set: a musical effect is idle with the transport
                    stopped. */}
                <Show when={isMusicalEffect(lf().effect)}>
                  <TipRow label="Needs the clock" text="Follows OPBridge's clock - it stays dark until the transport is running." />
                </Show>
                {/* Colour only where it does something. OPBridge ignores the
                    base colour for effects that define their own hue, so
                    offering a picker there would be a control that silently
                    does nothing. */}
                <Show when={effectTakesColor(lf().effect)}>
                  <div class="inspector-pills">
                    <span class="inspector-pills-label">Base colour</span>
                    <For each={STAGE_COLORS}>
                      {(c) => (
                        <button
                          type="button"
                          class="inspector-swatch sk-swatch"
                          data-hue={c.label === 'Off' ? 'none' : undefined}
                          style={{ '--swatch': rgbToHex(c.rgb) }}
                          data-selected={lf().color_label === c.label ? 'true' : 'false'}
                          title={c.label}
                          aria-label={c.label}
                          onClick={() => editItem(lf().id, { color_label: c.label })}
                        />
                      )}
                    </For>
                  </div>
                </Show>
                <Show when={lf().effect !== ''}>
                  <label class="inspector-field">
                    <span>Brightness</span>
                    <input
                      type="number" min="0" max="255"
                      value={lf().brightness ?? 255}
                      onInput={(e) => editItem(lf().id, { brightness: clampNum(e.currentTarget.value, 255) })}
                    />
                  </label>
                </Show>
              </div>
            );
          })()}
        </Show>

        <Show when={props.item.kind === 'opvisualTrigger'}>
          <OpVisualTriggerFields item={props.item as OpVisualTriggerItem} />
        </Show>
        <Show when={props.item.kind === 'opvisualGrid'}>
          <OpVisualGridFields item={props.item as OpVisualGridItem} />
        </Show>
        <Show when={props.item.kind === 'audioPads'}>
          <AudioPadsFields item={props.item as AudioPadsItem} />
        </Show>

        <Show when={props.item.kind === 'blackout'}>
          <div class="inspector-zone">
            {/* No target field, and the hint explains the absence rather than
                leaving the operator hunting for one. */}
            <InfoHint title="Blackout">
              <p>Kills every light on every connected bridge.</p>
              <p>There is nothing to aim, deliberately - so it cannot be pointed at the wrong
              thing in the one moment you need it.</p>
            </InfoHint>
          </div>
        </Show>

        <Show when={props.item.kind === 'radial'}>
          {(() => {
            const rad = () => props.item as Extract<LayoutItem, { kind: 'radial' }>;
            return (
              <div class="inspector-zone">
                {/* One way to choose a CC on every kind (2026-10-15): the Button's
                    browse list, with the Matrix a click inside it; was a CC field and a
                    separate full-width "Matrix..." button. */}
                <SendBrowser
                  space="cc"
                  label="CC"
                  channel={rad().channel}
                  selfId={props.item.id}
                  value={rad().ccNumber}
                  onChange={(n) => editItem(props.item.id, { ccNumber: n })}
                  onOpenMatrix={() => setMatrixPickerOpen(true)}
                />
                <label class="inspector-field">
                  <span>Channel</span>
                  <input
                    type="number" min="1" max="16"
                    value={rad().channel + 1}
                    onInput={(e) => editItem(props.item.id, {
                      channel: clampNum(e.currentTarget.value, 16) - 1,
                    })}
                  />
                </label>
                <label class="inspector-field">
                  <span>Reset to</span>
                  <input
                    type="number" min="0" max="127"
                    value={rad().defaultValue ?? 0}
                    title="Value a double-tap snaps back to"
                    onInput={(e) => editItem(props.item.id, {
                      defaultValue: clampNum(e.currentTarget.value, 127),
                    })}
                  />
                </label>
                <label class="inspector-field">
                  <span>Steps</span>
                  <input
                    type="number" min="1" max="127"
                    value={rad().steps ?? ''}
                    placeholder="Smooth"
                    title="Discrete detent positions. Blank = continuous. Minimum 1."
                    onInput={(e) => {
                      const raw = e.currentTarget.value.trim();
                      if (raw === '') { editItem(props.item.id, { steps: 0 }); return; }
                      const n = Math.max(1, Math.min(127, Number.parseInt(raw, 10) || 1));
                      editItem(props.item.id, { steps: n });
                    }}
                  />
                </label>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Sweep</span>
                  <For each={[240, 270, 300, 330]}>
                    {(deg) => (
                      <button
                        type="button"
                        class="inspector-pill"
                        data-selected={(rad().sweepDeg ?? 270) === deg ? 'true' : 'false'}
                        onClick={() => editItem(props.item.id, { sweepDeg: deg })}
                      >{deg}°</button>
                    )}
                  </For>
                </div>
              </div>
            );
          })()}
        </Show>

        {/* Label (DEC-117) — text + alignment + styling only. No CC, no
            channel, no action: it fires nothing by design. */}
        <Show when={props.item.kind === 'label'}>
          {(() => {
            const lbl = () => props.item as Extract<LayoutItem, { kind: 'label' }>;
            return (
              <div class="inspector-zone">
                <label class="inspector-field inspector-field-wide">
                  <span>Text</span>
                  <textarea
                    class="inspector-textarea"
                    rows="2"
                    value={lbl().text}
                    onInput={(e) => editItem(props.item.id, { text: e.currentTarget.value })}
                  />
                </label>
              </div>
            );
          })()}
        </Show>

        <Show when={props.item.kind === 'fader'}>
          {(() => {
            const fdr = () => props.item as Extract<LayoutItem, { kind: 'fader' }>;
            return (
              <div class="inspector-zone">
                {/* One way to choose a CC on every kind (2026-10-15): the Button's
                    browse list, with the Matrix a click inside it; was a CC field and a
                    separate full-width "Matrix..." button. */}
                <SendBrowser
                  space="cc"
                  label="CC"
                  channel={fdr().channel}
                  selfId={props.item.id}
                  value={fdr().ccNumber}
                  onChange={(n) => editItem(props.item.id, { ccNumber: n })}
                  onOpenMatrix={() => setMatrixPickerOpen(true)}
                />
                <label class="inspector-field">
                  <span>Channel</span>
                  <input
                    type="number" min="1" max="16"
                    value={fdr().channel + 1}
                    onInput={(e) => editItem(props.item.id, {
                      channel: clampNum(e.currentTarget.value, 16) - 1,
                    })}
                  />
                </label>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Rests at</span>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={!fdr().centered ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { centered: false })}
                    title="Resting position at bottom (0)"
                  >Bottom</button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={!!fdr().centered ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { centered: true })}
                    title="Resting position at center (64)"
                  >Center</button>
                </div>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config — XY Pad. Two independent (CC, channel)
            pairs, plus return-to-center + yInverted toggles. */}
        <Show when={props.item.kind === 'xypad'}>
          {(() => {
            const xy = () => props.item as Extract<LayoutItem, { kind: 'xypad' }>;
            return (
              <div class="inspector-zone">
                <div class="inspector-field-row">
                  <SendBrowser
                    space="cc"
                    label="X CC"
                    channel={xy().xChannel}
                    selfId={props.item.id}
                    value={xy().xCcNumber}
                    onChange={(n) => editItem(props.item.id, { xCcNumber: n })}
                  />
                  <label class="inspector-field">
                    <span>X channel</span>
                    <input
                      type="number" min="1" max="16"
                      value={xy().xChannel + 1}
                      onInput={(e) => editItem(props.item.id, {
                        xChannel: clampNum(e.currentTarget.value, 16) - 1,
                      })}
                    />
                  </label>
                </div>
                <div class="inspector-field-row">
                  <SendBrowser
                    space="cc"
                    label="Y CC"
                    channel={xy().yChannel}
                    selfId={props.item.id}
                    value={xy().yCcNumber}
                    onChange={(n) => editItem(props.item.id, { yCcNumber: n })}
                  />
                  <label class="inspector-field">
                    <span>Y channel</span>
                    <input
                      type="number" min="1" max="16"
                      value={xy().yChannel + 1}
                      onInput={(e) => editItem(props.item.id, {
                        yChannel: clampNum(e.currentTarget.value, 16) - 1,
                      })}
                    />
                  </label>
                </div>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Release</span>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={!xy().returnToCenter ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { returnToCenter: false })}
                    title="Puck stays where you released"
                  >Hold</button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={!!xy().returnToCenter ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { returnToCenter: true })}
                    title="Puck snaps back to center (64, 64) on release - Kaoss Pad style"
                  >Spring</button>
                </div>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Higher is</span>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={xy().yInverted !== false ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { yInverted: true })}
                    title="Up = higher value (TouchOSC convention)"
                  >Up ↑</button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={xy().yInverted === false ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { yInverted: false })}
                    title="Down = higher value (envelope convention)"
                  >Down ↓</button>
                </div>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config — Timeline Launcher. Timeline picker +
            render mode (whole / whole+sections / sections). Fires-to-
            nothing until OPBridge timeline engine ships (Phase M2). */}
        <Show when={props.item.kind === 'timelineLauncher'}>
          {(() => {
            const tl = () => props.item as Extract<LayoutItem, { kind: 'timelineLauncher' }>;
            followSongTempos(timelinesInActiveProject);
            const mode = (): 'whole' | 'whole+sections' | 'sections' =>
              tl().mode ?? 'whole+sections';
            const trigger = (): 'instant' | 'nextBar' => tl().trigger ?? 'nextBar';
            const loop = (): 'inherit' | 'on' | 'off' => tl().loop ?? 'inherit';
            /** What `inherit` currently resolves to, from the referenced
             *  timeline's own loop toggle. Null when no timeline is picked
             *  (nothing to inherit → no ghost highlight). */
            const inheritedLoop = (): boolean | null => {
              const t = timelines().find((x) => x.id === tl().timelineId);
              return t ? timelineLoops(t) : null;
            };
            return (
              <div class="inspector-zone">
                <label class="inspector-field">
                  <span>Timeline</span>
                  <select
                    value={tl().timelineId}
                    onChange={(e) => editItem(props.item.id, {
                      timelineId: e.currentTarget.value,
                    })}
                  >
                    <option value="">Pick a timeline</option>
                    {/* This project's timelines (plan 3.1), plus the one already
                        picked if it belongs to another project, so the choice
                        still reads as chosen. */}
                    <For each={[
                      ...timelinesInActiveProject(),
                      ...timelines().filter((x) => x.id === tl().timelineId
                        && !timelinesInActiveProject().some((y) => y.id === x.id)),
                    ]}>
                      {(t) => (
                        <option value={t.id}>
                          [{t.tier}] {t.name}{tempoText(t) ? ` \u00b7 ${tempoText(t)} BPM` : ''}{t.tier === 'genre' ? " - won't play yet" : ''}
                        </option>
                      )}
                    </For>
                  </select>
                </label>
                {/* A dropdown that says it (2026-10-15): as segments "Whole +
                    sections" did not fit, and as wrapped chips the label sat
                    on a line of its own above them. */}
                <label class="inspector-field">
                  <span>Buttons</span>
                  <select
                    class="inspector-launcher-buttons"
                    value={mode()}
                    onChange={(e) => editItem(props.item.id, { mode: e.currentTarget.value as ReturnType<typeof mode> })}
                  >
                    <option value="whole">Whole timeline</option>
                    <option value="whole+sections">Whole + sections</option>
                    <option value="sections">Sections only</option>
                  </select>
                </label>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Trigger</span>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={trigger() === 'instant' ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { trigger: 'instant' })}
                    title="Fire the moment the button is pressed"
                  >Instant</button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={trigger() === 'nextBar' ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { trigger: 'nextBar' })}
                    title="Wait for the next bar boundary - quantized launch"
                  >Next bar</button>
                </div>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Loop</span>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={loop() === 'inherit' ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { loop: 'inherit' })}
                    title="Follow the timeline's own loop toggle (next to its Play button)"
                  >Timeline</button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={loop() === 'on' ? 'true' : 'false'}
                    data-inherited={loop() === 'inherit' && inheritedLoop() === true ? 'true' : undefined}
                    onClick={() => editItem(props.item.id, { loop: 'on' })}
                    title={loop() === 'inherit' && inheritedLoop() === true
                      ? 'Inherited from the timeline: loop is on'
                      : 'Force loop on for this launch'}
                  >On</button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={loop() === 'off' ? 'true' : 'false'}
                    data-inherited={loop() === 'inherit' && inheritedLoop() === false ? 'true' : undefined}
                    onClick={() => editItem(props.item.id, { loop: 'off' })}
                    title={loop() === 'inherit' && inheritedLoop() === false
                      ? 'Inherited from the timeline: loop is off'
                      : 'Force loop off for this launch'}
                  >Off</button>
                </div>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config — Piano. Octaves + root octave + mode +
            channel + velocity. Pianos send Note On/Off (not CCs), so
            no CC number field. */}
        <Show when={props.item.kind === 'piano'}>
          {(() => {
            const pno = () => props.item as Extract<LayoutItem, { kind: 'piano' }>;
            return (
              <div class="inspector-zone">
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Octaves</span>
                  <button
                    type="button" class="inspector-pill"
                    data-selected={pno().octaves === 1 ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { octaves: 1 })}
                  >1</button>
                  <button
                    type="button" class="inspector-pill"
                    data-selected={pno().octaves === 2 ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { octaves: 2 })}
                  >2</button>
                  <button
                    type="button" class="inspector-pill"
                    data-selected={pno().octaves === 3 ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { octaves: 3 })}
                  >3</button>
                </div>
                <label class="inspector-field">
                  <span>First octave</span>
                  <input
                    type="number" min="0" max="8"
                    value={pno().rootOctave}
                    onInput={(e) => editItem(props.item.id, {
                      rootOctave: Math.max(0, Math.min(8, parseInt(e.currentTarget.value, 10) || 0)),
                    })}
                    title="MIDI octave of the leftmost C. Ableton default = C3 (middle C)"
                  />
                </label>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Mode</span>
                  <button
                    type="button" class="inspector-pill"
                    data-selected={pno().mode === 'toggle' ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { mode: 'toggle' })}
                    title="Tap to hold, tap again to release - chord-grab pattern"
                  >Toggle</button>
                  <button
                    type="button" class="inspector-pill"
                    data-selected={(pno().mode ?? 'momentary') === 'momentary' ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { mode: 'momentary' })}
                    title="Hold key = Note On; release = Note Off"
                  >Momentary</button>
                  <button
                    type="button" class="inspector-pill"
                    data-selected={pno().mode === 'trigger' ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { mode: 'trigger' })}
                    title="Tap = Note On + immediate Note Off (percussion samples)"
                  >Trigger</button>
                </div>
                <label class="inspector-field">
                  <span>Channel</span>
                  <input
                    type="number" min="1" max="16"
                    value={pno().channel + 1}
                    onInput={(e) => editItem(props.item.id, {
                      channel: clampNum(e.currentTarget.value, 16) - 1,
                    })}
                  />
                </label>
                <label class="inspector-field">
                  <span>Velocity</span>
                  <input
                    type="number" min="1" max="127"
                    value={pno().velocity}
                    onInput={(e) => editItem(props.item.id, {
                      velocity: Math.max(1, clampNum(e.currentTarget.value, 127)),
                    })}
                  />
                </label>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config — Transpose. Start/End semitone range;
            the range always spans 0 (home). Dispatches OSC to the
            OP-AbletonTranspose device, so no CC/channel. */}
        <Show when={props.item.kind === 'transpose'}>
          {(() => {
            const tr = () => props.item as Extract<LayoutItem, { kind: 'transpose' }>;
            return (
              <div class="inspector-zone">
                <NumberField
                  label="Start" value={tr().start} min={-12} max={0}
                  onCommit={(s) => setTransposeRange(tr().id, s, tr().end)}
                  title="Lowest semitone offered (-12..0)"
                />
                <NumberField
                  label="End" value={tr().end} min={0} max={12}
                  onCommit={(ev) => setTransposeRange(tr().id, tr().start, ev)}
                  title="Highest semitone offered (0..+12)"
                />
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config — Capo. Start/End capo-fret range + the
            GLOBAL guitar drop tuning that links capo ↔ transpose. */}
        <Show when={props.item.kind === 'capo'}>
          {(() => {
            const cp = () => props.item as Extract<LayoutItem, { kind: 'capo' }>;
            return (
              <div class="inspector-zone">
                <NumberField
                  label="Start" value={cp().start} min={0} max={11}
                  onCommit={(s) => setCapoRange(cp().id, s, cp().end)}
                  title="Lowest capo fret (0 = open)"
                />
                <NumberField
                  label="End" value={cp().end} min={0} max={11}
                  onCommit={(ev) => setCapoRange(cp().id, cp().start, ev)}
                  title="Highest capo fret (0..+11)"
                />
                <NumberField
                  label="Drop tuning" value={dropTuning()} min={-4} max={0}
                  onCommit={(d) => setDropTuning(d)}
                  title="Guitar drop tuning (0 = standard, -1 = Eb …). Shifts the capo↔transpose mapping. Global across all Capo + Transpose rows."
                />
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config - Rec Quantize. Which of Live's nine RECORD
            quantization values this row offers. Checkboxes rather than a
            start/end range because the useful sets are not contiguous in
            Live's enum order - the default skips 1/4 and both combos. Drawn
            in enum order regardless of tap order (setRecQuantLabels
            re-sorts), so the row on the canvas never reshuffles.

            ⚠️ RECORD quant, not LAUNCH quant - different vocabulary, see
            abletonRecQuant.ts. */}
        <Show when={props.item.kind === 'recQuant'}>
          {(() => {
            const rq = () => props.item as Extract<LayoutItem, { kind: 'recQuant' }>;
            const chosen = () => new Set<string>(rq().labels);
            function toggle(label: string): void {
              const next = new Set(chosen());
              if (next.has(label)) next.delete(label); else next.add(label);
              setRecQuantLabels(rq().id, Array.from(next));
            }
            const opq = () => rq().quantizer === 'op-quantize';
            return (
              <div class="inspector-zone">
                {/* Which quantizer. Never both: OP-Quantize holds Live's own
                    at None, and a Live send would switch hard quantize back
                    on underneath it. */}
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Quantizer</span>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={opq() ? 'false' : 'true'}
                    onClick={() => setRecQuantQuantizer(rq().id, 'live')}
                    title="Live's own record quantize: notes snap fully to the grid as they are recorded."
                  >
                    Live
                  </button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={opq() ? 'true' : 'false'}
                    onClick={() => setRecQuantQuantizer(rq().id, 'op-quantize')}
                    title="The OP-Quantize device (load it on each track you record): after a take, new notes move toward the grid by the Amount. Adds an Amount cell, and keeps Live's own record quantize at None."
                  >
                    OP-Quantize
                  </button>
                </div>
                <Show when={opq()}>
                  <TipRow label="OP-Quantize" text="Needs the OP-Quantize device on each track you record. Every tap also sets Live's own record quantize to None, which OP-Quantize needs, and the row warns if Live's is turned back on." />
                </Show>
                <div class="inspector-pills" data-wrap="true">
                  <span class="inspector-pills-label">Values</span>
                  <For each={REC_QUANT_LABELS}>
                    {(label) => (
                      <button
                        type="button"
                        class="inspector-pill"
                        data-selected={chosen().has(label) ? 'true' : 'false'}
                        onClick={() => toggle(label)}
                        title={
                          label === '0'
                            ? "Live's None - record quantization OFF. A real value, not 'unset'."
                            : `Record quantize to ${REC_QUANT_DISPLAY[label]}`
                        }
                      >
                        {REC_QUANT_DISPLAY[label]}
                      </button>
                    )}
                  </For>
                </div>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config - an Ableton Control full row (DEC-291). */}
        <Show when={props.item.kind === 'abletonRow'}>
          <AbletonRowEditor item={props.item as AbletonRowItem} />
        </Show>

        {/* Kind-specific config - Scene Copy. Which Live scene the first
            row addresses, and how many rows. Both 1-based like Live's own
            numbering; the 0-based OSC conversion lives in sendSceneCopy. */}
        <Show when={props.item.kind === 'sceneCopy'}>
          {(() => {
            const sc = () => props.item as Extract<LayoutItem, { kind: 'sceneCopy' }>;
            return (
              <div class="inspector-zone">
                <NumberField
                  label="First scene" value={sc().firstScene} min={1} max={99}
                  onCommit={(f) => setSceneCopyRange(sc().id, f, sc().count)}
                  title="Live scene the FIRST row addresses (1-based, as Live draws it)"
                />
                <NumberField
                  label="Scenes" value={sc().count} min={1} max={8}
                  onCommit={(c) => setSceneCopyRange(sc().id, sc().firstScene, c)}
                  title="How many COPY/PASTE row pairs to draw (1-8)"
                />
                {/* Off by default, and the title says what it does in full
                    rather than naming it - "DELETE ALL" is the label on the
                    button, not an explanation of what gets deleted. */}
                <label
                  class="inspector-toggle"
                  title="Adds a DELETE ALL bar that empties every clip slot on EVERY scene. Two taps: the first arms the device, the second deletes. Off by default - it grows the item by one short row."
                >
                  <input
                    type="checkbox"
                    checked={!!sc().showDeleteAll}
                    onInput={(e) => setSceneCopyDeleteAll(sc().id, e.currentTarget.checked)}
                  />
                  <span>Delete-all button</span>
                </label>
              </div>
            );
          })()}
        </Show>

        {/* Kind-specific config - Waves Autotune. MIDI channel (with a
            Matrix conflict check, since the key/scale CCs are fixed),
            scale-selector placement, Start/End semitone range (shared by
            both key rows; always spans 0 = home) + each row's home/root. */}
        <Show when={props.item.kind === 'wavesAutotune'}>
          {(() => {
            const wa = () => props.item as Extract<LayoutItem, { kind: 'wavesAutotune' }>;

            function onChannelPick(e: Event & { currentTarget: HTMLSelectElement }) {
              const newCh = Math.max(1, Math.min(16, parseInt(e.currentTarget.value, 10) || wa().channel));
              if (newCh === wa().channel) return;
              // Snap the select back optimistically; requestWavesChannel
              // applies immediately when conflict-free (the reactive channel
              // update re-selects the new value) or opens the takeover modal
              // (leaving the select on the committed channel until confirm).
              e.currentTarget.value = String(wa().channel);
              requestWavesChannel(wa(), newCh, 'change');
            }

            return (
              <>
                <div class="inspector-zone">
                  <label class="inspector-field">
                    <span>MIDI channel</span>
                    <select value={wa().channel} onChange={onChannelPick}
                      title="MIDI channel the Waves plugin listens on (key = CC 55, scale = CC 56)">
                      <For each={Array.from({ length: 16 }, (_, i) => i + 1)}>
                        {(ch) => <option value={ch}>{ch}</option>}
                      </For>
                    </select>
                  </label>
                  <div class="inspector-pills">
                    <span class="inspector-pills-label">Scale</span>
                    {/* Spatial placement map — each option sits on the side
                        of a center "KEYS" core where the scale row will render,
                        so the control reads as WHERE, not just which. */}
                    <div class="waves-placement-map" role="radiogroup" aria-label="Scale placement">
                      {WAVES_SCALE_PLACEMENTS.map((p) => (
                        <button
                          type="button"
                          class="waves-placement-btn"
                          data-pos={p.id}
                          role="radio"
                          aria-checked={wa().scalePlacement === p.id ? 'true' : 'false'}
                          data-active={wa().scalePlacement === p.id ? 'true' : 'false'}
                          onClick={() => setWavesAutotunePlacement(wa().id, p.id)}
                          title={p.title}
                          aria-label={p.label}
                        >
                          {p.id === 'top' ? '▲' : p.id === 'bottom' ? '▼' : p.id === 'left' ? '◀' : '▶'}
                        </button>
                      ))}
                      <span class="waves-placement-core" aria-hidden="true">KEYS</span>
                    </div>
                  </div>
                  {/* Tune strength (CC 6). Opt-in: turning it on adds a band
                      and re-flows the item, so an authored page only changes
                      shape when the operator asks it to. */}
                  <label
                    class="inspector-toggle"
                    title="Tune strength - adds a fader sending CC 6 on this item's channel. Off by default so existing layouts keep their shape."
                  >
                    <input
                      type="checkbox"
                      checked={!!wa().showStrength}
                      onInput={(e) => editItem(wa().id, { showStrength: e.currentTarget.checked })}
                    />
                    <span>Strength fader</span>
                  </label>
                </div>
                <div class="inspector-zone">
                  <NumberField
                    label="Start" value={wa().start} min={-12} max={0}
                    onCommit={(s) => setWavesAutotuneRange(wa().id, s, wa().end)}
                    title="Lowest semitone offset from each row's home (-12..0)"
                  />
                  <NumberField
                    label="End" value={wa().end} min={0} max={12}
                    onCommit={(ev) => setWavesAutotuneRange(wa().id, wa().start, ev)}
                    title="Highest semitone offset from each row's home (0..+12)"
                  />
                  <label class="inspector-field">
                    <span>Row 1 home</span>
                    <select
                      value={wa().row1Home}
                      onChange={(e) => setWavesAutotuneHome(wa().id, 1, parseInt(e.currentTarget.value, 10) || 0)}
                      title="Home/root note of the top row (default A → Am)"
                    >
                      <For each={NOTE_NAMES}>
                        {(name, i) => <option value={i()}>{name}</option>}
                      </For>
                    </select>
                  </label>
                  <label class="inspector-field">
                    <span>Row 2 home</span>
                    <select
                      value={wa().row2Home}
                      onChange={(e) => setWavesAutotuneHome(wa().id, 2, parseInt(e.currentTarget.value, 10) || 0)}
                      title="Home/root note of the bottom row (default E → Em)"
                    >
                      <For each={NOTE_NAMES}>
                        {(name, i) => <option value={i()}>{name}</option>}
                      </For>
                    </select>
                  </label>
                </div>
              </>
            );
          })()}
        </Show>

        <Show when={props.item.kind === 'mixer'}>
          {(() => {
            const mx = () => props.item as Extract<LayoutItem, { kind: 'mixer' }>;
            return (
              <div class="inspector-zone">
                {/* One row, not two checkboxes (2026-10-15): Main and Returns
                    already cleared each other (setMixerMaster / setMixerReturns). */}
                <div class="inspector-pills" data-mixer-shows>
                  <span class="inspector-pills-label">Shows</span>
                  <button type="button" class="inspector-pill"
                    data-selected={mx().master !== true && mx().returns !== true ? 'true' : 'false'}
                    onClick={() => { setMixerMaster(mx().id, false); setMixerReturns(mx().id, false); }}
                    title="A bank of Live's tracks">Tracks</button>
                  <button type="button" class="inspector-pill"
                    data-selected={mx().master === true ? 'true' : 'false'}
                    onClick={() => setMixerMaster(mx().id, true)}
                    title="One strip for Live's Main channel: volume and pan">Main</button>
                  <button type="button" class="inspector-pill"
                    data-selected={mx().returns === true ? 'true' : 'false'}
                    onClick={() => setMixerReturns(mx().id, true)}
                    title="A bank of Live's return tracks (A, B, ...): volume, pan, mute, solo, sends">Returns</button>
                </div>
                <Show when={mx().returns === true}>
                  <label class="inspector-field" title="How many return strips (A, B, C, …) to render">
                    <span>Return count</span>
                    <input
                      type="number"
                      min="1"
                      max="12"
                      value={mx().returnCount ?? 2}
                      onInput={(e) => setMixerReturnCount(mx().id, Number(e.currentTarget.value) || 2)}
                    />
                  </label>
                </Show>
                <div class="inspector-pills inspector-checks-row">
                  <span class="inspector-pills-label">On each strip</span>
                  <div class="inspector-checks">
                <label class="inspector-toggle" title={mx().master ? "Show the 'Main' label on the strip" : "Show each track's name on its strip"}>
                  <input
                    type="checkbox"
                    checked={mx().showNames}
                    onInput={(e) => editItem(mx().id, { showNames: e.currentTarget.checked })}
                  />
                  <span>{mx().master ? 'Label' : 'Names'}</span>
                </label>
                <label class="inspector-toggle" title="Show a meter bar (streams only while the device METERS toggle is on)">
                  <input
                    type="checkbox"
                    checked={mx().showMeters}
                    onInput={(e) => editItem(mx().id, { showMeters: e.currentTarget.checked })}
                  />
                  <span>Meters</span>
                </label>
                <label class="inspector-toggle" title="Show a pan slider - double-tap to center (two-way via OP-AbletonMixer v0.2)">
                  <input
                    type="checkbox"
                    checked={mx().showPan === true}
                    onInput={(e) => editItem(mx().id, { showPan: e.currentTarget.checked })}
                  />
                  <span>Pan</span>
                </label>
                {/* Mute / solo / arm / sends don't exist on the master track. */}
                <Show when={mx().master !== true}>
                  <label class="inspector-toggle" title="Show a mute button on each strip">
                    <input
                      type="checkbox"
                      checked={mx().showMute}
                      onInput={(e) => editItem(mx().id, { showMute: e.currentTarget.checked })}
                    />
                    <span>Mutes</span>
                  </label>
                  <label class="inspector-toggle" title="Show a solo button per strip (two-way via OP-AbletonMixer v0.2)">
                    <input
                      type="checkbox"
                      checked={mx().showSolo === true}
                      onInput={(e) => editItem(mx().id, { showSolo: e.currentTarget.checked })}
                    />
                    <span>Solo</span>
                  </label>
                  <Show when={mx().returns !== true}>
                    <label class="inspector-toggle" title="Show a record-arm button per strip (two-way via OP-AbletonMixer v0.2)">
                      <input
                        type="checkbox"
                        checked={mx().showArm === true}
                        onInput={(e) => editItem(mx().id, { showArm: e.currentTarget.checked })}
                      />
                      <span>Arm</span>
                    </label>
                  </Show>
                  <label class="inspector-toggle" title="Show per-send sliders per strip (two-way via OP-AbletonMixer v0.2; auto-sized to Live's return count)">
                    <input
                      type="checkbox"
                      checked={mx().showSends === true}
                      onInput={(e) => editItem(mx().id, { showSends: e.currentTarget.checked })}
                    />
                    <span>Sends</span>
                  </label>
                </Show>
                  </div>
                </div>
                <Show when={mx().master !== true}>
                  <Show when={mx().showSends === true}>
                    <label class="inspector-field" title="How many send slots (A, B, C, ...) to render per strip">
                      <span>Send slots</span>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={mx().sendCount ?? 2}
                        onInput={(e) => {
                          const n = Math.max(1, Math.min(12, Math.round(Number(e.currentTarget.value) || 2)));
                          editItem(mx().id, { sendCount: n });
                        }}
                      />
                    </label>
                  </Show>
                </Show>
              </div>
            );
          })()}
        </Show>

        <Show when={props.item.kind === 'buttongrid'}>
          {(() => {
            const grid = () => props.item as Extract<LayoutItem, { kind: 'buttongrid' }>;
            const selMode = (): 'radio' | 'radio-required' | 'multi' | 'up-to-n' =>
              grid().selectionMode ?? 'radio';
            return (
              <div class="inspector-zone">
                <label class="inspector-field">
                  <span>Mode</span>
                  <select
                    class="inspector-grid-mode"
                    value={selMode()}
                    title={BUTTONGRID_MODES.find((m) => m.id === selMode())?.title}
                    onChange={(e) => editItem(props.item.id, { selectionMode: e.currentTarget.value as ReturnType<typeof selMode> })}
                  >
                    <For each={BUTTONGRID_MODES}>{(m) => <option value={m.id}>{m.label}</option>}</For>
                  </select>
                </label>
                <Show when={selMode() === 'up-to-n'}>
                  <label class="inspector-field">
                    <span>Max on</span>
                    <input
                      type="number" min="1" max={grid().rows * grid().cols}
                      value={grid().maxActive ?? 2}
                      onInput={(e) => editItem(props.item.id, {
                        maxActive: Math.max(1, Math.min(grid().rows * grid().cols, parseInt(e.currentTarget.value, 10) || 1)),
                      })}
                      title="Cap on simultaneous active cells. Extra taps evict the oldest."
                    />
                  </label>
                </Show>
                <GridStartsRow item={grid()} />
                <hr class="inspector-div" />
                <label class="inspector-field">
                  <span>Rows</span>
                  <input
                    type="number" min="1" max="8"
                    value={grid().rows}
                    // On change, not on every keystroke: retyping 10 as 11
                    // passed through 1 and cut the grid to one cell.
                    onChange={(e) => {
                      const r = Math.max(1, Math.min(8, parseInt(e.currentTarget.value, 10) || grid().rows));
                      e.currentTarget.value = String(r);
                      resizeButtonGrid(grid().id, r, grid().cols);
                    }}
                  />
                </label>
                <label class="inspector-field">
                  <span>Columns</span>
                  <input
                    type="number" min="1" max="16"
                    value={grid().cols}
                    onChange={(e) => {
                      const c = Math.max(1, Math.min(16, parseInt(e.currentTarget.value, 10) || grid().cols));
                      e.currentTarget.value = String(c);
                      resizeButtonGrid(grid().id, grid().rows, c);
                    }}
                  />
                </label>
                <hr class="inspector-div" />
                <label class="inspector-field">
                  <span>Base CC</span>
                  <input
                    type="number" min="0" max="127"
                    value={grid().baseCC}
                    onInput={(e) => editItem(props.item.id, {
                      baseCC: clampNum(e.currentTarget.value, 127),
                    })}
                  />
                </label>
                <label class="inspector-field">
                  <span>Channel</span>
                  <input
                    type="number" min="1" max="16"
                    value={grid().channel + 1}
                    onInput={(e) => editItem(props.item.id, {
                      channel: clampNum(e.currentTarget.value, 16) - 1,
                    })}
                  />
                </label>
                <label class="inspector-field">
                  <span>On value</span>
                  <input
                    type="number" min="0" max="127"
                    value={grid().onValue}
                    onInput={(e) => editItem(props.item.id, {
                      onValue: clampNum(e.currentTarget.value, 127),
                    })}
                  />
                </label>
                <hr class="inspector-div" />
                <button
                  type="button"
                  class="inspector-secondary"
                  data-pressed={isEditingCellsFor(grid().id) ? 'true' : 'false'}
                  onClick={() => toggleEditingCellsFor(grid().id)}
                  title={isEditingCellsFor(grid().id)
                    ? 'Exit edit-cells mode - cells go back to firing MIDI (in test mode) or selecting the grid (in edit mode)'
                    : 'Enter edit-cells mode - configure each cell (label / CC / channel / color) inline; click a cell to focus its row'}
                >
                  {isEditingCellsFor(grid().id) ? 'Done editing cells' : 'Edit cells'}
                </button>
                <Show when={isEditingCellsFor(grid().id)}>
                  {/* Pass an accessor so GridCellRows tracks the latest
                      ButtonGridItem reference on every editGridCell
                      write. Passing the IIFE-captured `grid` directly
                      pins the reference at mount time — subsequent
                      cell edits don't flow through, and inputs appear
                      to "lose" changes after the first keystroke. */}
                  <GridCellRows grid={() => props.item as Extract<LayoutItem, { kind: 'buttongrid' }>} />
                </Show>
              </div>
            );
          })()}
        </Show>

        <Show when={props.item.kind === 'macro'}>
          {(() => {
            const mItem = () => props.item as Extract<LayoutItem, { kind: 'macro' }>;
            return (
              <div class="inspector-zone">
                <label class="inspector-field inspector-field-grow">
                  <span>Macro</span>
                  <select
                    value={mItem().macroId}
                    onChange={(e) => editItem(props.item.id, {
                      macroId: e.currentTarget.value,
                    })}
                  >
                    <For each={macros()}>
                      {(m) => <option value={m.id}>{m.name}</option>}
                    </For>
                    <Show when={!macros().find((m) => m.id === mItem().macroId)}>
                      <option value={mItem().macroId}>(missing)</option>
                    </Show>
                  </select>
                </label>
                <button
                  type="button"
                  class="inspector-secondary"
                  onClick={openMacrosEditor}
                >
                  Edit macros…
                </button>
                <MidiTriggerLearn
                  itemId={props.item.id}
                  binding={mItem().midiTrigger}
                  path="a"
                  onSave={(binding) => editItem(props.item.id, { midiTrigger: binding })}
                  onClear={() => editItem(props.item.id, { midiTrigger: undefined })}
                />
              </div>
            );
          })()}
        </Show>

        <Show when={props.item.kind === 'sequencer'}>
          {(() => {
            const sItem = () => props.item as Extract<LayoutItem, { kind: 'sequencer' }>;
            return (
              <div class="inspector-zone">
                {/* Where its steps are set (2026-10-15): the panel had only MIDI
                    trigger, which left "how do I program it?" unanswered. */}
                <div data-sequencer-how>
                  <TipRow label="Programming it" text="Program it on the sequencer: tap a step to turn it on, drag a step up or down for how hard it hits, hold a lane's name to change what it plays." />
                </div>
                <MidiTriggerLearn
                  itemId={props.item.id}
                  binding={sItem().midiTrigger}
                  path="a"
                  onSave={(binding) => editItem(props.item.id, { midiTrigger: binding })}
                  onClear={() => editItem(props.item.id, { midiTrigger: undefined })}
                />
              </div>
            );
          })()}
        </Show>

        <Show when={props.item.kind === 'lfo'}>
          {(() => {
            const lfo = () => props.item as Extract<LayoutItem, { kind: 'lfo' }>;
            return (
              <div class="inspector-zone">
                <label class="inspector-field">
                  <span>Min</span>
                  <input
                    type="number" min="0" max="127"
                    value={lfo().min}
                    onInput={(e) => editItem(props.item.id, {
                      min: clampNum(e.currentTarget.value, 127),
                    })}
                  />
                </label>
                <label class="inspector-field">
                  <span>Max</span>
                  <input
                    type="number" min="0" max="127"
                    value={lfo().max}
                    onInput={(e) => editItem(props.item.id, {
                      max: clampNum(e.currentTarget.value, 127),
                    })}
                  />
                </label>
                <label class="inspector-field">
                  <span>Bars</span>
                  <input
                    type="number" min="1" max="32"
                    value={lfo().bars}
                    onInput={(e) => editItem(props.item.id, {
                      bars: Math.max(1, Math.min(32, parseInt(e.currentTarget.value, 10) || 1)),
                    })}
                  />
                </label>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Wave</span>
                  {WAVEFORMS.map((w) => (
                    <button
                      type="button"
                      class="inspector-pill"
                      data-selected={lfo().waveform === w ? 'true' : 'false'}
                      onClick={() => editItem(props.item.id, { waveform: w })}
                    >
                      {w[0]!.toUpperCase() + w.slice(1)}
                    </button>
                  ))}
                </div>
                <MidiTriggerLearn
                  itemId={props.item.id}
                  binding={lfo().midiTrigger}
                  path="a"
                  onSave={(binding) => editItem(props.item.id, { midiTrigger: binding })}
                  onClear={() => editItem(props.item.id, { midiTrigger: undefined })}
                />
              </div>
            );
          })()}
        </Show>

        {/* DEC-154 Learn for a plain Button - the kind the DEC named FIRST
            ("FireButton `trigger` mode") and which shipped after Sequencer/
            Macro/LFO, because those three resolve to an OPBridge action that
            takes an ID while a button resolves to BYTES.

            Hidden for `actionType: 'ableton'` rather than rendered-and-broken:
            that chain can carry practice-clock steps which never leave the
            browser, so an A-path rule could only ever fire part of it, and a
            binding that half-fires at a gig is worse than no binding.

            Hidden for `actionType: 'obs'` for a DIFFERENT reason, worth not
            collapsing: an OBS button resolves to an HTTP verb on a named
            bridge, not to MIDI bytes and not to an OPBridge toggle, so
            `midiTrigger/ruleSync.ts::actionFor` has nothing to build a rule
            action out of. Offering Learn here would author a binding that
            silently never fires - the same dead control, reached by another
            road. Making a pedal switch OBS scenes is a real want and is
            recorded as queued work, not as a gap nobody noticed.

            Hidden for `actionType: 'abletonDevice'` for the SAME reason as
            OBS, and it is not hypothetical here either: that button
            dispatches `POST /osc/send` to a loopback port on a named
            bridge, which `actionFor` cannot express as a rule action - the
            rules engine has no OSC-to-loopback action AND no per-item bridge
            affinity to aim one with. `timeline/bridgeSync.ts` drops the same
            item for the same missing pair. */}
        <Show when={props.item.kind === 'button'
          && (props.item as ButtonItem).actionType !== 'ableton'
          && (props.item as ButtonItem).actionType !== 'obs'
          && (props.item as ButtonItem).actionType !== 'abletonDevice'
          // Run Program: an HTTP launch on a named bridge, which the rules
          // engine has no action for - same dead-binding reason as OBS.
          && (props.item as ButtonItem).actionType !== 'runProgram'
          // Light Preset: `POST /lighting/fire` on a named bridge - the rules
          // engine has no lighting-preset action either.
          && (props.item as ButtonItem).actionType !== 'lightPreset'
          // Hue Scene: a named action of its own (2026-10-23); not a MIDI binding.
          && (props.item as ButtonItem).actionType !== 'hueScene'}>
          {(() => {
            const btn = () => props.item as ButtonItem;
            return (
              <div class="inspector-zone">
                <MidiTriggerLearn
                  itemId={props.item.id}
                  binding={btn().midiTrigger}
                  path="a"
                  onSave={(binding) => editItem(props.item.id, { midiTrigger: binding })}
                  onClear={() => editItem(props.item.id, { midiTrigger: undefined })}
                />
              </div>
            );
          })()}
        </Show>

        {/* Show-mode guard (SD-5 / DEC-072). Discrete controls (button, grid,
            transpose, capo, waves) two-tap-arm; continuous ones (fader, XY,
            mixer) long-press-to-grab. Per-item — the author marks exactly
            which controls are dangerous enough to protect. */}
        {/* Guard and Share with mods share a row (2026-10-05, vertical room):
            two checkboxes side by side; a share status line wraps under. */}
        {/* Guard is its own row since 2026-10-15 (Off / On and a line saying
            what it does); Share with mods and Controller keep the pair. */}
        <Show when={
          props.item.kind === 'button' || props.item.kind === 'buttongrid' ||
          props.item.kind === 'transpose' || props.item.kind === 'capo' ||
          props.item.kind === 'recQuant' || props.item.kind === 'abletonRow' ||
          props.item.kind === 'sceneCopy' ||
          props.item.kind === 'wavesAutotune' || props.item.kind === 'opvisualTrigger' || props.item.kind === 'opvisualGrid' || props.item.kind === 'audioPads' ||
          props.item.kind === 'fader' || props.item.kind === 'radial' || props.item.kind === 'xypad' || props.item.kind === 'mixer'
        }>
          <GuardToggle item={props.item} />
        </Show>
        <div class="inspector-zone-pair">

        {/* Share with mods sits just above Sends to: it is part of what the
            button does, and Sends to stays the last section. */}
        <Show when={!SENDS_NOTHING.has(props.item.kind)}>
          <ShareWithModsField item={props.item} />
          {/* A pedal or pad OPBridge knows, pointed at this (DEC-256). Keyed
              on the item so switching items re-reads the registry. */}
          <Show when={props.item.id} keyed>
            {(_id) => <Show when={anyConnectedReactive()}><AssignControllerField item={props.item} /></Show>}
          </Show>
        </Show>
        </div>

        {/* SENDS TO - always the LAST section of What it does (operator,
            2026-09-23: "any 'sends to' needs to go in a bottom section of
            what it does; how it looks should only be for aesthetic
            customisation"). One label and one place for every item's
            destination. Only the kinds that actually dispatch (see
            FIRES_TO_BRIDGES for the exclusions). OBS and Ableton Device get
            the SINGLE-machine form: one control per field, so a scene switch
            can never be aimed at two machines by a second, multi-select
            picker writing the same `bridgeIds`. */}
        <Show when={FIRES_TO_BRIDGES.has(props.item.kind)}>
          <Switch fallback={<BridgeAffinityField item={props.item as LayoutItem & { bridgeIds?: string[] }} />}>
            <Match when={props.item.kind === 'button' && (props.item as ButtonItem).actionType === 'obs'}>
              <SingleMachineField
                item={props.item as Extract<LayoutItem, { kind: 'button' }>}
                annotate={(rowId, connected) => obsBridgeSummary(obsState(rowId), connected)}
              />
            </Match>
            {/* Run Program: one machine, the one that runs it - program ids
                are per-machine. */}
            <Match when={props.item.kind === 'button' && (props.item as ButtonItem).actionType === 'runProgram'}>
              <SingleMachineField
                item={props.item as Extract<LayoutItem, { kind: 'button' }>}
                annotate={(rowId, connected) => programBridgeSummary(programState(rowId), connected)}
              />
            </Match>
            {/* Hue Scene: one machine it is pressed through. OPBridge carries the recall to the PC holding the Hue
                bridge from any of them; the one that holds it is named. */}
            <Match when={props.item.kind === 'button' && (props.item as ButtonItem).actionType === 'hueScene'}>
              <SingleMachineField
                item={props.item as Extract<LayoutItem, { kind: 'button' }>}
                annotate={(rowId, connected) => {
                  if (!connected) return 'offline';
                  const st = hueState(rowId);
                  return st.status === 'ok' && st.bridges.length > 0 ? 'holds the Hue bridge' : '';
                }}
              />
            </Match>
            {/* Light Preset: one machine, the one the lights are wired to - a
                preset is not carried across the mesh. */}
            <Match when={props.item.kind === 'button' && (props.item as ButtonItem).actionType === 'lightPreset'}>
              <SingleMachineField
                item={props.item as Extract<LayoutItem, { kind: 'button' }>}
                annotate={(rowId, connected) => {
                  const owner = bridges().find((b) => b.id === rowId)?.nodeId;
                  const drives = !!owner && fixtures().some((f) => f.outputNodeId === owner);
                  return presetBridgeSummary(presetState(rowId), connected, drives);
                }}
              />
            </Match>
            {/* OPVisual: one machine, the one it runs on - OPBridge reaches only
                the OPVisual linked to IT, and the OSC target is loopback, so a
                second machine would be a silent drop either way. The channel
                ("Sends via", DEC-215) sits in the same zone. */}
            <Match when={props.item.kind === 'opvisualTrigger' || props.item.kind === 'opvisualGrid'}>
              <SingleMachineField
                item={props.item as LayoutItem & { bridgeIds?: string[] }}
                annotate={(_rowId, connected) => (connected ? '' : 'offline')}
                after={<OpVisualSendsVia item={props.item as OpVisualTriggerItem | OpVisualGridItem} />}
              />
            </Match>
            <Match when={props.item.kind === 'button' && (props.item as ButtonItem).actionType === 'abletonDevice'}>
              <SingleMachineField
                item={props.item as Extract<LayoutItem, { kind: 'button' }>}
                annotate={(_rowId, connected) => (connected ? '' : 'offline')}
              />
            </Match>
          </Switch>
        </Show>

        </div>{/* /What it does */}

        {/* HOW IT LOOKS — the red band. Shape (buttons only) + Size +
            Rotation (buttons only) + face flags + font + colour. */}
        <div class="inspector-band" data-band="form" data-intent="looks">
          <span class="inspector-band-label">How it looks</span>
          {/* The live preview (DEC-318): a copy of one of the grid's own pads, at rest, lit and waiting. */}
          <Show when={props.item.kind === 'buttongrid'} fallback={<><ItemPreview item={props.item} /><hr class="inspector-div" /></>}>
            <GridPreview item={props.item as Extract<LayoutItem, { kind: 'buttongrid' }>} />
            <hr class="inspector-div" />
          </Show>

          {/* COLOUR - first in How it looks (operator, 2026-10-15), on every kind that
              draws a colour (DEC-127, operator 2026-07-29:
              "each item needs a colour picker").

              This replaces a four-swatch row that was offered on seven of the
              eighteen kinds and named STATE tokens - idle / incoming / armed /
              ok. Under the skin system those four collapse to two
              distinguishable colours (idle and ok both resolve to
              --sk-line-lit; incoming and armed both to --sk-armed-ink), so
              half the picker was already a no-op before this change.

              The new axis is orthogonal to both the skin and the state: the
              hue says WHICH control this is, brightness still says what it is
              DOING. Legacy `color` values are migrated on READ (hueOf), so no
              saved project is rewritten and nothing changes until an author
              picks a colour. */}
          <Show when={!IGNORES_COLOUR.has(props.item.kind)}>
          <div class="inspector-zone inspector-colour">
            <div class="inspector-pills">
              {/* A Light Colour also picks the LIGHTS' colour above; two rows
                  both called Colour read as one control (2026-10-15). */}
              <span class="inspector-pills-label">{props.item.kind === 'lightColor' ? 'Button colour' : 'Colour'}</span>
              {HUE_ORDER.map((h) => (
                <button
                  type="button"
                  class="inspector-swatch sk-swatch"
                  data-hue={h}
                  /* The chip is filled from the same --hue the item would
                     get, so a swatch cannot drift from what picking it does. */
                  style={{ '--swatch': HUE_VALUES[h] }}
                  // A Display's colour is its own `hue` only (Auto when unset, DEC-319); its legacy `color` is not a choice.
                  data-selected={(props.item.kind === 'display' ? props.item.hue ?? 'none' : hueOf(props.item as { hue?: PaletteHue; color?: string })) === h ? 'true' : 'false'}
                  onClick={() => props.item.kind === 'buttongrid'
                    ? setGridLook(props.item.id, { hue: h })
                    : editItem(props.item.id, { hue: h })}
                  aria-label={h === 'none' && props.item.kind === 'buttongrid' ? 'rainbow' : h === 'none' && props.item.kind === 'display' ? 'auto' : h}
                  title={h === 'none'
                    ? (props.item.kind === 'buttongrid' ? 'Rainbow - each cell its own colour'
                      : props.item.kind === 'display' ? 'Auto - the colour of what it shows' : 'No colour - the skin decides')
                    : props.item.kind === 'buttongrid' ? `${h} - every cell` : h}
                />
              ))}
            </div>
          </div>
          </Show>


          <Show when={props.item.kind === 'buttongrid'}>
            <GridFinishRow item={props.item as Extract<LayoutItem, { kind: 'buttongrid' }>} />
          </Show>
          <Show when={props.item.kind === 'display'}>
            <DisplayStyleRow item={props.item as Extract<LayoutItem, { kind: 'display' }>} />
          </Show>
          <Show when={PAD_KINDS.has(props.item.kind)}>
            <GridFinishRow item={props.item} />
          </Show>
          <hr class="inspector-div" />

          {/* Shape (buttons only): a plain dropdown of the 12 shapes (operator,
              2026-10-15: "Shape as a dropdown"); was a thumbnail opening a
              portalled 3x4 grid. */}
          <Show when={props.item.kind === 'button'}>
            <div class="inspector-zone">
              <label class="inspector-field">
                <span>Shape</span>
                <select
                  class="inspector-shape-select"
                  value={(props.item as ButtonItem).shape ?? 'rect'}
                  onChange={(e) => setButtonShape(props.item.id, e.currentTarget.value as ButtonShape)}
                >
                  <For each={SHAPE_LIBRARY}>{(d) => <option value={d.id}>{d.label}</option>}</For>
                </select>
              </label>
            </div>
          </Show>


          {/* StatusLed shape picker — narrower set than ButtonShape (LED
              indicators shouldn't be triangles). Three pills, one row. Sits under Colour, where a Button's Shape
              does, in words like the Button's (was lowercase "circle / rect
              / pill" at the bottom, 2026-10-15). */}
          <Show when={props.item.kind === 'statusLed'}>
            {(() => {
              const led = () => props.item as Extract<LayoutItem, { kind: 'statusLed' }>;
              const shape = (): 'circle' | 'rect' | 'pill' => led().shape ?? 'circle';
              return (
                <div class="inspector-zone">
                  <div class="inspector-pills">
                    <span class="inspector-pills-label">Shape</span>
                    {([['circle', 'Circle'], ['rect', 'Square'], ['pill', 'Pill']] as const).map(([s, word]) => (
                      <button
                        type="button"
                        class="inspector-pill"
                        data-selected={shape() === s ? 'true' : 'false'}
                        onClick={() => editItem(props.item.id, { shape: s })}
                        title={`${word} lamp`}
                      >
                        {word}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}
          </Show>

          {/* Orientation, the same row for a Fader and a Light Level, in How it
              looks (2026-10-15): the Fader had pills under What it does, the
              Light Level a lone "Vertical" checkbox. */}
          <Show when={props.item.kind === 'fader' || props.item.kind === 'lightLevel'}>
            {(() => {
              const vert = () => (props.item as { vertical?: boolean }).vertical !== false;
              return (
                <div class="inspector-zone">
                  <div class="inspector-pills" data-orientation>
                    <span class="inspector-pills-label">Orientation</span>
                    <button type="button" class="inspector-pill" data-selected={vert() ? 'true' : 'false'}
                      onClick={() => editItem(props.item.id, { vertical: true })}>Vertical</button>
                    <button type="button" class="inspector-pill" data-selected={vert() ? 'false' : 'true'}
                      onClick={() => editItem(props.item.id, { vertical: false })}>Horizontal</button>
                  </div>
                </div>
              );
            })()}
          </Show>

          {/* Kind-specific looks, lifted out of What it does (2026-10-15):
              they change only how the item is drawn. */}
          <Show when={props.item.kind === 'label'}>
            {(() => {
              const lblL = () => props.item as Extract<LayoutItem, { kind: 'label' }>;
              return (
                <div class="inspector-zone">
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Align</span>
                  <For each={['left', 'center', 'right'] as const}>
                    {(a) => (
                      <button
                        type="button"
                        class="inspector-pill"
                        data-selected={(lblL().align ?? 'center') === a ? 'true' : 'false'}
                        onClick={() => editItem(props.item.id, { align: a })}
                      >{a[0]!.toUpperCase() + a.slice(1)}</button>
                    )}
                  </For>
                </div>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Vertical</span>
                  <For each={['top', 'middle', 'bottom'] as const}>
                    {(v) => (
                      <button
                        type="button"
                        class="inspector-pill"
                        data-selected={(lblL().valign ?? 'middle') === v ? 'true' : 'false'}
                        onClick={() => editItem(props.item.id, { valign: v })}
                      >{v[0]!.toUpperCase() + v.slice(1)}</button>
                    )}
                  </For>
                </div>
                <div class="inspector-pills">
                  <span class="inspector-pills-label">Style</span>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={lblL().background ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { background: !lblL().background })}
                    title="Filled background behind the text"
                  >Fill</button>
                  <button
                    type="button"
                    class="inspector-pill"
                    data-selected={lblL().border ? 'true' : 'false'}
                    onClick={() => editItem(props.item.id, { border: !lblL().border })}
                    title="Border. Fill + border on a thin item = a divider"
                  >Border</button>
                </div>
                {/* Text size (2026-10-27, items-look-study: Label.tsx reads `textSize` but no row set it). */}
                <div class="inspector-pills" data-look-field="textSize">
                  <span class="inspector-pills-label">Text size</span>
                  {NAME_SIZE_PRESETS.map((s) => (
                    <button
                      type="button"
                      class="inspector-pill"
                      data-selected={(lblL().textSize ?? 'auto') === s.id ? 'true' : 'false'}
                      onClick={() => editItem(props.item.id, { textSize: s.id })}
                      title={s.id === 'auto' ? 'Scales with the label’s height' : `${s.label}: a fixed size`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                </div>
              );
            })()}
          </Show>
          <Show when={props.item.kind === 'mixer'}>
            {(() => {
              const mxL = () => props.item as Extract<LayoutItem, { kind: 'mixer' }>;
              return (
                <div class="inspector-zone">
                {/* Two by two: four skin names do not fit one iPad row
                    (Console was cut by 9px, `_probe_inspector_layout`). */}
                <div class="inspector-pills inspector-pills-grid" data-cols="2">
                  <span class="inspector-pills-label">Skin</span>
                  <div class="inspector-pills-cells">
                  {MIXER_SKINS.map((s) => (
                    <button
                      type="button"
                      class="inspector-pill"
                      data-selected={(mxL().skin ?? 'console') === s.id ? 'true' : 'false'}
                      onClick={() => editItem(mxL().id, { skin: s.id })}
                      title={s.title}
                    >
                      {s.label}
                    </button>
                  ))}
                  </div>
                </div>
                </div>
              );
            })()}
          </Show>



          <Show when={props.item.kind === 'buttongrid'}>
            <GridTitleIconRows item={props.item as Extract<LayoutItem, { kind: 'buttongrid' }>} />
          </Show>
          <Show when={props.item.kind === 'display'}>
            <DisplayTextRows item={props.item as Extract<LayoutItem, { kind: 'display' }>} />
          </Show>
          <Show when={TRAY_KINDS.has(props.item.kind)}>
            <TrayTitleRow item={props.item as { id: string; name?: string; titleAt?: GridTitleAt }} />
          </Show>
          <Show when={PAD_KINDS.has(props.item.kind)}>
            <PadIconRows item={props.item} />
          </Show>
          <Show when={READS_NAME.has(props.item.kind) || READS_INFO.has(props.item.kind)}>
          <div class="inspector-zone inspector-toggle-row">
            <Show when={READS_NAME.has(props.item.kind)}>
            <label class="inspector-toggle">
              <input
                type="checkbox"
                checked={!props.item.hideName}
                onInput={(e) => setFaceFlag(props.item.id, 'hideName', !e.currentTarget.checked)}
              />
              <span>Show name</span>
            </label>
            </Show>
            <Show when={READS_INFO.has(props.item.kind)}>
            <label class="inspector-toggle">
              <input
                type="checkbox"
                checked={!props.item.hideLabel}
                onInput={(e) => setFaceFlag(props.item.id, 'hideLabel', !e.currentTarget.checked)}
              />
              <span>Show info</span>
            </label>
            </Show>
          </div>
          </Show>

          {/* Font-size preset for the item's name. 'Auto' (default) runs
              the per-line auto-shrink helper. Explicit presets lock a
              fixed size so a bank of buttons reads consistently even
              when their labels vary in length. */}
          <Show when={READS_FONT.has(props.item.kind)}>
          <div class="inspector-zone">
            <div class="inspector-pills">
              <span class="inspector-pills-label">Font</span>
              {NAME_SIZE_PRESETS.map((s) => (
                <button
                  type="button"
                  class="inspector-pill"
                  data-selected={(props.item.nameSize ?? 'auto') === s.id ? 'true' : 'false'}
                  onClick={() => props.item.kind === 'buttongrid'
                    ? setGridLook(props.item.id, { nameSize: s.id })
                    : editItem(props.item.id, { nameSize: s.id })}
                  title={s.title}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
          </Show>

          <Show when={props.item.kind === 'buttongrid'}>
            <hr class="inspector-div" />
            <GridTextureRows item={props.item as Extract<LayoutItem, { kind: 'buttongrid' }>} />
            <hr class="inspector-div" />
            <GridFlashRow item={props.item as Extract<LayoutItem, { kind: 'buttongrid' }>} />
          </Show>
          <Show when={PAD_KINDS.has(props.item.kind)}>
            <hr class="inspector-div" />
            <GridTextureRows item={props.item} />
          </Show>
          <Show when={props.item.kind === 'display'}>
            <hr class="inspector-div" />
            <DisplayShowRows item={props.item as Extract<LayoutItem, { kind: 'display' }>} />
          </Show>
          <hr class="inspector-div" />

          {/* SIZE last in How it looks (DEC-318: "size and gap" close the section). */}
          <div class="inspector-zone">
            <div class="inspector-pills">
              <span class="inspector-pills-label">Size</span>
              {SIZES.map((s) => (
                <button
                  type="button"
                  class="inspector-pill"
                  data-selected={(props.item.size ?? 'M') === s ? 'true' : 'false'}
                  onClick={() => setItemSize(props.item.id, s)}
                  title={`${s} preset`}
                >
                  {s}
                </button>
              ))}
            </div>

          {/* Width + Height numeric inputs — commit on blur/Enter, clamped
              to the item's kind/shape constraints, aspect rule applied
              (so typing 400w on a circle also drops h to 400). Buttongrid
              + mixer opt out — their pixel size derives from a count × cell. */}
          <Show when={props.item.kind !== 'buttongrid' && props.item.kind !== 'mixer'}>
            {(() => {
              const isButton = () => props.item.kind === 'button';
              const buttonShape = (): ButtonShape | null =>
                isButton() ? (props.item as ButtonItem).shape ?? 'rect' : null;
              const clamps = () => itemResizeClamps(props.item);
              function commit(axis: 'w' | 'h', raw: string) {
                const c = clamps();
                const parsed = parseInt(raw, 10);
                if (!Number.isFinite(parsed)) return;
                let nw = axis === 'w'
                  ? Math.min(c.maxW, Math.max(c.minW, parsed))
                  : props.item.w;
                let nh = axis === 'h'
                  ? Math.min(c.maxH, Math.max(c.minH, parsed))
                  : props.item.h;
                // Ratio lock: scale the *other* axis by the item's current
                // w:h at moment-of-commit. Reads item.w / item.h fresh each
                // keystroke so consecutive edits stay coherent (drift from
                // rounding accumulates but is bounded by the clamps).
                if (isRatioLocked() && props.item.w > 0 && props.item.h > 0) {
                  const originAspect = props.item.w / props.item.h;
                  if (axis === 'w') {
                    nh = originAspect === 0 ? nh : nw / originAspect;
                  } else {
                    nw = nh * originAspect;
                  }
                  nw = Math.min(c.maxW, Math.max(c.minW, nw));
                  nh = Math.min(c.maxH, Math.max(c.minH, nh));
                }
                const sh = buttonShape();
                if (sh) {
                  const adjusted = applyAspectRule(sh, nw, nh, axis);
                  nw = Math.min(c.maxW, Math.max(c.minW, adjusted.w));
                  nh = Math.min(c.maxH, Math.max(c.minH, adjusted.h));
                }
                resizeItem(props.item.id, nw, nh);
              }
              return (
                <div class="inspector-dim-block">
                  {/* ONE row under the S/M/L presets (2026-10-05; was a
                      Dimensions header and a row each, DEC-044 adapted):
                      Width, Height, then the lock that governs both. */}
                  <label class="inspector-dim-field">
                    <span>Width</span>
                    <input
                      type="number"
                      min={clamps().minW}
                      max={clamps().maxW}
                      step="1"
                      value={Math.round(props.item.w)}
                      onInput={(e) => commit('w', e.currentTarget.value)}
                    />
                  </label>
                  <label class="inspector-dim-field">
                    <span>Height</span>
                    <input
                      type="number"
                      min={clamps().minH}
                      max={clamps().maxH}
                      step="1"
                      value={Math.round(props.item.h)}
                      onInput={(e) => commit('h', e.currentTarget.value)}
                    />
                  </label>
                  <button
                    type="button"
                    class="inspector-dim-lock"
                    data-locked={isRatioLocked() ? 'true' : 'false'}
                    data-pressed={isRatioLocked() ? 'true' : 'false'}
                    onClick={toggleRatioLock}
                    aria-label={isRatioLocked() ? 'Unlock aspect ratio' : 'Lock aspect ratio'}
                    title={isRatioLocked() ? 'Aspect ratio locked - click to unlock' : 'Aspect ratio unlocked - click to lock'}
                  >
                    <span class="inspector-dim-lock-glyph">{isRatioLocked() ? '🔒' : '🔓'}</span>
                  </button>
                </div>
              );
            })()}
          </Show>
          </div>{/* /Size */}

          {/* Space between a grid's cells (operator, 2026-10-15: "i would
              like to be able to remove/change that padding"). How it looks,
              not What it does: it moves nothing but pixels. */}
          <Show when={props.item.kind === 'buttongrid'}>
            <div class="inspector-zone">
              <label class="inspector-field">
                <span>Gap</span>
                <input
                  type="number" min="0" max="24"
                  value={(props.item as Extract<LayoutItem, { kind: 'buttongrid' }>).gap ?? 5}
                  onInput={(e) => {
                    const v = parseInt(e.currentTarget.value, 10);
                    if (Number.isFinite(v)) editItem(props.item.id, { gap: Math.max(0, Math.min(24, v)) });
                  }}
                  title="Space between the cells, in pixels. 0 puts them edge to edge."
                />
              </label>
            </div>
          </Show>

          {/* Rotation dial (buttons only). Dial primary + slider under
              (both interactive) + numeric readout / click-to-type. */}
          <Show when={props.item.kind === 'button'}>
            {(() => {
              const btn = props.item as ButtonItem;
              return (
                <div class="inspector-zone inspector-rotation">
                  <span class="inspector-pills-label">Rotation</span>
                  <RotationDial
                    value={btn.rotation ?? 0}
                    onChange={(deg) => editItem(props.item.id, { rotation: deg })}
                  />
                </div>
              );
            })()}
          </Show>
        </div>{/* /Form band */}
      </div>

      {/* Actions zone, pinned to the right. */}
      <div class="inspector-actions">
        <button
          type="button"
          class="inspector-delete"
          onClick={() => {
            removeItem(props.item.id);
            onClose();
          }}
        >
          Delete
        </button>
        <button type="button" class="inspector-done" onClick={onClose}>
          Done
        </button>
      </div>

      {/* Matrix picker — portaled, large. */}
      <Show when={matrixPickerOpen()}>
        <Portal>
          <MatrixPicker
            space={pickSpace()}
            currentChannel={(props.item as Extract<LayoutItem, { kind: MatrixBindableKind }>).channel}
            currentCcNumber={pickCurrentValue()}
            forLabel={matrixPickLabel()}
            /* Same rule `handleMatrixPick` uses to decide whether a pick
               needs the overwrite confirm: CC 0 on ch 0 is the un-set
               default every fresh control carries, not a chosen binding.
               Highlighting it would point a novice at a cell they never
               picked. */
            hasCurrent={pickSpace() !== 'cc' || !(
              (props.item as Extract<LayoutItem, { kind: MatrixBindableKind }>).ccNumber === 0
              && (props.item as Extract<LayoutItem, { kind: MatrixBindableKind }>).channel === 0
            )}
            onPick={handleMatrixPick}
            onClose={() => setMatrixPickerOpen(false)}
          />
        </Portal>
      </Show>

      {/* Confirmation prompt when a matrix pick would overwrite an
          existing binding. Portaled to body so it escapes the scaled
          shell — position:fixed centering only honors the viewport when
          no transformed ancestor exists. */}
      <Show when={pendingPick() !== null}>
        {(() => {
          const p = pendingPick();
          if (!p) return null;
          return (
            <Portal>
              <div
                class="confirm-overlay"
                onClick={cancelPick}
                role="dialog"
                aria-modal="true"
                aria-label="Confirm matrix mapping change"
              >
                <div class="confirm-modal" onClick={(e) => e.stopPropagation()}>
                  <div class="confirm-title">Change this button's mapping?</div>
                  <div class="confirm-body">
                    <div class="confirm-row">
                      <span class="confirm-key">From</span>
                      <span class="confirm-val">ch {p.fromCh + 1} · CC {p.fromCc}</span>
                    </div>
                    <div class="confirm-row">
                      <span class="confirm-key">To</span>
                      <span class="confirm-val confirm-val-new">ch {p.toCh + 1} · CC {p.toCc}</span>
                    </div>
                  </div>
                  <div class="confirm-actions">
                    <button type="button" class="confirm-cancel" onClick={cancelPick}>
                      Keep current
                    </button>
                    <button type="button" class="confirm-ok" onClick={confirmPick}>
                      Change to ch {p.toCh + 1} · CC {p.toCc}
                    </button>
                  </div>
                </div>
              </div>
            </Portal>
          );
        })()}
      </Show>
    </div>
    </>
  );
}

/** Auto-resize a textarea to fit its content. Reset height first so
 *  shrinks work when content deletes. */
function autoResize(el: HTMLTextAreaElement | undefined | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

/** Show-mode guard toggle (SD-5). Shown for buttons (two-tap arm) and
 *  faders / XY pads (long-press-to-grab); the tooltip copy adapts to the
 *  control's guard gesture. */
/**
 * "Share with mods" (OPMod ask + operator, 2026-09-23; every kind that sends
 * anything since 2026-09-29, operator: "literally everything"). The wording
 * is OPMod's, deliberately: sharing makes an item GRANTABLE - the streamer
 * picks which mods may press what in OPMod - so "all your mods can press
 * this" would be false. The status line is the sync's own answer
 * (`modShare/sync.ts`), so the box cannot claim a share that did not land.
 *
 * What a share registers per kind (one press, on/off pairs, one per option,
 * a value) is `modShare/ops.ts`. When some of it waits on an OPBridge update,
 * or a mod's press differs from a finger's (a tap instead of a hold), the
 * short line says so and the detail is behind "Why?" (DEC-221).
 */
const NEED_WORDS: Record<Need, string> = {
  route: 'OBS scenes, programs, lights, timelines, engines and Stream Deck actions',
  sequence: 'anything that sends more than one message per press (a tap, a preset change, a chain)',
  inputs: 'faders, knobs, XY pads, levels and other values',
};

function ShareWithModsField(props: { item: LayoutItem }) {
  const reason = () => unshareableReason(props.item);
  const st = () => shareStatus(props.item.id);
  const shared = () => { const s = st(); return s.kind === 'shared' || s.kind === 'waiting' ? s : null; };
  return (
    <div class="inspector-zone" data-share-with-mods>
      <label
        class="inspector-toggle"
        title="Mods you choose in OPMod can use this remotely."
      >
        <input
          type="checkbox"
          checked={props.item.shareWithMods === true}
          disabled={reason() !== null && props.item.shareWithMods !== true}
          onInput={(e) => editItem(props.item.id, { shareWithMods: e.currentTarget.checked } as ItemPatch)}
        />
        <span>Share with mods</span>
      </label>
      <Show when={reason() && props.item.shareWithMods !== true}>
        <span class="inspector-hint">{reason()}</span>
      </Show>
      <Show when={props.item.shareWithMods === true}>
        <span class="inspector-hint" data-share-status={st().kind}>
          {(() => {
            const s = st();
            switch (s.kind) {
              case 'shared':
                return s.ops === 1
                  ? 'Shared. Mods you choose in OPMod can use this.'
                  : `Shared: ${s.ops} controls mods you choose in OPMod can use.`;
              case 'waiting': return 'Ready to share - waiting for an OPBridge update.';
              case 'pending': return 'Sharing...';
              case 'off': return 'Sharing as soon as OPBridge is connected.';
              case 'unsupported': return s.why;
              case 'blocked': return s.why;
              case 'error': return `Not shared: ${s.why}`;
            }
          })()}
        </span>
        <Show when={shared() && shared()!.waiting > 0}>
          <InfoHint
            title="Waiting on OPBridge"
            label="Why?"
            {...(st().kind === 'waiting'
              ? {}
              : { short: `${shared()!.waiting} more ${shared()!.waiting === 1 ? 'waits' : 'wait'} for an OPBridge update.` })}
          >
            <p>OPButtons has everything ready, but the OPBridge on that computer does not
            support these yet:</p>
            <ul>
              {shared()!.waitingOn.map((n) => <li>{NEED_WORDS[n]}</li>)}
            </ul>
            <p>They are shared automatically as soon as OPBridge is updated - nothing to do
            here. Until then mods cannot use them.</p>
          </InfoHint>
        </Show>
        <Show when={shared() && shared()!.notes.length > 0}>
          <InfoHint title="How mods see this" label="How mods see it">
            {shared()!.notes.map((n) => <p>{n}</p>)}
          </InfoHint>
        </Show>
        <Show when={st().kind === 'blocked' || st().kind === 'error'}>
          <button type="button" class="inspector-pill" onClick={() => retryShare()}>Try again</button>
        </Show>
      </Show>
    </div>
  );
}

function GuardToggle(props: { item: LayoutItem }) {
  // Kind-aware default - sceneCopy is guarded unless explicitly turned off.
  // Shared with the component via `guardEnabled` so the row cannot lie.
  const guarded = () => guardEnabled(props.item);
  const continuous = () => props.item.kind === 'fader' || props.item.kind === 'radial' || props.item.kind === 'xypad' || props.item.kind === 'mixer';
  // Off / On with one line saying what it does (operator, 2026-10-15:
  // "Guard explained"); was a bare checkbox whose meaning lived in a tooltip.
  return (
    <div class="inspector-zone" data-guard>
      <div class="inspector-pills">
        <span class="inspector-pills-label">Guard<Tip text={props.item.kind === 'opvisualGrid'
          ? 'STOP ALL, B/OUT and a layer\u2019s CLR need a second tap; cells and columns never ask. Not while editing.'
          : `${continuous()
          ? 'Hold it to grab it before it moves.'
          : 'First tap arms it, second tap fires.'} Not while editing.`} /></span>
        <button type="button" class="inspector-pill" data-selected={guarded() ? 'false' : 'true'}
          onClick={() => editItem(props.item.id, { guard: false })}>Off</button>
        <button type="button" class="inspector-pill" data-selected={guarded() ? 'true' : 'false'}
          onClick={() => editItem(props.item.id, { guard: true })}>On</button>
      </div>
    </div>
  );
}

/** Name field — shared by every item kind. Textarea so labels can span
 *  multiple lines (Shift+Enter / Ctrl+Enter inserts a newline; plain
 *  Enter commits + blurs). Draft-and-commit pattern: keystrokes update
 *  a local signal, the store gets one write on blur or Enter. Without
 *  this pattern, per-keystroke pushes trigger InspectorPanel-adjacent
 *  <Show> remounts (Solid 1.9 quirk with dynamic children of unkeyed
 *  Show) and focus is lost after the first character. */
function NameField(props: { item: LayoutItem }) {
  const [draft, setDraft] = createSignal(props.item.name ?? '');
  let field: HTMLTextAreaElement | undefined;

  // Follow a name set from ELSEWHERE while the panel is open - picking a
  // program or a lighting preset names an unnamed button. Without this the
  // draft kept the empty name it was opened with, and the next blur
  // committed that empty draft over the new name (2026-09-29). Never while
  // the operator is typing in it.
  createEffect(() => {
    const n = props.item.name ?? '';
    if (field && document.activeElement === field) return;
    setDraft(n);
  });

  function commit() {
    const v = draft();
    if (v === (props.item.name ?? '')) return;
    setItemName(props.item.id, v);
  }

  return (
    <textarea
      class="inspector-name-textarea"
      placeholder={namePlaceholder(props.item)}
      rows={1}
      value={draft()}
      onInput={(e) => { setDraft(e.currentTarget.value); autoResize(e.currentTarget); }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key !== 'Enter') return;
        if (e.shiftKey || e.ctrlKey || e.metaKey) return; // let newline through
        e.preventDefault();
        commit();
        (e.currentTarget as HTMLTextAreaElement).blur();
      }}
      ref={(el) => { field = el; queueMicrotask(() => autoResize(el)); }}
    />
  );
}

/** Placeholder text for the Name field — shows what the face will fall
 *  back to if no override is set. */
function namePlaceholder(item: LayoutItem): string {
  switch (item.kind) {
    case 'button':
      return item.actionType === 'noteon'
        ? `note ${item.note ?? 60}`
        : item.actionType === 'pc'
          ? `pc ${item.program ?? 0}`
          // An Ableton Control button has no CC, so `cc 0` here would be a
          // placeholder promising a fallback face that never appears.
          : item.actionType === 'ableton'
            ? (item.abletonSteps?.length ?? 0) === 1 ? buttonFace(item.abletonSteps![0]!, { preset: (id) => motionPresetById(id)?.name }) : `${item.abletonSteps?.length ?? 0} steps`
            // Same reasoning one step along: an OBS button has no CC either,
            // and the scene name is the only honest stand-in for its face.
            : item.actionType === 'obs'
              ? (item.obsScene || 'obs scene')
              // And once more: a device toggle has no CC, so "cc 0" here
              // would promise a face it never renders.
              : item.actionType === 'abletonDevice'
                ? (item.abletonDevice?.deviceName || 'ableton device')
                : item.actionType === 'runProgram'
                  ? (item.runProgramId || 'run program')
                  : item.actionType === 'lightPreset'
                    ? (item.lightPreset?.presetId || 'light preset')
                    : item.actionType === 'hueScene'
                      ? (item.hueScene?.name || 'hue scene')
                      : `cc ${item.ccNumber}`;
    case 'lfo':        return `${item.waveform} · cc ${item.ccNumber}`;
    case 'buttongrid': return `grid ${item.rows}×${item.cols}`;
    case 'sequencer':  return 'sequencer';
    case 'macro':      return 'macro';
    case 'streamdeck': return item.actionId ? (item.actionTitle ?? 'action') : 'unassigned';
    case 'fader':      return `cc ${item.ccNumber}`;
    case 'radial':     return `knob cc ${item.ccNumber}`;
    case 'label':      return 'label';
    case 'presetChange':
      return item.presetName ?? `SL${item.setlist + 1} ${helixSlotLabel(item.program)}`;
    case 'snapshotChange':
      return snapshotLabel(item.snapshot);
    case 'xypad':      return `xy cc ${item.xCcNumber}·${item.yCcNumber}`;
    case 'timelineLauncher':
      return item.timelineId ? `→ ${item.timelineId.slice(0, 8)}` : 'timeline';
    case 'piano':      return `piano ${item.octaves}oct · C${item.rootOctave}`;
    case 'transpose':  return `transpose ${item.start}..${item.end > 0 ? '+' : ''}${item.end}`;
    case 'capo':       return `capo ${item.start}..${item.end}`;
    case 'recQuant':   return `rec quant ${item.labels.length} values`;
    case 'abletonRow': return `${ABLETON_CONTROLS.find((c) => c.row === item.control)?.label.toLowerCase() ?? item.control} row`;
    case 'sceneCopy':  return `scenes ${item.firstScene}..${item.firstScene + item.count - 1}`;
    case 'wavesAutotune': return 'waves autotune';
    case 'mixer':      return `mixer ${item.trackCount}ch`;
    case 'image':      return 'image';
    case 'statusLed':
      return item.sourceType === 'midi.note'
        ? `led note ${item.note ?? 60}`
        : `led cc ${item.ccNumber ?? 0}`;
    case 'display':
      return `display · ${item.source}`;
    case 'rigStateButton':
      return 'rig state';
    // Lighting: the placeholder is the TARGET, because that is the thing the
    // operator will not otherwise be able to tell two adjacent buttons apart
    // by. `describeTarget` is the same text the face uses, so the Name field
    // shows exactly what naming it would replace.
    case 'lightColor':  return `${item.color_label} · ${describeTarget(item.target)}`;
    case 'lightLevel':  return `level · ${describeTarget(item.target)}`;
    case 'lightFx':     return item.effect
      ? `${item.effect} · ${describeTarget(item.target)}`
      : `stop fx · ${describeTarget(item.target)}`;
    case 'blackout':    return 'blackout';
    case 'opvisualTrigger': return actionFace(item.action).toLowerCase();
    case 'opvisualGrid':    return 'opvisual';
    case 'audioPads':       return item.pads.length === 1 ? 'audio pad' : 'audio pads';
  }
}
